/**
 * Family Repository UI translations — global language toggle.
 * Languages: English, Hindi, Marathi, Tamil, Bengali, Gujarati.
 * Fallback: any missing key falls back to English.
 */

export const LANGS = [
    { code: "en", label: "English", native: "English" },
    { code: "hi", label: "Hindi", native: "हिन्दी" },
    { code: "mr", label: "Marathi", native: "मराठी" },
    { code: "ta", label: "Tamil", native: "தமிழ்" },
    { code: "bn", label: "Bengali", native: "বাংলা" },
    { code: "gu", label: "Gujarati", native: "ગુજરાતી" },
    { code: "te", label: "Telugu", native: "తెలుగు" },
    { code: "kn", label: "Kannada", native: "ಕನ್ನಡ" },
    { code: "ml", label: "Malayalam", native: "മലയാളം" },
    { code: "pa", label: "Punjabi", native: "ਪੰਜਾਬੀ" },
];

export const STRINGS = {
    en: {
        tourListen: "Listen",
        tourStop: "Stop",
        tourEyebrow: "Welcome to Family Repository",
        tourStepOf: "Step {n} of {total}",
        tourNext: "Next",
        tourStart: "Start exploring",
        tourS1Title: "Build your Family Tree",
        tourS1Body: "Start by adding parents, grandparents and children. Every person you add becomes a living branch of your family's story.",
        tourS1Cta: "Open Family Tree",
        tourS2Title: "Preserve Memories",
        tourS2Body: "Upload photos, record voices and write stories. Organise them into albums so future generations can relive them.",
        tourS2Cta: "Open Memories",
        tourS3Title: "Secure the Vault",
        tourS3Body: "Keep wills, property papers and legal documents safe with bank-grade security — and pass them on when the time comes.",
        tourS3Cta: "Open Vault",
        back: "Back",
        cancel: "Cancel",
        delete: "Delete",
        logout: "Logout",
        optional: "(optional)",
        tagline: "Your Family. Forever.",
        familyLegacy: "Family Legacy",
        signInTitle: "Sign in to Family Repository",
        otpTitle: "Enter your OTP",
        phoneSub: "We'll send a one-time password to your mobile.",
        otpSentTo: "OTP sent to",
        expiresIn: "It expires in",
        mobileNumber: "Mobile number",
        phoneHint: "Only Indian numbers are supported (must start with 6–9).",
        yourName: "Your name",
        firstTimeOnly: "(first time only)",
        haveInviteCode: "Have a family invite code?",
        apply: "Apply",
        sendOtp: "Send OTP",
        sendingOtp: "Sending OTP…",
        otpLabel: "6-digit OTP",
        verifyContinue: "Verify & Continue",
        verifying: "Verifying…",
        changeNumber: "Change number",
        resendOtp: "Resend OTP",
        resendIn: "Resend in",
        terms: "By continuing, you agree to Family Repository's Terms of Service and Privacy Policy.",
        trustStrip: "Encrypted • Family-controlled • Made in India",
        welcome: "Welcome",
        dashSub: "Your Family Repository is ready. Begin by adding your first family member or recording an elder's story.",
        quickActions: "Quick Actions",
        footerNote: "Your data is private, encrypted, and family-controlled. Made for India — always.",
        roleFamilyHead: "Family Head",
        roleAdmin: "Admin",
        roleEditor: "Editor",
        roleViewer: "Viewer",
        roleMember: "Member",
        cardTreeTitle: "View Family Tree",
        cardTreeDesc: "See your entire family across generations.",
        cardAddTitle: "Add Member",
        cardAddDesc: "Invite or add a parent, sibling, child, cousin.",
        cardMemoriesTitle: "Memories",
        cardMemoriesDesc: "Upload photos and videos of your family moments.",
        cardStoryTitle: "Record Story",
        cardStoryDesc: "Capture an elder's voice and save it forever.",
        cardVaultTitle: "Document Vault",
        cardVaultDesc: "Wills, deeds & papers — locked, shared on your terms.",
        cardSettingsTitle: "Privacy & Settings",
        cardSettingsDesc: "Roles, invites, your data and the activity log.",
        memories: "Memories",
        addMemory: "Add Memory",
        memoriesEmpty: "No memories yet",
        memoriesEmptySub: "Upload your first family photo — weddings, festivals, everyday joy.",
        caption: "Caption",
        captionPlaceholder: "e.g. Diwali at Nani's house, 1998",
        memoryDate: "When was this?",
        tagMember: "Tag a family member",
        choosePhoto: "Choose photo",
        changePhoto: "Change photo",
        uploadMemory: "Save Memory",
        uploading: "Uploading…",
        deleteMemoryConfirm: "Delete this memory? This cannot be undone.",
        confirmDelete: "Tap again to confirm",
        uploadedBy: "Added by",

        // ============ Landing / Hero ============
        heroEyebrow: "Your Family · Your Legacy",
        heroHeadline1: "Your family's history",
        heroHeadlineAccent: "preserved",
        heroHeadline2: "forever.",
        heroSubline: "Build your family tree, record your elders' voices, preserve photos and documents — all in one private space made for Indian families, in your own language.",
        heroCtaPrimary: "Start free with mobile OTP",
        heroCtaSecondary: "See how it works",
        heroTrustPrivate: "Private by default",
        heroTrustLangs: "10 Indian languages",
        heroTrustFree: "Free to start",
        heroSocialProof: "Trusted by 12,500+ Indian families",
        heroBadge10: "10+",
        heroBadge10Cap: "Indian languages",
        heroStoryAudio: "Audio Stories",
        heroStoryPhotos: "Photos",
        heroStoryDocs: "Docs",

        // Hero stats band
        statHappyFamilies: "Happy Families",
        statTreesCreated: "Family Members Preserved",
        statStoriesRecorded: "Stories & Memories",
        statDataPrivacy: "Data Privacy",

        // TryDemoTree
        demoBadge: "Create without signup / No Phone / No OTP",
        demoHeadline: "Create your family tree within 1 minute",
        demoSubline: "Add 6 names. Watch your 6-generation family tree animate right here.",
        demoTrustNoPhone: "No phone",
        demoTrustNoOtp: "No OTP",
        demoTrustNoSignup: "No signup",
        demoStepPrefix: "Step",
        demoStepOf: "of 6 —",
        demoLabelFather: "Father's name",
        demoPhFather: "e.g. Rajesh Sharma",
        demoLabelGrandfather: "Grandfather's name",
        demoPhGrandfather: "e.g. Mohan Sharma",
        demoLabelGreatGrand: "Great-grandfather's name",
        demoPhGreatGrand: "e.g. Kishan Sharma",
        demoLabelChild: "Son / Daughter's name",
        demoPhChild: "e.g. Ananya Sharma",
        demoLabelGrandchild: "Grandchild's name",
        demoPhGrandchild: "e.g. Vihaan Sharma",
        roleFather: "Father",
        roleGrandfather: "Grandfather",
        roleGreatGrandfather: "Great-grandfather",
        roleChild: "Son/Daughter",
        roleGrandchild: "Grandchild",
        demoLabelYou: "Your name",
        demoLabelParent: "Your parent's name",
        demoLabelGrand: "Your grandparent's name",
        demoPhYou: "e.g. Rahul Sharma",
        demoPhParent: "e.g. Suresh Sharma",
        demoPhGrand: "e.g. Ramchandra Sharma",
        demoHint: "Add {n} more names to see your tree come to life.",
        demoNext: "Next",
        demoSeeTree: "See your tree",
        demoRestart: "Try again",
        demoStartReal: "Start my real tree (free)",
        demoTreeBadge: "Your mini tree",
        demoChooseDesign: "Choose a poster design",
        demoClassic: "Classic",
        demoFamilyTitle: "The {name} Family",
        demoShareWhatsApp: "Share on WhatsApp",
        demoShareText: "I just made our family tree in 30 seconds! 🌳 Make yours free: {link}",
        demoScanToJoin: "Scan to make yours free",
        demoTreeHeadline: "Six generations, one living tree.",
        demoTreeAddMore: "Add 20+ more relatives, photos and memories — free.",
        demoFooterPrivacy: "Your demo results stay only in your browser. Nothing is saved or sent.",
        roleGrandparent: "Grandparent",
        roleParent: "Parent",
        roleYou: "You",

        // Testimonials
        testHeadline: "Families across India trust us",
        testSubline: "Real families - Real Life Stories & Legacies Preserved Forever.",
        testShareBtn: "Share Your Family Story",
        testShareHint: "Has Family Repository helped your family? Share your story.",

        // NoOtpBanner
        noOtpTitle: "Family Repository NEVER asks for your OTP.",
        noOtpBody: "If anyone calls or messages you asking for OTP — disconnect immediately. We will never request it.",

        // Landing header
        navLogin: "Login",
        navStartFree: "Free Signup",
        navDashboard: "Dashboard",

        // ── App shell: top nav ──
        tnHome: "Home",
        tnFamilyTree: "Family Tree",
        tnFamilyHub: "Family Hub",
        tnEvents: "Events",
        tnPackages: "Packages",
        tnVault: "Vault",
        tnReports: "Reports",
        tnMore: "More",
        tnRecords: "Records",
        tnSupport: "Support",

        // ── App shell: sidebar ──
        sbDashboard: "Dashboard",
        sbMemberProfile: "Member Profile",
        sbgConnections: "Connections",
        sbConnectFriends: "Connect with Extended Families",
        sbConnectRelatives: "Connect with Close Families",
        sbWorldMap: "World Map",
        sbgMemories: "Memories",
        sbPhotoMemories: "Photo Memories",
        sbAudioMemories: "Audio Memories",
        sbVideoMemories: "Video Memories",
        sbgEvents: "Events & Reminders",
        sbFamilyCalendar: "Family Calendar",
        sbTodaysCelebration: "Today's Celebration",
        sbFamilyTimeline: "Individual Life Journey",
        sbSmartReminder: "Smart Reminder",
        sbgHealth: "Health & Medical Repository",
        sbMedicalHistory: "Medical History",
        sbChronicDiseases: "Chronic / Hereditary Diseases",
        sbCancerRegistry: "Cancer Registry",
        sbGeneticCongenital: "Genetic & Congenital",
        sbAllergies: "Allergies & Intolerances",
        sbVaccination: "Vaccination Records",
        sbLifestyle: "Lifestyle Dashboard",
        sbPregnancyBirth: "Pregnancy & Birth History",
        sbOrganDonation: "Organ Donation Preference",
        sbBloodGroup: "Blood Group Registry",
        sbgVault: "Personal Vault",
        sbDocumentVault: "Document Vault",
        sbLegalVault: "Legal Vault",
        sbgDisplay: "Display & Download",
        sbDownloadExport: "Download & Export",
        sbDigitalFamilyTree: "Digital Family Tree",
        sbPackagesPlans: "Packages & Plans",
        sbPrivacySettings: "Privacy & Settings",

        // ── App shell: header / menus ──
        hdrDashboard: "Dashboard",
        hdrMenu: "Menu",
        hdrMyProfile: "My Profile",
        hdrSettings: "Settings",
        hdrSignOut: "Sign Out",
        signOutTitle: "Sign out of Family Repository?",
        signOutBody: "You can log back in anytime with your mobile OTP.",
        roleAdministrator: "Administrator",
        roleMemberPlain: "Member",
        searchPlaceholder: "Search anything…",
        searchNoResults: "No results for",
        grpMembers: "Members",
        grpMemories: "Memories",
        grpDocuments: "Documents",
        grpStories: "Stories",
        grpEvents: "Events",
        msgTitle: "Messages & Reminders",
        msgMarkAll: "Mark all read",
        msgEmpty: "No messages yet — reminders and family updates will appear here.",

        // ── Mobile bottom nav ──
        mbHome: "Home",
        mbTree: "Tree",
        mbAdd: "Add",
        mbHub: "Hub",
        mbMe: "Me",

        // ── Dashboard hero cards ──
        dbMemberSince: "Member Since",
        dbGrowingTogether: "Growing Together",
        dbGrowingSub: "Every Family has a Story.",
        dbProfileCompleteness: "Profile Completeness",
        dbMemoryOfDay: "Memory of the Day",
        dbMemoryQuote: "Today's Family — Tomorrow's Inspiration.",
        dbViewMemory: "View Memory",
        dbGoPremium: "Go Premium",
        dbPremiumDesc: "Unlock advanced features & secure your family data.",
        dbUpgradeNow: "Upgrade Now",

        // ── Family pulse ──
        plMembers: "Members",
        plGenerations: "Generations",
        plYearsHistory: "Years of History",
        plFamilyAccounts: "Family Accounts",
        plTotalRecords: "Total Records",
        plPhotosVideos: "Photos & Videos",
        plDocuments: "Documents",
        plUpcomingEvents: "Upcoming Events",
        plAnalytics: "Analytics",

        // ── Quick actions ──
        qaTitle: "Quick Actions",
        qaSubtitle: "Shortcuts to frequently used features",
        qaAddFamilyMember: "Add Family Member",
        qaViewFamilyTree: "View Family Tree",
        qaAddMemory: "Add Memory",
        qaScheduleEvent: "Schedule Event",
        qaInviteFamily: "Invite Family",

        // ── Charts ──
        chFamilyGrowth: "Family Growth",
        chRecords: "Records",
        chActivity: "Activity",
        chAllTime: "All time",
        chWeek: "Week",
        chTotal: "Total",
        chActivityEmpty: "Activity will appear as your family adds memories",
        recDocuments: "Documents",
        recPhotosVideos: "Photos & Videos",
        recHealthRecords: "Health Records",
        recLegalRecords: "Legal Records",
        recStories: "Stories",

        // ── Celebrations / events widgets ──
        dbTodaysCelebrations: "Today's Celebrations",
        dbViewAll: "View All",
        dbNoCelebToday: "No birthdays or anniversaries today.",
        dbTurnsToday: "turns {n} today!",
        dbRemembranceDay: "remembrance day",
        dbViewCalendar: "View Calendar",
        dbNoUpcoming60: "No upcoming events in the next 60 days.",
        evBirthdayOf: "{name}'s Birthday",
        evRemembranceOf: "{name} — Remembrance",
        evToday: "Today",
        evInDays: "in {n}d",
        evTurning: "turning {n}",

        // ── Recent activity ──
        dbRecentActivity: "Recent Activity",
        dbNoActivity: "No activity yet — add members or memories to get started.",
        actSomeone: "Someone",
        actPersonAdded: "added a new member",
        actPersonUpdated: "updated a member profile",
        actPersonDeleted: "removed a member",
        actMemoryUploaded: "uploaded a photo",
        actMemoryDeleted: "deleted a photo",
        actDocUploaded: "uploaded a document",
        actDocViewed: "viewed a document",
        actStoryRecorded: "recorded a story",
        actStoryPlayed: "played a story",
        actCalendarCreate: "created an event",
        actCalendarUpdate: "updated an event",
        actCapsuleCreated: "created a memory capsule",
        actAchievementAdded: "added an achievement",
        actDefault: "made an update",
        taJustNow: "just now",
        taMinAgo: "{n}m ago",
        taHourAgo: "{n}h ago",
        taDayAgo: "{n}d ago",

        // ── Getting started ──
        gsTitle: "Getting started",
        gsProgress: "{done}/{total} complete",
        gsAddMember: "Add a family member",
        gsUploadMemory: "Upload a memory",
        gsRecordStory: "Record a story",
        gsInviteFamily: "Invite family",
        gsSecureDoc: "Secure a document",
        gsCelebrateTitle: "Getting started complete!",
        gsCelebrateBody: "All 5 steps done — your family's digital legacy has officially begun. Keep growing your tree!",
        actProfileUpdated: "updated their profile",
        daySun: "Sun",
        dayMon: "Mon",
        dayTue: "Tue",
        dayWed: "Wed",
        dayThu: "Thu",
        dayFri: "Fri",
        daySat: "Sat",

        // ── Family Hub ──
        hbTitle: "Family — Everything in One Place",
        hbCategories: "Categories",
        hbSearchAll: "Search all features…",
        hbNoMatch: "No features match",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "SOON",
        hbNotifyMe: "Notify me",
        hbNotified: "Notified when ready",
        hbNotifyTitle: "We'll notify you when \"{title}\" goes live",
        hbNotifyBody: "Your interest is recorded. The team prioritises features that the most families ask for first.",
        hbGreat: "Great",

        // ── Records pages ──
        rcSearch: "Search…",
        rcExportCsv: "Export CSV",
        rcAddNew: "Add new",
        rcCreate: "Create",
        rcEdit: "Edit",
        rcSave: "Save",
        rcCancel: "Cancel",
        rcLoading: "Loading…",
        rcNoYet: "No {label} yet",
        rcClick: "Click",
        rcFirstEntry: "to add your first entry.",
        rcNew: "New",
        rcFormHint: "Fill the fields below — required fields are marked with a red star.",
        rcHealthBold: "Family record-keeping only",
        rcHealthRest: "— not for diagnosis, treatment or medical advice. Health records can be added or edited by the Family Head, with family consent.",
        rcConfirmTitle: "Please confirm",
        rcConfirmDelete: "Delete this record?",
        rcUpdated: "Updated.",
        rcSaved: "Saved.",
        rcCreated: "Created.",
        rcDeleted: "Deleted.",
        rcChoose: "Choose…",
        rcUnknownSection: "Unknown section",
        rcSectionMissing: "This Family Records section does not exist.",
        rcBackToHub: "Back to Family Hub",
        rcWatchDemo: "Watch Demo",
        rcDemoSoon: "Demo Coming Soon",
        rcFamilyService: "Family Service",
        rcUntitled: "(untitled)",

        // ── Settings ──
        setTitle: "Settings & Privacy",
        setBack: "Back",
        setTabInvites: "Invites",
        setTabSecurity: "Security",
        setTabData: "My Data",
        setTabActivity: "Activity",
        setTabHelp: "Help",
        setMyAccount: "My account",
        setMyAccountSub: "Add your email to receive receipts, welcome mail and support replies. Your phone number is your login and can't be changed here.",
        setName: "Name",
        setEmail: "Email",
        setEmailInvalid: "Enter a valid email address.",
        setSaveChanges: "Save changes",
        setSaving: "Saving…",
        setSaved: "Account details saved.",
        setFamilyMembers: "Family members",
        setRolesHint: "Admin: full control · Editor: add/edit members · Viewer: read only",
        setYou: "(You)",
        setRoleUpdated: "Role updated.",
        setActiveInvites: "Active invites",
        setInvitesSub: "Send invites from a member's profile drawer in the tree. Revoke anytime.",
        setNoActiveInvites: "No active invites.",
        setRevoke: "Revoke",
        setExpires: "expires",
        setOnlyAdmins: "Only family admins can manage invites.",
        setInviteRevoked: "Invite revoked.",
        setSeniorSub: "Simplifies privacy choices to 3 large options: Private / Family / Public.",
        setWelcomeTour: "Welcome tour",
        setWelcomeTourSub: "Replay the 3-step introduction: Family Tree → Memories → Vault.",
        setReplayTour: "Replay tour",
        setLivingIn: "Living in",
        setLivingInSub: "Optional — helps us serve NRI families better.",
        setIndia: "India",
        setAbroad: "Abroad (NRI)",
        setSelect: "Select",
        setWhatWeStore: "What we store about you",
        setDpdpNote: "DPDP Act: full transparency, always.",
        setProfileSections: "Profile sections",
        setVaultDocs: "Vault documents",
        setInvitesSent: "Invites sent",
        setActivityEntries: "Activity entries",
        setDownloadData: "Download all my data (JSON)",
        setDeleteTitle: "Delete my account & data",
        setDeleteSub: "Permanently removes your account, your stories, vault documents and activity log. Family tree entries you created about others remain with the family.",
        setDeleteEverything: "Delete everything",
        setDeleteConfirmTitle: "Delete your account permanently?",
        setDeleteConfirmBody: "This cannot be undone. Your stories, documents and account will be erased in line with the DPDP Act.",
        setDeleting: "Deleting…",
        setYesDelete: "Yes, delete everything",
        setActivityLog: "Activity log",
        setAuditAdminSub: "Who viewed and changed what, across your family.",
        setAuditOwnSub: "Your own activity.",
        setNoActivityYet: "No activity yet.",
        setSearchFamily: "Search family…",

        // ── Poster templates showcase (landing) ──
        ptsEyebrow: "From Our Design Studio",
        ptsTitle: "Beautiful Family Poster Templates",
        ptsSub: "Hand-crafted poster designs from our studio — your family photos fit right in. Pick your favourite after you sign up.",
        ptsCta: "Create Your Family Poster",
        ptsPremium: "Premium",

        // ── Help Center ──
        hcTitle: "Help Center",
        hcSub: "Ask us anything — we usually reply within a few hours.",
        hcAskTitle: "Ask a question",
        hcAskSub: "Describe your issue and our support team will get back to you.",
        hcSubject: "Subject",
        hcSubjectPh: "e.g. I can't upload a photo",
        hcMessage: "Your question",
        hcMessagePh: "Tell us what happened, step by step…",
        hcSubmit: "Send question",
        hcSubmitting: "Sending…",
        hcSuccess: "Your question has been sent — we'll reply soon.",
        hcFailed: "Could not send your question. Please try again.",
        hcFillBoth: "Please fill the subject and your question.",
        hcMyTickets: "My questions",
        hcNoTickets: "No questions yet — ask your first one above.",
        hcStatusOpen: "Open",
        hcStatusResolved: "Resolved",
        hcSupportReply: "Support",
        hcQuickHelp: "Quick help",
        hcWhatsapp: "WhatsApp support — instant chat",
        hcCall: "Call: +91-99999-99999 (Mon–Sat 10–7)",
        hcFaq: "FAQ — Frequently asked questions",
        hcTipTitle: "Tip",
        hcTipBody: "Most answers are already in the FAQ. For anything personal to your family's data, ask here — only our support team sees it.",

        // ── Demo tree photos + poster ──
        demoAddPhoto: "Add photo (optional)",
        demoChangePhoto: "Change photo",
        demoDownloadPoster: "Download poster",
        demoDownloading: "Preparing…",
    },
    hi: {
        tourListen: "सुनें",
        tourStop: "रोकें",
        tourEyebrow: "वंशट्री में आपका स्वागत है",
        tourStepOf: "चरण {n} / {total}",
        tourNext: "आगे",
        tourStart: "शुरू करें",
        tourS1Title: "अपना वंश वृक्ष बनाएं",
        tourS1Body: "माता-पिता, दादा-दादी और बच्चों को जोड़कर शुरुआत करें। हर सदस्य आपके परिवार की कहानी की एक जीवित शाखा बन जाता है।",
        tourS1Cta: "वंश वृक्ष खोलें",
        tourS2Title: "यादें संजोएं",
        tourS2Body: "फोटो अपलोड करें, आवाज़ें रिकॉर्ड करें और कहानियां लिखें। इन्हें एल्बम में सजाएं ताकि आने वाली पीढ़ियां इन्हें फिर से जी सकें।",
        tourS2Cta: "यादें खोलें",
        tourS3Title: "वॉल्ट सुरक्षित करें",
        tourS3Body: "वसीयत, संपत्ति के कागज़ात और कानूनी दस्तावेज़ बैंक-स्तरीय सुरक्षा में रखें — और सही समय पर अगली पीढ़ी को सौंपें।",
        tourS3Cta: "वॉल्ट खोलें",
        back: "वापस",
        cancel: "रद्द करें",
        delete: "हटाएं",
        logout: "लॉगआउट",
        optional: "(वैकल्पिक)",
        tagline: "आपका परिवार। हमेशा के लिए।",
        familyLegacy: "पारिवारिक विरासत",
        signInTitle: "Family Repository में साइन इन करें",
        otpTitle: "अपना OTP दर्ज करें",
        phoneSub: "हम आपके मोबाइल पर एक OTP भेजेंगे।",
        otpSentTo: "OTP भेजा गया",
        expiresIn: "यह समाप्त होगा",
        mobileNumber: "मोबाइल नंबर",
        phoneHint: "केवल भारतीय नंबर मान्य हैं (6–9 से शुरू)।",
        yourName: "आपका नाम",
        firstTimeOnly: "(केवल पहली बार)",
        haveInviteCode: "परिवार का इनवाइट कोड है?",
        apply: "लागू करें",
        sendOtp: "OTP भेजें",
        sendingOtp: "OTP भेजा जा रहा है…",
        otpLabel: "6 अंकों का OTP",
        verifyContinue: "सत्यापित करें और आगे बढ़ें",
        verifying: "सत्यापित हो रहा है…",
        changeNumber: "नंबर बदलें",
        resendOtp: "OTP दोबारा भेजें",
        resendIn: "दोबारा भेजें",
        terms: "जारी रखकर आप Family Repository की सेवा शर्तों और गोपनीयता नीति से सहमत होते हैं।",
        trustStrip: "एन्क्रिप्टेड • परिवार-नियंत्रित • भारत में निर्मित",
        welcome: "स्वागत है",
        dashSub: "आपका Family Repository तैयार है। अपना पहला सदस्य जोड़ें या किसी बुज़ुर्ग की कहानी रिकॉर्ड करें।",
        quickActions: "त्वरित कार्य",
        footerNote: "आपका डेटा निजी, एन्क्रिप्टेड और परिवार-नियंत्रित है। भारत के लिए बना — हमेशा।",
        roleFamilyHead: "परिवार प्रमुख",
        roleAdmin: "एडमिन",
        roleEditor: "संपादक",
        roleViewer: "दर्शक",
        roleMember: "सदस्य",
        cardTreeTitle: "वंश वृक्ष देखें",
        cardTreeDesc: "पीढ़ियों में फैला अपना पूरा परिवार देखें।",
        cardAddTitle: "सदस्य जोड़ें",
        cardAddDesc: "माता-पिता, भाई-बहन, बच्चे या रिश्तेदार जोड़ें।",
        cardMemoriesTitle: "यादें",
        cardMemoriesDesc: "परिवार के पलों की फोटो अपलोड करें।",
        cardStoryTitle: "कहानी रिकॉर्ड करें",
        cardStoryDesc: "बुज़ुर्गों की आवाज़ हमेशा के लिए सहेजें।",
        cardVaultTitle: "दस्तावेज़ वॉल्ट",
        cardVaultDesc: "वसीयत, दस्तावेज़ — सुरक्षित, आपकी शर्तों पर साझा।",
        cardSettingsTitle: "गोपनीयता और सेटिंग्स",
        cardSettingsDesc: "भूमिकाएँ, निमंत्रण, आपका डेटा और गतिविधि लॉग।",
        memories: "यादें",
        addMemory: "याद जोड़ें",
        memoriesEmpty: "अभी कोई यादें नहीं",
        memoriesEmptySub: "अपनी पहली पारिवारिक फोटो अपलोड करें — शादियाँ, त्योहार, रोज़ की खुशियाँ।",
        caption: "कैप्शन",
        captionPlaceholder: "जैसे — नानी के घर दिवाली, 1998",
        memoryDate: "यह कब की है?",
        tagMember: "परिवार के सदस्य को टैग करें",
        choosePhoto: "फोटो चुनें",
        changePhoto: "फोटो बदलें",
        uploadMemory: "याद सहेजें",
        uploading: "अपलोड हो रहा है…",
        deleteMemoryConfirm: "क्या यह याद हटाएं? इसे वापस नहीं लाया जा सकता।",
        confirmDelete: "हटाने के लिए फिर टैप करें",
        uploadedBy: "जोड़ा गया",
        heroEyebrow: "आपका परिवार · आपकी विरासत",
        heroHeadline1: "आपके परिवार का इतिहास",
        heroHeadlineAccent: "सुरक्षित",
        heroHeadline2: "हमेशा के लिए।",
        heroSubline: "अपना वंश वृक्ष बनाएँ, बुज़ुर्गों की आवाज़ रिकॉर्ड करें, फ़ोटो और दस्तावेज़ सहेजें — भारतीय परिवारों के लिए, आपकी अपनी भाषा में।",
        heroCtaPrimary: "मोबाइल OTP से मुफ़्त शुरू करें",
        heroCtaSecondary: "देखें कैसे काम करता है",
        heroTrustPrivate: "निजी डिफ़ॉल्ट",
        heroTrustLangs: "10 भारतीय भाषाएँ",
        heroTrustFree: "मुफ़्त शुरुआत",
        heroSocialProof: "दुनिया भर के भारतीय परिवारों का भरोसा",
        heroBadge10: "10+",
        heroBadge10Cap: "भारतीय भाषाएँ",
        heroStoryAudio: "आवाज़ कहानियाँ",
        heroStoryPhotos: "फ़ोटो",
        heroStoryDocs: "दस्तावेज़",
        statHappyFamilies: "खुश परिवार",
        statTreesCreated: "सदस्य संरक्षित",
        statStoriesRecorded: "कहानियाँ व यादें",
        statDataPrivacy: "डेटा गोपनीयता",
        demoBadge: "बिना साइनअप के आज़माएँ",
        demoHeadline: "अपना वंश वृक्ष जीवंत होते देखें — 30 सेकंड में",
        demoSubline: "6 नाम जोड़ें। 6 पीढ़ियों का वंश वृक्ष यहीं एनिमेट होगा।",
        demoTrustNoPhone: "कोई फ़ोन नहीं",
        demoTrustNoOtp: "कोई OTP नहीं",
        demoTrustNoSignup: "कोई साइनअप नहीं",
        demoStepPrefix: "चरण",
        demoStepOf: "में से 6 —",
        demoLabelFather: "पिता का नाम",
        demoPhFather: "जैसे — राजेश शर्मा",
        demoLabelGrandfather: "दादा का नाम",
        demoPhGrandfather: "जैसे — मोहन शर्मा",
        demoLabelGreatGrand: "परदादा का नाम",
        demoPhGreatGrand: "जैसे — किशन शर्मा",
        demoLabelChild: "बेटा/बेटी का नाम",
        demoPhChild: "जैसे — अनन्या शर्मा",
        demoLabelGrandchild: "पोता/पोती का नाम",
        demoPhGrandchild: "जैसे — विहान शर्मा",
        roleFather: "पिता",
        roleGrandfather: "दादा",
        roleGreatGrandfather: "परदादा",
        roleChild: "बेटा/बेटी",
        roleGrandchild: "पोता/पोती",
        demoLabelYou: "आपका नाम",
        demoLabelParent: "आपके माता/पिता का नाम",
        demoLabelGrand: "आपके दादा/दादी का नाम",
        demoPhYou: "जैसे — राहुल शर्मा",
        demoPhParent: "जैसे — सुरेश शर्मा",
        demoPhGrand: "जैसे — रामचंद्र शर्मा",
        demoHint: "अपना वृक्ष देखने के लिए {n} और नाम जोड़ें।",
        demoNext: "आगे",
        demoSeeTree: "अपना वृक्ष देखें",
        demoRestart: "फिर से कोशिश",
        demoStartReal: "असली वृक्ष शुरू करें (मुफ़्त)",
        demoTreeBadge: "आपका मिनी वृक्ष",
        demoChooseDesign: "पोस्टर डिज़ाइन चुनें",
        demoClassic: "क्लासिक",
        demoFamilyTitle: "{name} परिवार",
        demoShareWhatsApp: "WhatsApp पर भेजें",
        demoShareText: "मैंने 30 सेकंड में हमारा परिवार वृक्ष बनाया! 🌳 आप भी मुफ़्त बनाएँ: {link}",
        demoScanToJoin: "स्कैन करें, मुफ़्त बनाएँ",
        demoTreeHeadline: "छह पीढ़ियाँ, एक जीवित वृक्ष।",
        demoTreeAddMore: "20+ और रिश्तेदार, फ़ोटो और यादें जोड़ें — मुफ़्त।",
        demoFooterPrivacy: "आपके डेमो नाम केवल आपके ब्राउज़र में रहते हैं। कुछ भी सहेजा या भेजा नहीं जाता।",
        roleGrandparent: "दादा/दादी",
        roleParent: "माता/पिता",
        roleYou: "आप",
        testHeadline: "पूरे भारत के परिवारों का भरोसा",
        testSubline: "असली परिवार, असली कहानियाँ, हमेशा के लिए सहेजी।",
        testShareBtn: "अपनी पारिवारिक कहानी साझा करें",
        testShareHint: "क्या Family Repository ने आपके परिवार की मदद की है? अपनी कहानी साझा करें।",
        noOtpTitle: "Family Repository आपसे कभी OTP नहीं माँगेगा।",
        noOtpBody: "अगर कोई OTP माँगे तो तुरंत कॉल काट दें। हम कभी OTP नहीं माँगते।",
        navLogin: "लॉगिन",
        navStartFree: "मुफ़्त शुरू करें",
        navDashboard: "डैशबोर्ड",
        tnHome: "होम",
        tnFamilyTree: "वंश वृक्ष",
        tnFamilyHub: "परिवार हब",
        tnEvents: "कार्यक्रम",
        tnPackages: "पैकेज",
        tnVault: "वॉल्ट",
        tnReports: "रिपोर्ट",
        tnMore: "और",
        tnRecords: "रेकॉर्ड",
        tnSupport: "सहायता",
        sbDashboard: "डैशबोर्ड",
        sbMemberProfile: "सदस्य प्रोफ़ाइल",
        sbgConnections: "रिश्ते",
        sbConnectFriends: "विस्तारित परिवार से जुड़ें",
        sbConnectRelatives: "करीबी परिवार से जुड़ें",
        sbWorldMap: "विश्व मानचित्र",
        sbgMemories: "यादें",
        sbPhotoMemories: "फोटो यादें",
        sbAudioMemories: "ऑडियो यादें",
        sbVideoMemories: "वीडियो यादें",
        sbgEvents: "कार्यक्रम और रिमाइंडर",
        sbFamilyCalendar: "परिवार कैलेंडर",
        sbTodaysCelebration: "आज का उत्सव",
        sbFamilyTimeline: "व्यक्ति की जीवन यात्रा",
        sbSmartReminder: "स्मार्ट रिमाइंडर",
        sbgHealth: "स्वास्थ्य और चिकित्सा भंडार",
        sbMedicalHistory: "चिकित्सा इतिहास",
        sbChronicDiseases: "दीर्घकालिक / वंशानुगत बीमारियाँ",
        sbCancerRegistry: "कैंसर रजिस्ट्र्री",
        sbGeneticCongenital: "आनुवंशिक और जन्मजात",
        sbAllergies: "एलर्जी और असहिष्णुताएँ",
        sbVaccination: "टीकाकरण रिकॉर्ड",
        sbLifestyle: "लाइफ़स्टाइल डैशबोर्ड",
        sbPregnancyBirth: "गर्भावस्था और जन्म इतिहास",
        sbOrganDonation: "अंगदान वरीयता",
        sbBloodGroup: "रक्त समूह रजिस्ट्र्री",
        sbgVault: "निजी वॉल्ट",
        sbDocumentVault: "दस्तावेज़ वॉल्ट",
        sbLegalVault: "कानूनी वॉल्ट",
        sbgDisplay: "डिस्प्ले और डाउनलोड",
        sbDownloadExport: "डाउनलोड और एक्सपोर्ट",
        sbDigitalFamilyTree: "डिजिटल फैमिली ट्री",
        sbPackagesPlans: "पैकेज और प्लान",
        sbPrivacySettings: "प्राइवेसी और सेटिंग्स",
        hdrDashboard: "डैशबोर्ड",
        hdrMenu: "मेन्यू",
        hdrMyProfile: "मेरा प्रोफाइल",
        hdrSettings: "सेटिंग्स",
        hdrSignOut: "साइन आउट",
        signOutTitle: "Family Repository से साइन आउट करें?",
        signOutBody: "आप कभी भी अपने मोबाइल OTP से दोबारा लॉगिन कर सकते हैं।",
        roleAdministrator: "प्रशासक",
        roleMemberPlain: "सदस्य",
        searchPlaceholder: "कुछ भी खोजें…",
        searchNoResults: "इसके लिए कोई परिणाम नहीं",
        grpMembers: "सदस्य",
        grpMemories: "यादें",
        grpDocuments: "दस्तावेज़",
        grpStories: "कहानियाँ",
        grpEvents: "घटनाएँ",
        msgTitle: "संदेश और रिमाइंडर",
        msgMarkAll: "सभी को पढ़ा हुआ चिह्नित करें",
        msgEmpty: "अभी तक कोई संदेश नहीं है — रिमाइंडर और परिवार से जुड़ी जानकारी यहाँ दिखेगी।",
        mbHome: "होम",
        mbTree: "ट्री",
        mbAdd: "जोड़ें",
        mbHub: "हब",
        mbMe: "मैं",
        dbMemberSince: "सदस्य बने",
        dbGrowingTogether: "साथ-साथ बढ़ते हुए",
        dbGrowingSub: "हर परिवार की एक कहानी होती है।",
        dbProfileCompleteness: "प्रोफ़ाइल पूर्णता",
        dbMemoryOfDay: "आज की याद",
        dbMemoryQuote: "आज का परिवार — कल की प्रेरणा।",
        dbViewMemory: "याद देखें",
        dbGoPremium: "प्रीमियम लें",
        dbPremiumDesc: "उन्नत फ़ीचर खोलें और अपने पारिवारिक डेटा को सुरक्षित रखें।",
        dbUpgradeNow: "अभी अपग्रेड करें",
        plMembers: "सदस्य",
        plGenerations: "पीढ़ियाँ",
        plYearsHistory: "इतने सालों का इतिहास",
        plFamilyAccounts: "परिवार के खाते",
        plTotalRecords: "कुल रिकॉर्ड",
        plPhotosVideos: "फोटो और वीडियो",
        plDocuments: "दस्तावेज़",
        plUpcomingEvents: "आगामी कार्यक्रम",
        plAnalytics: "विश्लेषण",
        qaTitle: "त्वरित कार्रवाई",
        qaSubtitle: "अक्सर उपयोग की जाने वाली सुविधाओं के शॉर्टकट",
        qaAddFamilyMember: "परिवार का सदस्य जोड़ें",
        qaViewFamilyTree: "वंश-वृक्ष देखें",
        qaAddMemory: "याद जोड़ें",
        qaScheduleEvent: "कार्यक्रम तय करें",
        qaInviteFamily: "परिवार को आमंत्रित करें",
        chFamilyGrowth: "परिवार की बढ़ोतरी",
        chRecords: "रिकॉर्ड",
        chActivity: "गतिविधि",
        chAllTime: "अब तक",
        chWeek: "सप्ताह",
        chTotal: "कुल",
        chActivityEmpty: "जैसे-जैसे आपका परिवार यादें जोड़ेगा, गतिविधियाँ यहाँ दिखेंगी",
        recDocuments: "दस्तावेज़",
        recPhotosVideos: "फोटो और वीडियो",
        recHealthRecords: "स्वास्थ्य रिकॉर्ड",
        recLegalRecords: "कानूनी रिकॉर्ड",
        recStories: "कहानियाँ",
        dbTodaysCelebrations: "आज के उत्सव",
        dbViewAll: "सब देखें",
        dbNoCelebToday: "आज कोई जन्मदिन या सालगिरह नहीं है।",
        dbTurnsToday: "आज {n} साल के हो रहे हैं!",
        dbRemembranceDay: "स्मृति दिवस",
        dbViewCalendar: "कैलेंडर देखें",
        dbNoUpcoming60: "अगले 60 दिनों में कोई आने वाले कार्यक्रम नहीं हैं।",
        evBirthdayOf: "{name} का जन्मदिन",
        evRemembranceOf: "{name} — स्मरण",
        evToday: "आज",
        evInDays: "{n} दिन में",
        evTurning: "{n} वर्ष के हो रहे हैं",
        dbRecentActivity: "हाल की गतिविधि",
        dbNoActivity: "अभी तक कोई गतिविधि नहीं है — शुरू करने के लिए सदस्य या यादें जोड़ें।",
        actSomeone: "कोई",
        actPersonAdded: "ने नया सदस्य जोड़ा",
        actPersonUpdated: "ने किसी सदस्य की प्रोफ़ाइल अपडेट की",
        actPersonDeleted: "ने एक सदस्य हटाया",
        actMemoryUploaded: "ने एक फ़ोटो अपलोड की",
        actMemoryDeleted: "ने एक फ़ोटो हटाई",
        actDocUploaded: "ने एक दस्तावेज़ अपलोड किया",
        actDocViewed: "ने एक दस्तावेज़ देखा",
        actStoryRecorded: "ने एक कहानी रिकॉर्ड की",
        actStoryPlayed: "ने एक कहानी सुनी",
        actCalendarCreate: "ने एक कार्यक्रम बनाया",
        actCalendarUpdate: "ने एक कार्यक्रम अपडेट किया",
        actCapsuleCreated: "ने एक मेमरी कैप्सूल बनाया",
        actAchievementAdded: "ने एक उपलब्धि जोड़ी",
        actDefault: "ने एक अपडेट किया",
        taJustNow: "अभी अभी",
        taMinAgo: "{n} मिनट पहले",
        taHourAgo: "{n} घंटे पहले",
        taDayAgo: "{n} दिन पहले",
        gsTitle: "शुरू करना",
        gsProgress: "{done}/{total} पूरा",
        gsAddMember: "एक परिवार का सदस्य जोड़ें",
        gsUploadMemory: "एक याद अपलोड करें",
        gsRecordStory: "एक कहानी रिकॉर्ड करें",
        gsInviteFamily: "परिवार को आमंत्रित करें",
        gsSecureDoc: "एक दस्तावेज़ सुरक्षित करें",
        gsCelebrateTitle: "शुरुआती चरण पूरे हो गए!",
        gsCelebrateBody: "सभी 5 चरण पूरे — आपके परिवार की डिजिटल विरासत आधिकारिक रूप से शुरू हो चुकी है। अपना पारिवारिक पेड़ बढ़ाते रहें!",
        actProfileUpdated: "ने अपनी प्रोफ़ाइल अपडेट की",
        daySun: "रवि",
        dayMon: "सोम",
        dayTue: "मंगल",
        dayWed: "बुध",
        dayThu: "गुरु",
        dayFri: "शुक्र",
        daySat: "शनि",
        hbTitle: "परिवार — सब कुछ एक ही जगह",
        hbCategories: "श्रेणियाँ",
        hbSearchAll: "सभी सुविधाओं में खोजें…",
        hbNoMatch: "कोई सुविधा मेल नहीं खा रही",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "जल्द",
        hbNotifyMe: "मुझे सूचित करें",
        hbNotified: "तैयार होने पर सूचित किया गया",
        hbNotifyTitle: "जब \\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\" LIVE होगा, हम आपको सूचित करेंगे",
        hbNotifyBody: "आपकी रुचि दर्ज कर ली गई है। हमारी टीम पहले वही सुविधाएँ बनाती है, जिनके लिए सबसे ज़्यादा परिवार अनुरोध करते हैं।",
        hbGreat: "बहुत बढ़िया",
        rcSearch: "खोजें…",
        rcExportCsv: "CSV निर्यात करें",
        rcAddNew: "नया जोड़ें",
        rcCreate: "बनाएँ",
        rcEdit: "संपादित करें",
        rcSave: "सहेजें",
        rcCancel: "रद्द करें",
        rcLoading: "लोड हो रहा है…",
        rcNoYet: "अभी तक कोई {label} नहीं",
        rcClick: "क्लिक करें",
        rcFirstEntry: "अपनी पहली एंट्री जोड़ने के लिए।",
        rcNew: "नया",
        rcFormHint: "नीचे दिए गए फ़ील्ड भरें — जिन पर लाल सितारा लगा है, वे अनिवार्य हैं।",
        rcHealthBold: "केवल परिवार रिकॉर्ड रखने के लिए",
        rcHealthRest: "— निदान, इलाज या चिकित्सीय सलाह के लिए नहीं। स्वास्थ्य रिकॉर्ड परिवार की सहमति से केवल परिवार प्रमुख द्वारा जोड़े या बदले जा सकते हैं।",
        rcConfirmTitle: "कृपया पुष्टि करें",
        rcConfirmDelete: "यह रिकॉर्ड हटाएँ?",
        rcUpdated: "अपडेट कर दिया गया।",
        rcSaved: "सहेजा गया।",
        rcCreated: "बना दिया गया।",
        rcDeleted: "हटा दिया गया।",
        rcChoose: "चुनें…",
        rcUnknownSection: "अज्ञात सेक्शन",
        rcSectionMissing: "यह Family Records सेक्शन मौजूद नहीं है।",
        rcBackToHub: "Family Hub पर वापस जाएँ",
        rcWatchDemo: "डेमो देखें",
        rcDemoSoon: "डेमो जल्द ही उपलब्ध होगा",
        rcFamilyService: "परिवार सेवा",
        rcUntitled: "(शीर्षकहीन)",
        setTitle: "सेटिंग्स और गोपनीयता",
        setBack: "पीछे",
        setTabInvites: "निमंत्रण",
        setTabSecurity: "सुरक्षा",
        setTabData: "मेरा डेटा",
        setTabActivity: "गतिविधि",
        setTabHelp: "मदद",
        setMyAccount: "मेरा खाता",
        setMyAccountSub: "रसीदें, वेलकम मेल और सपोर्ट के जवाब पाने के लिए अपना ईमेल जोड़ें। आपका मोबाइल नंबर ही आपका लॉगिन है और इसे यहाँ बदला नहीं जा सकता।",
        setName: "नाम",
        setEmail: "ईमेल",
        setEmailInvalid: "कृपया एक सही ईमेल पता दर्ज करें।",
        setSaveChanges: "परिवर्तन सहेजें",
        setSaving: "सहेजा जा रहा है…",
        setSaved: "खाते का विवरण सहेजा गया।",
        setFamilyMembers: "परिवार के सदस्य",
        setRolesHint: "एडमिन: पूरा नियंत्रण · एडिटर: सदस्य जोड़ें/बदलें · व्यूअर: केवल देख सकते हैं",
        setYou: "(आप)",
        setRoleUpdated: "भूमिका अपडेट कर दी गई है।",
        setActiveInvites: "सक्रिय आमंत्रण",
        setInvitesSub: "पेड़ में किसी सदस्य की प्रोफ़ाइल ड्रॉअर से आमंत्रण भेजें। कभी भी रद्द कर सकते हैं।",
        setNoActiveInvites: "कोई सक्रिय आमंत्रण नहीं।",
        setRevoke: "रद्द करें",
        setExpires: "समाप्त होता है",
        setOnlyAdmins: "केवल परिवार के एडमिन ही आमंत्रण प्रबंधित कर सकते हैं।",
        setInviteRevoked: "आमंत्रण रद्द कर दिया गया।",
        setSeniorSub: "गोपनीयता विकल्पों को 3 बड़े विकल्पों में सरल बनाता है: निजी / परिवार / सार्वजनिक।",
        setWelcomeTour: "स्वागत दौरा",
        setWelcomeTourSub: "3-चरणीय परिचय दोबारा देखें: Family Tree → Memories → Vault.",
        setReplayTour: "दौरा दोबारा देखें",
        setLivingIn: "रह रहे हैं",
        setLivingInSub: "वैकल्पिक — NRI परिवारों की बेहतर सेवा में मदद करता है।",
        setIndia: "भारत",
        setAbroad: "विदेश (NRI)",
        setSelect: "चुनें",
        setWhatWeStore: "हम आपके बारे में क्या सहेजते हैं",
        setDpdpNote: "DPDP Act: पूरी पारदर्शिता, हमेशा।",
        setProfileSections: "प्रोफ़ाइल खंड",
        setVaultDocs: "Vault दस्तावेज़",
        setInvitesSent: "निमंत्रण भेजे गए",
        setActivityEntries: "गतिविधि प्रविष्टियाँ",
        setDownloadData: "मेरा सारा डेटा डाउनलोड करें (JSON)",
        setDeleteTitle: "मेरा खाता और डेटा मिटाएँ",
        setDeleteSub: "आपका खाता, आपकी कहानियाँ, तिजोरी के दस्तावेज़ और गतिविधि लॉग हमेशा के लिए हटा दिए जाएँगे। आपने दूसरों के बारे में जो परिवार-वृक्ष प्रविष्टियाँ बनाई हैं, वे परिवार के साथ बनी रहेंगी।",
        setDeleteEverything: "सब कुछ मिटाएँ",
        setDeleteConfirmTitle: "क्या आप अपना खाता हमेशा के लिए मिटाना चाहते हैं?",
        setDeleteConfirmBody: "इसे वापस नहीं किया जा सकता। आपकी कहानियाँ, दस्तावेज़ और खाता DPDP कानून के अनुसार मिटा दिए जाएँगे।",
        setDeleting: "मिटाया जा रहा है…",
        setYesDelete: "हाँ, सब कुछ मिटाएँ",
        setActivityLog: "गतिविधि लॉग",
        setAuditAdminSub: "आपके पूरे परिवार में किसने क्या देखा और बदला।",
        setAuditOwnSub: "आपकी अपनी गतिविधि।",
        setNoActivityYet: "अभी तक कोई गतिविधि नहीं।",
        setSearchFamily: "परिवार में खोजें…",
        ptsEyebrow: "हमारे डिज़ाइन स्टूडियो से",
        ptsTitle: "सुंदर परिवार पोस्टर टेम्पलेट",
        ptsSub: "हमारे स्टूडियो के हाथों से बने पोस्टर डिज़ाइन — आपकी परिवार की तस्वीरें इनमें बिल्कुल फिट बैठती हैं। साइन अप करने के बाद अपना पसंदीदा चुनें।",
        ptsCta: "अपना परिवार पोस्टर बनाएं",
        ptsPremium: "प्रीमियम",
        hcTitle: "सहायता केंद्र",
        hcSub: "हमसे कुछ भी पूछें — हम आम तौर पर कुछ घंटों के अंदर जवाब देते हैं।",
        hcAskTitle: "प्रश्न पूछें",
        hcAskSub: "अपनी समस्या बताएं, हमारी सपोर्ट टीम आपको जवाब देगी।",
        hcSubject: "विषय",
        hcSubjectPh: "जैसे: मैं फोटो अपलोड नहीं कर पा रहा/रही हूं",
        hcMessage: "आपका प्रश्न",
        hcMessagePh: "एक–एक करके बताइए क्या हुआ…",
        hcSubmit: "प्रश्न भेजें",
        hcSubmitting: "भेजा जा रहा है…",
        hcSuccess: "आपका प्रश्न भेज दिया गया है — हम जल्द ही जवाब देंगे।",
        hcFailed: "आपका प्रश्न नहीं भेजा जा सका। कृपया दोबारा कोशिश करें।",
        hcFillBoth: "कृपया विषय और अपना प्रश्न, दोनों भरें।",
        hcMyTickets: "मेरे प्रश्न",
        hcNoTickets: "अभी तक कोई प्रश्न नहीं — ऊपर अपना पहला प्रश्न पूछें।",
        hcStatusOpen: "खुला",
        hcStatusResolved: "सुलझा दिया गया",
        hcSupportReply: "सपोर्ट",
        hcQuickHelp: "तुरंत मदद",
        hcWhatsapp: "WhatsApp सपोर्ट — तुरंत चैट",
        hcCall: "कॉल: +91-99999-99999 (सोम–शनि 10–7)",
        hcFaq: "FAQ — अक्सर पूछे जाने वाले सवाल",
        hcTipTitle: "सलाह",
        hcTipBody: "ज़्यादातर जवाब पहले से ही FAQ में दिए गए हैं। आपके परिवार के डेटा से जुड़ी कोई भी निजी बात हो, तो यहाँ पूछें — इसे सिर्फ़ हमारी सपोर्ट टीम ही देखती है।",
        demoAddPhoto: "फोटो जोड़ें (वैकल्पिक)",
        demoChangePhoto: "फोटो बदलें",
        demoDownloadPoster: "पोस्टर डाउनलोड करें",
        demoDownloading: "तैयार किया जा रहा है…",
    },
    mr: {
        tourListen: "ऐका",
        tourStop: "थांबवा",
        tourEyebrow: "वंशट्रीमध्ये आपले स्वागत आहे",
        tourStepOf: "पायरी {n} / {total}",
        tourNext: "पुढे",
        tourStart: "सुरुवात करा",
        tourS1Title: "आपला वंशवृक्ष तयार करा",
        tourS1Body: "आई-वडील, आजी-आजोबा आणि मुलांना जोडून सुरुवात करा. प्रत्येक सदस्य आपल्या कुटुंबाच्या कथेची जिवंत शाखा बनतो.",
        tourS1Cta: "वंशवृक्ष उघडा",
        tourS2Title: "आठवणी जपा",
        tourS2Body: "फोटो अपलोड करा, आवाज रेकॉर्ड करा आणि गोष्टी लिहा. त्या अल्बममध्ये लावा म्हणजे पुढच्या पिढ्या त्या पुन्हा अनुभवू शकतील.",
        tourS2Cta: "आठवणी उघडा",
        tourS3Title: "व्हॉल्ट सुरक्षित करा",
        tourS3Body: "मृत्युपत्र, मालमत्तेची कागदपत्रे आणि कायदेशीर दस्तऐवज बँक-दर्जाच्या सुरक्षेत ठेवा — आणि योग्य वेळी पुढच्या पिढीकडे सोपवा.",
        tourS3Cta: "व्हॉल्ट उघडा",
        back: "मागे",
        cancel: "रद्द करा",
        delete: "हटवा",
        logout: "लॉगआउट",
        optional: "(ऐच्छिक)",
        tagline: "तुमचे कुटुंब. कायमचे.",
        familyLegacy: "कौटुंबिक वारसा",
        signInTitle: "Family Repository मध्ये साइन इन करा",
        otpTitle: "तुमचा OTP टाका",
        phoneSub: "आम्ही तुमच्या मोबाइलवर OTP पाठवू.",
        otpSentTo: "OTP पाठवला",
        expiresIn: "तो संपेल",
        mobileNumber: "मोबाइल नंबर",
        phoneHint: "फक्त भारतीय नंबर (6–9 ने सुरू).",
        yourName: "तुमचे नाव",
        firstTimeOnly: "(फक्त पहिल्यांदा)",
        haveInviteCode: "कुटुंबाचा इनव्हाइट कोड आहे?",
        apply: "लागू करा",
        sendOtp: "OTP पाठवा",
        sendingOtp: "OTP पाठवत आहे…",
        otpLabel: "6 अंकी OTP",
        verifyContinue: "पडताळा आणि पुढे जा",
        verifying: "पडताळत आहे…",
        changeNumber: "नंबर बदला",
        resendOtp: "OTP पुन्हा पाठवा",
        resendIn: "पुन्हा पाठवा",
        terms: "पुढे जाऊन तुम्ही Family Repository च्या सेवा अटी व गोपनीयता धोरणास सहमती देता.",
        trustStrip: "एन्क्रिप्टेड • कुटुंब-नियंत्रित • भारतात बनवले",
        welcome: "स्वागत आहे",
        dashSub: "तुमचे Family Repository तयार आहे. पहिला सदस्य जोडा किंवा वडीलधाऱ्यांची गोष्ट रेकॉर्ड करा.",
        quickActions: "जलद कृती",
        footerNote: "तुमचा डेटा खाजगी, एन्क्रिप्टेड आणि कुटुंब-नियंत्रित आहे. भारतासाठी बनवले — नेहमीच.",
        roleFamilyHead: "कुटुंबप्रमुख",
        roleAdmin: "अ‍ॅडमिन",
        roleEditor: "संपादक",
        roleViewer: "प्रेक्षक",
        roleMember: "सदस्य",
        cardTreeTitle: "वंशवृक्ष पहा",
        cardTreeDesc: "पिढ्यान्‌पिढ्यांचे तुमचे संपूर्ण कुटुंब पहा.",
        cardAddTitle: "सदस्य जोडा",
        cardAddDesc: "आई-वडील, भावंडे, मुले किंवा नातेवाईक जोडा.",
        cardMemoriesTitle: "आठवणी",
        cardMemoriesDesc: "कुटुंबाच्या क्षणांचे फोटो अपलोड करा.",
        cardStoryTitle: "गोष्ट रेकॉर्ड करा",
        cardStoryDesc: "वडीलधाऱ्यांचा आवाज कायमचा जपा.",
        cardVaultTitle: "दस्तऐवज व्हॉल्ट",
        cardVaultDesc: "मृत्युपत्रे, कागदपत्रे — सुरक्षित, तुमच्या अटींवर.",
        cardSettingsTitle: "गोपनीयता आणि सेटिंग्ज",
        cardSettingsDesc: "भूमिका, आमंत्रणे, तुमचा डेटा आणि अ‍ॅक्टिव्हिटी लॉग.",
        memories: "आठवणी",
        addMemory: "आठवण जोडा",
        memoriesEmpty: "अजून आठवणी नाहीत",
        memoriesEmptySub: "पहिला कौटुंबिक फोटो अपलोड करा — लग्न, सण, रोजचा आनंद.",
        caption: "कॅप्शन",
        captionPlaceholder: "उदा. आजीच्या घरी दिवाळी, 1998",
        memoryDate: "हे कधीचे आहे?",
        tagMember: "कुटुंब सदस्याला टॅग करा",
        choosePhoto: "फोटो निवडा",
        changePhoto: "फोटो बदला",
        uploadMemory: "आठवण जतन करा",
        uploading: "अपलोड होत आहे…",
        deleteMemoryConfirm: "ही आठवण हटवायची? हे परत आणता येणार नाही.",
        confirmDelete: "पुष्टीसाठी पुन्हा टॅप करा",
        uploadedBy: "जोडले",
        heroEyebrow: "तुमचे कुटुंब · तुमचा वारसा",
        heroHeadline1: "तुमच्या कुटुंबाचा इतिहास",
        heroHeadlineAccent: "जपून",
        heroHeadline2: "कायमचा.",
        heroSubline: "तुमचे कुटुंब वृक्ष तयार करा, वडीलधाऱ्यांचे आवाज रेकॉर्ड करा, फोटो व कागदपत्रे जतन करा — भारतीय कुटुंबांसाठी, तुमच्या भाषेत.",
        heroCtaPrimary: "मोबाइल OTP ने मोफत सुरू करा",
        heroCtaSecondary: "हे कसे काम करते ते पहा",
        heroTrustPrivate: "डीफॉल्ट खाजगी",
        heroTrustLangs: "10 भारतीय भाषा",
        heroTrustFree: "मोफत सुरुवात",
        heroSocialProof: "जगभरातील भारतीय कुटुंबांचा विश्वास",
        heroBadge10: "10+",
        heroBadge10Cap: "भारतीय भाषा",
        heroStoryAudio: "आवाज कथा",
        heroStoryPhotos: "फोटो",
        heroStoryDocs: "कागदपत्रे",
        statHappyFamilies: "आनंदी कुटुंबे",
        statTreesCreated: "सदस्य जतन केले",
        statStoriesRecorded: "कथा व आठवणी",
        statDataPrivacy: "डेटा गोपनीयता",
        demoBadge: "साइनअप शिवाय पहा",
        demoHeadline: "तुमचे कुटुंब वृक्ष जिवंत होताना पहा — 30 सेकंदांत",
        demoSubline: "6 नावे जोडा. 6 पिढ्यांचे कुटुंब वृक्ष येथेच अ‍ॅनिमेट होईल.",
        demoTrustNoPhone: "फोन नाही",
        demoTrustNoOtp: "OTP नाही",
        demoTrustNoSignup: "साइनअप नाही",
        demoStepPrefix: "पायरी",
        demoStepOf: "पैकी 6 —",
        demoLabelFather: "वडिलांचे नाव",
        demoPhFather: "उदा. राजेश शर्मा",
        demoLabelGrandfather: "आजोबांचे नाव",
        demoPhGrandfather: "उदा. मोहन शर्मा",
        demoLabelGreatGrand: "पणजोबांचे नाव",
        demoPhGreatGrand: "उदा. किशन शर्मा",
        demoLabelChild: "मुलगा/मुलीचे नाव",
        demoPhChild: "उदा. अनन्या शर्मा",
        demoLabelGrandchild: "नातवंडाचे नाव",
        demoPhGrandchild: "उदा. विहान शर्मा",
        roleFather: "वडील",
        roleGrandfather: "आजोबा",
        roleGreatGrandfather: "पणजोबा",
        roleChild: "मुलगा/मुलगी",
        roleGrandchild: "नातवंड",
        demoLabelYou: "तुमचे नाव",
        demoLabelParent: "तुमच्या पालकाचे नाव",
        demoLabelGrand: "तुमच्या आजी/आजोबांचे नाव",
        demoPhYou: "उदा. राहुल शर्मा",
        demoPhParent: "उदा. सुरेश शर्मा",
        demoPhGrand: "उदा. रामचंद्र शर्मा",
        demoHint: "तुमचे वृक्ष पाहण्यासाठी आणखी {n} नावे जोडा.",
        demoNext: "पुढे",
        demoSeeTree: "तुमचे वृक्ष पहा",
        demoRestart: "पुन्हा प्रयत्न",
        demoStartReal: "खरे वृक्ष सुरू करा (मोफत)",
        demoTreeBadge: "तुमचे मिनी वृक्ष",
        demoChooseDesign: "पोस्टर डिझाईन निवडा",
        demoClassic: "क्लासिक",
        demoFamilyTitle: "{name} कुटुंब",
        demoShareWhatsApp: "WhatsApp वर पाठवा",
        demoShareText: "मी 30 सेकंदात आमचे कुटुंब वृक्ष बनवले! 🌳 तुम्हीही मोफत बनवा: {link}",
        demoScanToJoin: "स्कॅन करा, मोफत बनवा",
        demoTreeHeadline: "सहा पिढ्या, एक जिवंत वृक्ष.",
        demoTreeAddMore: "आणखी 20+ नातेवाईक, फोटो व आठवणी जोडा — मोफत.",
        demoFooterPrivacy: "तुमची डेमो माहिती फक्त तुमच्या ब्राउझरमध्ये राहते. काहीही सेव्ह किंवा पाठवले जात नाही.",
        roleGrandparent: "आजी/आजोबा",
        roleParent: "पालक",
        roleYou: "तुम्ही",
        testHeadline: "भारतभरातील कुटुंबांचा विश्वास",
        testSubline: "खरी कुटुंबे, खऱ्या कथा, कायमच्या जतन.",
        testShareBtn: "तुमची कौटुंबिक कथा शेअर करा",
        testShareHint: "Family Repository ने तुमच्या कुटुंबाला मदत केली? तुमची कथा शेअर करा.",
        noOtpTitle: "Family Repository तुमचा OTP कधीच विचारणार नाही.",
        noOtpBody: "जर कोणी OTP मागत असेल तर लगेच कॉल कट करा. आम्ही कधीही OTP मागत नाही.",
        navLogin: "लॉगिन",
        navStartFree: "मोफत सुरू करा",
        navDashboard: "डॅशबोर्ड",
        tnHome: "मुखपृष्ठ",
        tnFamilyTree: "कुटुंब वंशवृक्ष",
        tnFamilyHub: "कुटुंब केंद्र",
        tnEvents: "कार्यक्रम",
        tnPackages: "पॅकेजेस",
        tnVault: "तिजोरी",
        tnReports: "अहवाल",
        tnMore: "आणखी",
        tnRecords: "नोंदी",
        tnSupport: "मदत",
        sbDashboard: "डॅशबोर्ड",
        sbMemberProfile: "सदस्य प्रोफाइल",
        sbgConnections: "नाते-संबंध",
        sbConnectFriends: "विस्तारित कुटुंबांशी जोडले जावा",
        sbConnectRelatives: "जवळच्या कुटुंबांशी जोडले जावा",
        sbWorldMap: "जागतिक नकाशा",
        sbgMemories: "आठवणी",
        sbPhotoMemories: "फोटो आठवणी",
        sbAudioMemories: "ऑडिओ आठवणी",
        sbVideoMemories: "व्हिडिओ आठवणी",
        sbgEvents: "कार्यक्रम व स्मरणपत्रे",
        sbFamilyCalendar: "कौटुंबिक दिनदर्शिका",
        sbTodaysCelebration: "आजचा विशेष दिवस",
        sbFamilyTimeline: "व्यक्तीचे जीवनप्रवास",
        sbSmartReminder: "स्मार्ट स्मरणपत्र",
        sbgHealth: "आरोग्य व वैद्यकीय नोंदी",
        sbMedicalHistory: "वैद्यकीय इतिहास",
        sbChronicDiseases: "जुनाट / अनुवंशिक आजार",
        sbCancerRegistry: "कर्करोग नोंदणी",
        sbGeneticCongenital: "अनुवंशिक व जन्मजात",
        sbAllergies: "अॅलर्जी व असहिष्णुता",
        sbVaccination: "लसीकरण नोंदी",
        sbLifestyle: "जीवनशैली डॅशबोर्ड",
        sbPregnancyBirth: "गर्भधारणा व जन्माचा इतिहास",
        sbOrganDonation: "अवयवदान पसंती",
        sbBloodGroup: "रक्तगट नोंदणी",
        sbgVault: "वैयक्तिक तिजोरी",
        sbDocumentVault: "कागदपत्रांची तिजोरी",
        sbLegalVault: "कायदेशीर तिजोरी",
        sbgDisplay: "प्रदर्शन व डाउनलोड",
        sbDownloadExport: "डाउनलोड आणि एक्सपोर्ट",
        sbDigitalFamilyTree: "डिजिटल कौटुंबिक वंशावळीचे झाड",
        sbPackagesPlans: "पॅकेजेस आणि योजना",
        sbPrivacySettings: "प्रायव्हसी आणि सेटिंग्ज",
        hdrDashboard: "डॅशबोर्ड",
        hdrMenu: "मेन्यू",
        hdrMyProfile: "माझी प्रोफाइल",
        hdrSettings: "सेटिंग्ज",
        hdrSignOut: "साइन आउट",
        signOutTitle: "Family Repository मधून साइन आउट करायचे?",
        signOutBody: "आपण केव्हाही आपल्या मोबाईल OTP ने पुन्हा लॉगिन करू शकता.",
        roleAdministrator: "प्रशासक",
        roleMemberPlain: "सदस्य",
        searchPlaceholder: "काहीही शोधा…",
        searchNoResults: "यासाठी काहीही सापडले नाही",
        grpMembers: "सदस्य",
        grpMemories: "आठवणी",
        grpDocuments: "कागदपत्रे",
        grpStories: "कथा",
        grpEvents: "कार्यक्रम",
        msgTitle: "संदेश आणि स्मरणपत्रे",
        msgMarkAll: "सर्व वाचले म्हणून चिन्हांकित करा",
        msgEmpty: "अजून कोणतेही संदेश नाहीत — स्मरणपत्रे आणि कुटुंबातील अद्यतने येथे दिसतील.",
        mbHome: "मुख्यपृष्ठ",
        mbTree: "वृक्ष",
        mbAdd: "जोडा",
        mbHub: "हब",
        mbMe: "मी",
        dbMemberSince: "सदस्यत्व सुरू",
        dbGrowingTogether: "सोबत वाढत आहोत",
        dbGrowingSub: "प्रत्येक कुटुंबाची एक कहाणी असते.",
        dbProfileCompleteness: "प्रोफाइल पूर्णता",
        dbMemoryOfDay: "आजची आठवण",
        dbMemoryQuote: "आजचे कुटुंब — उद्याची प्रेरणा.",
        dbViewMemory: "आठवण पहा",
        dbGoPremium: "प्रीमियम घ्या",
        dbPremiumDesc: "अधिक सुविधा अनलॉक करा आणि तुमची कुटुंब माहिती सुरक्षित ठेवा.",
        dbUpgradeNow: "आता अपग्रेड करा",
        plMembers: "सदस्य",
        plGenerations: "पिढ्या",
        plYearsHistory: "इतक्या वर्षांचा इतिहास",
        plFamilyAccounts: "कुटुंबातील खाती",
        plTotalRecords: "एकूण नोंदी",
        plPhotosVideos: "फोटो आणि व्हिडिओ",
        plDocuments: "दस्तऐवज",
        plUpcomingEvents: "येणाऱ्या कार्यक्रमांची माहिती",
        plAnalytics: "विश्लेषण",
        qaTitle: "जलद कृती",
        qaSubtitle: "नेहमी वापरल्या जाणाऱ्या सुविधा इथे शॉर्टकटने",
        qaAddFamilyMember: "कुटुंबातील सदस्य जोडा",
        qaViewFamilyTree: "कुटुंब वृक्ष पाहा",
        qaAddMemory: "आठवण जोडा",
        qaScheduleEvent: "कार्यक्रमाची नोंद करा",
        qaInviteFamily: "कुटुंबीयांना आमंत्रित करा",
        chFamilyGrowth: "कुटुंब वाढ",
        chRecords: "नोंदी",
        chActivity: "कार्यकाळ",
        chAllTime: "संपूर्ण कालावधी",
        chWeek: "आठवडा",
        chTotal: "एकूण",
        chActivityEmpty: "आपले कुटुंब आठवणी जोडेल तसे येथे कृती दिसेल",
        recDocuments: "कागदपत्रे",
        recPhotosVideos: "फोटो आणि व्हिडिओ",
        recHealthRecords: "आरोग्य नोंदी",
        recLegalRecords: "कायदेशीर नोंदी",
        recStories: "कथा",
        dbTodaysCelebrations: "आजचे सण-उत्सव",
        dbViewAll: "सर्व पहा",
        dbNoCelebToday: "आज कोणाचेही वाढदिवस किंवा लग्नाच्या वाढदिवस नाहीत.",
        dbTurnsToday: "आज {n} वर्षांचे झाले!",
        dbRemembranceDay: "स्मरणदिन",
        dbViewCalendar: "कॅलेंडर पहा",
        dbNoUpcoming60: "पुढील ६० दिवसांत कोणतेही कार्यक्रम नाहीत.",
        evBirthdayOf: "{name} यांचा वाढदिवस",
        evRemembranceOf: "{name} — स्मरण",
        evToday: "आज",
        evInDays: "{n} दिवसांत",
        evTurning: "{n} वर्षांचे होत आहेत",
        dbRecentActivity: "अलीकडील कृती",
        dbNoActivity: "अजून कोणतीही कृती नाही — सुरुवात करण्यासाठी सदस्य किंवा आठवणी जोडा.",
        actSomeone: "कोणीतरी",
        actPersonAdded: "नवीन सदस्य जोडला",
        actPersonUpdated: "सदस्याची माहिती अद्ययावत केली",
        actPersonDeleted: "सदस्य काढून टाकला",
        actMemoryUploaded: "छायाचित्र अपलोड केले",
        actMemoryDeleted: "छायाचित्र हटवले",
        actDocUploaded: "कागदपत्र अपलोड केले",
        actDocViewed: "कागदपत्र पाहिले",
        actStoryRecorded: "एक गोष्ट रेकॉर्ड केली",
        actStoryPlayed: "एक गोष्ट ऐकली",
        actCalendarCreate: "कार्यक्रम तयार केला",
        actCalendarUpdate: "कार्यक्रम अद्ययावत केला",
        actCapsuleCreated: "एक मेमरी कॅप्सूल तयार केली",
        actAchievementAdded: "एक कामगिरी जोडली",
        actDefault: "एक बदल केला",
        taJustNow: "आत्ताच",
        taMinAgo: "{n} मिनिटांपूर्वी",
        taHourAgo: "{n} तासांपूर्वी",
        taDayAgo: "{n} दिवसांपूर्वी",
        gsTitle: "सुरुवात करूया",
        gsProgress: "{done}/{total} पूर्ण",
        gsAddMember: "कुटुंबातील सदस्य जोडा",
        gsUploadMemory: "आठवण अपलोड करा",
        gsRecordStory: "कथा रेकॉर्ड करा",
        gsInviteFamily: "कुटुंबाला आमंत्रित करा",
        gsSecureDoc: "दस्तावेज सुरक्षित करा",
        gsCelebrateTitle: "सुरुवातीची पावले पूर्ण!",
        gsCelebrateBody: "सर्व ५ टप्पे पूर्ण — तुमच्या कुटुंबाची डिजिटल परंपरा अधिकृतरीत्या सुरू झाली आहे. तुमचे कौटुंबिक वृक्ष वाढवत रहा!",
        actProfileUpdated: "आपला प्रोफाइल अद्ययावत केला",
        daySun: "रवि",
        dayMon: "सोम",
        dayTue: "मंगळ",
        dayWed: "बुध",
        dayThu: "गुरु",
        dayFri: "शुक्र",
        daySat: "शनि",
        hbTitle: "कुटुंब — सगळं एकाच ठिकाणी",
        hbCategories: "वर्ग",
        hbSearchAll: "सर्व सुविधा शोधा…",
        hbNoMatch: "कोणतीही सुविधा जुळत नाही",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "लवकरच",
        hbNotifyMe: "मला कळवा",
        hbNotified: "तयार झाल्यावर कळवले जाईल",
        hbNotifyTitle: "“{title}” सुरू झाल्यावर आम्ही तुम्हाला कळवू",
        hbNotifyBody: "आपली आवड नोंदवली आहे. आमचा कार्यसंघ ज्या सुविधांसाठी जास्तीत जास्त कुटुंबे विनंती करतात त्यांना आधी प्राधान्य देतो.",
        hbGreat: "छान",
        rcSearch: "शोधा…",
        rcExportCsv: "CSV निर्यात करा",
        rcAddNew: "नवीन जोडा",
        rcCreate: "तयार करा",
        rcEdit: "संपादा",
        rcSave: "जतन करा",
        rcCancel: "रद्द करा",
        rcLoading: "लोड होत आहे…",
        rcNoYet: "अजून {label} नाही",
        rcClick: "क्लिक करा",
        rcFirstEntry: "तुमची पहिली नोंद जोडण्यासाठी.",
        rcNew: "नवीन",
        rcFormHint: "खालील माहिती भरा — आवश्यक जागा लाल * असलेल्या चिन्हाने दाखवलेल्या आहेत.",
        rcHealthBold: "फक्त कुटुंबीय नोंदींसाठी",
        rcHealthRest: "— निदान, उपचार किंवा वैद्यकीय सल्ल्यासाठी नाही. आरोग्याच्या नोंदी कुटुंबप्रमुख कुटुंबाच्या संमतीने जोडू किंवा बदलू शकतात.",
        rcConfirmTitle: "कृपया खात्री करा",
        rcConfirmDelete: "ही नोंद काढून टाकायची?",
        rcUpdated: "अपडेट केले.",
        rcSaved: "जतन केले.",
        rcCreated: "तयार केले.",
        rcDeleted: "काढून टाकले.",
        rcChoose: "निवडा…",
        rcUnknownSection: "अज्ञात विभाग",
        rcSectionMissing: "हा कुटुंबीय नोंदींचा विभाग अस्तित्वात नाही.",
        rcBackToHub: "कुटुंब हबकडे परत जा",
        rcWatchDemo: "डेमो पाहा",
        rcDemoSoon: "डेमो लवकरच येत आहे",
        rcFamilyService: "कुटुंब सेवा",
        rcUntitled: "(शीर्षक नाही)",
        setTitle: "सेटिंग्ज आणि गोपनीयता",
        setBack: "मागे",
        setTabInvites: "निमंत्रणे",
        setTabSecurity: "सुरक्षा",
        setTabData: "माझी माहिती",
        setTabActivity: "क्रियाकलाप",
        setTabHelp: "मदत",
        setMyAccount: "माझे खाते",
        setMyAccountSub: "पावत्या, स्वागत ईमेल आणि मदतीच्या उत्तरांसाठी आपला ईमेल जोडा. आपला फोन क्रमांक हेच आपले लॉगिन आहे आणि ते येथे बदलता येणार नाही.",
        setName: "नाव",
        setEmail: "ईमेल",
        setEmailInvalid: "कृपया वैध ईमेल पत्ता टाका.",
        setSaveChanges: "बदल जतन करा",
        setSaving: "जतन करत आहे…",
        setSaved: "खात्याची माहिती जतन झाली.",
        setFamilyMembers: "कुटुंबातील सदस्य",
        setRolesHint: "Admin: संपूर्ण नियंत्रण · Editor: सदस्य जोडा/संपादा · Viewer: फक्त वाचन",
        setYou: "(आपण)",
        setRoleUpdated: "भूमिका अद्यतनित केली.",
        setActiveInvites: "सक्रिय आमंत्रणे",
        setInvitesSub: "वृक्षात सदस्याच्या प्रोफाइल ड्रॉवरमधून आमंत्रणे पाठवा. केव्हाही रद्द करू शकता.",
        setNoActiveInvites: "सध्या कोणतीही सक्रिय आमंत्रणे नाहीत.",
        setRevoke: "रद्द करा",
        setExpires: "समाप्त होईल",
        setOnlyAdmins: "फक्त कुटुंब प्रशासकच आमंत्रणे व्यवस्थापित करू शकतात.",
        setInviteRevoked: "आमंत्रण रद्द केले.",
        setSeniorSub: "गोपनीयतेच्या निवडी ३ मोठ्या पर्यायांमध्ये सोप्या करते: खाजगी / कुटुंब / सार्वजनिक.",
        setWelcomeTour: "स्वागत मार्गदर्शन",
        setWelcomeTourSub: "३ टप्प्यांची ओळख पुन्हा पाहा: वंशवृक्ष → आठवणी → तिजोरी.",
        setReplayTour: "मार्गदर्शन पुन्हा पाहा",
        setLivingIn: "सध्या वास्तव्य",
        setLivingInSub: "ऐच्छिक — NRI कुटुंबांना आम्ही अधिक चांगली सेवा देण्यासाठी मदत करते.",
        setIndia: "भारत",
        setAbroad: "परदेश (NRI)",
        setSelect: "निवडा",
        setWhatWeStore: "आपल्याबद्दल आम्ही काय जतन करतो",
        setDpdpNote: "DPDP कायदा: पूर्ण पारदर्शकता, नेहमीच.",
        setProfileSections: "प्रोफाइल विभाग",
        setVaultDocs: "तिजोरीतील कागदपत्रे",
        setInvitesSent: "आमंत्रणं पाठवली",
        setActivityEntries: "क्रियाकलाप नोंदी",
        setDownloadData: "माझी सर्व माहिती डाउनलोड करा (JSON)",
        setDeleteTitle: "माझं खाते आणि माहिती हटवा",
        setDeleteSub: "तुमचं खाते, तुमच्या गोष्टी, वॉल्टमधील कागदपत्रं आणि क्रियाकलाप नोंद कायमचं काढून टाकली जाईल. तुम्ही इतरांच्याबद्दल तयार केलेल्या वंशावळीतील नोंदी कुटुंबाकडेच राहतील.",
        setDeleteEverything: "सगळं हटवा",
        setDeleteConfirmTitle: "तुमचं खाते कायमचं हटवायचं आहे का?",
        setDeleteConfirmBody: "हे मागे घेता येणार नाही. DPDP कायद्यानुसार तुमच्या गोष्टी, कागदपत्रं आणि खाते कायमचं मिटवलं जाईल.",
        setDeleting: "हटवत आहोत…",
        setYesDelete: "हो, सगळं हटवा",
        setActivityLog: "क्रियाकलाप नोंद",
        setAuditAdminSub: "तुमच्या कुटुंबात कोणी काय पाहिलं आणि काय बदललं.",
        setAuditOwnSub: "तुमचे स्वतःचे क्रियाकलाप.",
        setNoActivityYet: "अजून कोणताही क्रियाकलाप नाही.",
        setSearchFamily: "कुटुंबात शोधा…",
        ptsEyebrow: "आमच्या डिझाईन स्टुडिओकडून",
        ptsTitle: "सुंदर कौटुंबिक पोस्टर नमुने",
        ptsSub: "आमच्या स्टुडिओतून तयार केलेली खास पोस्टर डिझाईन्स — तुमचे कौटुंबिक फोटो त्यात छान बसतात. साइन अप केल्यानंतर तुमचा आवडता नमुना निवडा.",
        ptsCta: "तुमचे कौटुंबिक पोस्टर तयार करा",
        ptsPremium: "प्रीमियम",
        hcTitle: "मदत केंद्र",
        hcSub: "काहीही विचारा — आम्ही साधारण काही तासांत उत्तर देतो.",
        hcAskTitle: "प्रश्न विचारा",
        hcAskSub: "तुमची समस्या थोडक्यात लिहा, आमची सहाय्यक टीम तुम्हाला परत कळवेल.",
        hcSubject: "विषय",
        hcSubjectPh: "उदा. मी फोटो अपलोड करू शकत नाही",
        hcMessage: "तुमचा प्रश्न",
        hcMessagePh: "काय घडलं ते टप्प्याटप्प्याने लिहा…",
        hcSubmit: "प्रश्न पाठवा",
        hcSubmitting: "पाठवत आहोत…",
        hcSuccess: "तुमचा प्रश्न पाठवला गेला आहे — आम्ही लवकरच उत्तर देऊ.",
        hcFailed: "तुमचा प्रश्न पाठवता आला नाही. कृपया पुन्हा प्रयत्न करा.",
        hcFillBoth: "कृपया विषय आणि तुमचा प्रश्न दोन्ही भरा.",
        hcMyTickets: "माझे प्रश्न",
        hcNoTickets: "अजून कोणतेही प्रश्न नाहीत — वर तुमचा पहिला प्रश्न विचारा.",
        hcStatusOpen: "उघडे",
        hcStatusResolved: "निकाली काढलेले",
        hcSupportReply: "सपोर्ट",
        hcQuickHelp: "झटपट मदत",
        hcWhatsapp: "WhatsApp सपोर्ट — तत्काळ चॅट",
        hcCall: "कॉल: +91-99999-99999 (सोम–शनि १०–७)",
        hcFaq: "FAQ — नेहमी विचारले जाणारे प्रश्न",
        hcTipTitle: "सूचना",
        hcTipBody: "बहुतांश उत्तरं आधीच FAQ मध्ये आहेत. तुमच्या कुटुंबाच्या माहितीशी संबंधित वैयक्तिक काहीही असेल, तर इथे विचारा — हे फक्त आमच्या सपोर्ट टीमलाच दिसेल.",
        demoAddPhoto: "फोटो जोडा (ऐच्छिक)",
        demoChangePhoto: "फोटो बदला",
        demoDownloadPoster: "पोस्टर डाउनलोड करा",
        demoDownloading: "तयार करत आहोत…",
    },
    ta: {
        tourListen: "கேளுங்கள்",
        tourStop: "நிறுத்து",
        tourEyebrow: "வம்ச மரத்திற்கு வரவேற்கிறோம்",
        tourStepOf: "படி {n} / {total}",
        tourNext: "அடுத்து",
        tourStart: "தொடங்குங்கள்",
        tourS1Title: "உங்கள் குடும்ப மரத்தை உருவாக்குங்கள்",
        tourS1Body: "பெற்றோர், தாத்தா-பாட்டி மற்றும் குழந்தைகளைச் சேர்த்து தொடங்குங்கள். நீங்கள் சேர்க்கும் ஒவ்வொருவரும் உங்கள் குடும்பக் கதையின் உயிருள்ள கிளையாவார்.",
        tourS1Cta: "குடும்ப மரத்தைத் திற",
        tourS2Title: "நினைவுகளைப் பாதுகாக்குங்கள்",
        tourS2Body: "புகைப்படங்களை பதிவேற்றி, குரல்களை பதிவுசெய்து, கதைகளை எழுதுங்கள். எதிர்கால தலைமுறைகள் மீண்டும் அனுபவிக்க அவற்றை ஆல்பங்களாக ஒழுங்கமைக்கவும்.",
        tourS2Cta: "நினைவுகளைத் திற",
        tourS3Title: "பெட்டகத்தைப் பாதுகாக்கவும்",
        tourS3Body: "உயில்கள், சொத்து ஆவணங்கள் மற்றும் சட்ட ஆவணங்களை வங்கி-தர பாதுகாப்புடன் வைத்திருங்கள் — உரிய நேரத்தில் அடுத்த தலைமுறைக்கு வழங்குங்கள்.",
        tourS3Cta: "பெட்டகத்தைத் திற",
        back: "பின்செல்",
        cancel: "ரத்து",
        delete: "நீக்கு",
        logout: "வெளியேறு",
        optional: "(விருப்பத்திற்கு)",
        tagline: "உங்கள் குடும்பம். என்றென்றும்.",
        familyLegacy: "குடும்ப பாரம்பரியம்",
        signInTitle: "Family Repository-இல் உள்நுழைக",
        otpTitle: "உங்கள் OTP-ஐ உள்ளிடவும்",
        phoneSub: "உங்கள் மொபைலுக்கு OTP அனுப்புவோம்.",
        otpSentTo: "OTP அனுப்பப்பட்டது",
        expiresIn: "காலாவதி ஆகும்",
        mobileNumber: "மொபைல் எண்",
        phoneHint: "இந்திய எண்கள் மட்டும் (6–9 இல் தொடங்க வேண்டும்).",
        yourName: "உங்கள் பெயர்",
        firstTimeOnly: "(முதல் முறை மட்டும்)",
        haveInviteCode: "குடும்ப அழைப்புக் குறியீடு உள்ளதா?",
        apply: "பயன்படுத்து",
        sendOtp: "OTP அனுப்பு",
        sendingOtp: "OTP அனுப்பப்படுகிறது…",
        otpLabel: "6 இலக்க OTP",
        verifyContinue: "சரிபார்த்து தொடரவும்",
        verifying: "சரிபார்க்கிறது…",
        changeNumber: "எண்ணை மாற்று",
        resendOtp: "OTP மீண்டும் அனுப்பு",
        resendIn: "மீண்டும் அனுப்ப",
        terms: "தொடர்வதன் மூலம் Family Repository-இன் சேவை விதிமுறைகள் மற்றும் தனியுரிமைக் கொள்கைக்கு ஒப்புக்கொள்கிறீர்கள்.",
        trustStrip: "என்க்ரிப்டட் • குடும்பக் கட்டுப்பாடு • இந்தியாவில் உருவாக்கப்பட்டது",
        welcome: "வரவேற்கிறோம்",
        dashSub: "உங்கள் Family Repository தயார். முதல் உறுப்பினரைச் சேர்க்கவும் அல்லது பெரியவர்களின் கதையை பதிவு செய்யவும்.",
        quickActions: "விரைவு செயல்கள்",
        footerNote: "உங்கள் தரவு தனிப்பட்டது, என்க்ரிப்ட் செய்யப்பட்டது, குடும்பக் கட்டுப்பாட்டில் உள்ளது. இந்தியாவுக்காக — எப்போதும்.",
        roleFamilyHead: "குடும்பத் தலைவர்",
        roleAdmin: "நிர்வாகி",
        roleEditor: "திருத்துநர்",
        roleViewer: "பார்வையாளர்",
        roleMember: "உறுப்பினர்",
        cardTreeTitle: "குடும்ப மரத்தைக் காண்க",
        cardTreeDesc: "தலைமுறைகள் முழுவதும் உங்கள் குடும்பத்தைக் காணுங்கள்.",
        cardAddTitle: "உறுப்பினரைச் சேர்",
        cardAddDesc: "பெற்றோர், உடன்பிறந்தோர், குழந்தை அல்லது உறவினரைச் சேர்க்கவும்.",
        cardMemoriesTitle: "நினைவுகள்",
        cardMemoriesDesc: "குடும்ப தருணங்களின் புகைப்படங்களை பதிவேற்றவும்.",
        cardStoryTitle: "கதையை பதிவு செய்",
        cardStoryDesc: "பெரியவர்களின் குரலை என்றென்றும் சேமிக்கவும்.",
        cardVaultTitle: "ஆவணப் பெட்டகம்",
        cardVaultDesc: "உயில்கள், ஆவணங்கள் — பாதுகாப்பாக, உங்கள் விதிமுறைகளில்.",
        cardSettingsTitle: "தனியுரிமை & அமைப்புகள்",
        cardSettingsDesc: "பங்குகள், அழைப்புகள், உங்கள் தரவு மற்றும் செயல்பாட்டு பதிவு.",
        memories: "நினைவுகள்",
        addMemory: "நினைவைச் சேர்",
        memoriesEmpty: "இன்னும் நினைவுகள் இல்லை",
        memoriesEmptySub: "முதல் குடும்பப் புகைப்படத்தை பதிவேற்றவும் — திருமணங்கள், பண்டிகைகள், அன்றாட மகிழ்ச்சி.",
        caption: "தலைப்பு",
        captionPlaceholder: "உதா. பாட்டியின் வீட்டில் தீபாவளி, 1998",
        memoryDate: "இது எப்போது?",
        tagMember: "குடும்ப உறுப்பினரை குறிக்கவும்",
        choosePhoto: "புகைப்படத்தைத் தேர்வு செய்",
        changePhoto: "புகைப்படத்தை மாற்று",
        uploadMemory: "நினைவைச் சேமி",
        uploading: "பதிவேற்றுகிறது…",
        deleteMemoryConfirm: "இந்த நினைவை நீக்கவா? இதை மீட்டெடுக்க முடியாது.",
        confirmDelete: "உறுதிப்படுத்த மீண்டும் தட்டவும்",
        uploadedBy: "சேர்த்தவர்",
        heroEyebrow: "உங்கள் குடும்பம் · உங்கள் பாரம்பரியம்",
        heroHeadline1: "உங்கள் குடும்பத்தின் வரலாறு",
        heroHeadlineAccent: "சேமிக்கப்பட்டு",
        heroHeadline2: "எப்போதும்.",
        heroSubline: "உங்கள் குடும்ப மரத்தை உருவாக்கி, மூத்தோரின் குரல் பதிவுகளைச் சேமித்து, புகைப்படங்களையும் ஆவணங்களையும் பாதுகாத்து வையுங்கள் — இந்திய குடும்பங்களுக்காக உருவாக்கப்பட்ட, உங்கள் சொந்த மொழியிலான, ஒரு தனியார் இடத்தில் அனைத்தையும் ஒரே இடத்தில் வைத்துக்கொள்ளலாம்.",
        heroCtaPrimary: "மொபைல் OTP மூலம் இலவசமாக தொடங்குங்கள்",
        heroCtaSecondary: "இது எப்படி செயல்படுகிறது என்பதைப் பாருங்கள்",
        heroTrustPrivate: "இயல்பாகவே தனியார்",
        heroTrustLangs: "10 இந்திய மொழிகள்",
        heroTrustFree: "இலவச தொடக்கம்",
        heroSocialProof: "12,500+ இந்தியக் குடும்பங்கள் நம்பிக்கை வைக்கும் தளம்",
        heroBadge10: "10+",
        heroBadge10Cap: "இந்திய மொழிகள்",
        heroStoryAudio: "ஒலி கதைகள்",
        heroStoryPhotos: "புகைப்படங்கள்",
        heroStoryDocs: "ஆவணங்கள்",
        statHappyFamilies: "மகிழ்ச்சியான குடும்பங்கள்",
        statTreesCreated: "சேமிக்கப்பட்ட குடும்ப உறுப்பினர்கள்",
        statStoriesRecorded: "கதைகள் & நினைவுகள்",
        statDataPrivacy: "தரவு தனியுரிமை",
        demoBadge: "பதிவு செய்யாமல் முயற்சி செய்யவும்",
        demoHeadline: "உங்கள் குடும்ப மரம் 30 வினாடிகளில் உயிர்ப்புடன் எப்படி தோன்றுகிறது என்று பாருங்கள்",
        demoSubline: "6 பேரின் பெயர்கள் சேர்க்கவும். உங்கள் 6 தலைமுறை குடும்ப மரம் இங்கே உயிர்ப்புடன் அசையும்.",
        demoTrustNoPhone: "தொலைபேசி எண் தேவையில்லை",
        demoTrustNoOtp: "OTP தேவையில்லை",
        demoTrustNoSignup: "பதிவு தேவையில்லை",
        demoStepPrefix: "படி",
        demoStepOf: "6 இல் —",
        demoLabelFather: "தந்தையின் பெயர்",
        demoPhFather: "எ.கா. Rajesh Sharma",
        demoLabelGrandfather: "தாத்தாவின் பெயர்",
        demoPhGrandfather: "எ.கா. Mohan Sharma",
        demoLabelGreatGrand: "பெரிய தாத்தாவின் பெயர்",
        demoPhGreatGrand: "எ.கா. Kishan Sharma",
        demoLabelChild: "மகன் / மகளின் பெயர்",
        demoPhChild: "எ.கா. Ananya Sharma",
        demoLabelGrandchild: "பெற்றோரின் பேரக் குழந்தையின் பெயர்",
        demoPhGrandchild: "எ.கா. Vihaan Sharma",
        roleFather: "தந்தை",
        roleGrandfather: "தாத்தா",
        roleGreatGrandfather: "முதுமுதுப் பாட்டன்",
        roleChild: "மகன்/மகள்",
        roleGrandchild: "பெற்றப்பேறு (பேரன்/பேத்து)",
        demoLabelYou: "உங்கள் பெயர்",
        demoLabelParent: "உங்கள் பெற்றோரின் பெயர்",
        demoLabelGrand: "உங்கள் தாத்தா / பாட்டியின் பெயர்",
        demoPhYou: "எ.கா. ராகுல் சர்மா",
        demoPhParent: "எ.கா. சுரேஷ் சர்மா",
        demoPhGrand: "எ.கா. ராமச்சந்திர சர்மா",
        demoHint: "உங்கள் மரம் உயிர்ப்புடன் தோன்ற, இன்னும் {n} பெயர்களைச் சேர்க்கவும்.",
        demoNext: "அடுத்தது",
        demoSeeTree: "உங்கள் மரத்தைக் காண",
        demoRestart: "மீண்டும் முயற்சிக்க",
        demoStartReal: "என் உண்மையான மரத்தை தொடங்க (இலவசம்)",
        demoTreeBadge: "உங்கள் சிறிய குடும்ப மரம்",
        demoChooseDesign: "போஸ்டர் வடிவமைப்பைத் தேர்ந்தெடுக்கவும்",
        demoClassic: "கிளாசிக்",
        demoFamilyTitle: "{name} குடும்பம்",
        demoShareWhatsApp: "WhatsApp-இல் பகிரவும்",
        demoShareText: "நான் 30 வினாடிகளில் எங்கள் குடும்ப மரத்தை உருவாக்கினேன்! 🌳 நீங்களும் இலவசமாக உருவாக்குங்கள்: {link}",
        demoScanToJoin: "ஸ்கேன் செய்து இலவசமாக உருவாக்குங்கள்",
        demoTreeHeadline: "ஆறு தலைமுறைகள், ஒரு உயிருள்ள குடும்ப மரம்.",
        demoTreeAddMore: "இன்னும் 20+ உறவினர்கள், புகைப்படங்கள், நினைவுகளை — இலவசமாக சேர்க்கவும்.",
        demoFooterPrivacy: "உங்கள் டெமோ விளைவுகள் உங்களுடைய உலாவியில் மட்டுமே இருக்கும். எதுவும் சேமிக்கப்படாது, அனுப்பப்படாது.",
        roleGrandparent: "தாத்தா / பாட்டி",
        roleParent: "பெற்றோர்",
        roleYou: "நீங்கள்",
        testHeadline: "இந்தியாவின் பல குடும்பங்கள் எங்களை நம்புகிறார்கள்",
        testSubline: "உண்மையான குடும்பங்கள், உண்மையான வாழ்க்கை கதைகள் – எப்பொழுதும் பாதுகாக்கப்பட்டவை.",
        testShareBtn: "உங்கள் குடும்பக் கதையை பகிரவும்",
        testShareHint: "Family Repository உங்கள் குடும்பத்திற்கு உதவியிருக்கிறதா? உங்கள் கதையைப் பகிருங்கள்.",
        noOtpTitle: "Family Repository உங்கள் OTP-ஐ ஒருபோதும் கேட்காது.",
        noOtpBody: "யாராவது OTP கேட்டு உங்களுக்கு போன் செய்யவோ மெசேஜ் அனுப்பவோ செய்தால் – உடனே தொடர்பை துண்டிக்கவும். நாங்கள் அதை ஒருபோதும் கேட்கமாட்டோம்.",
        navLogin: "உள்நுழைய",
        navStartFree: "இலவசமாக தொடங்கவும்",
        navDashboard: "டாஷ்போர்டு",
        tnHome: "முகப்பு",
        tnFamilyTree: "குடும்ப மரம்",
        tnFamilyHub: "குடும்ப மையம்",
        tnEvents: "நிகழ்வுகள்",
        tnPackages: "பொதிகள்",
        tnVault: "வால்ட்",
        tnReports: "அறிக்கைகள்",
        tnMore: "மேலும்",
        tnRecords: "பதிவுகள்",
        tnSupport: "உதவி",
        sbDashboard: "டாஷ்போர்டு",
        sbMemberProfile: "உறுப்பினர் விவரம்",
        sbgConnections: "இணைப்புகள்",
        sbConnectFriends: "விரிந்த குடும்பத்தினருடன் இணைவோம்",
        sbConnectRelatives: "அருகிய குடும்பத்தினருடன் இணைவோம்",
        sbWorldMap: "உலக வரைபடம்",
        sbgMemories: "நினைவுகள்",
        sbPhotoMemories: "புகைப்பட நினைவுகள்",
        sbAudioMemories: "ஒலி நினைவுகள்",
        sbVideoMemories: "காணொளி நினைவுகள்",
        sbgEvents: "நிகழ்வுகள் & நினைவூட்டல்கள்",
        sbFamilyCalendar: "குடும்ப நாட்காட்டி",
        sbTodaysCelebration: "இன்றைய கொண்டாட்டம்",
        sbFamilyTimeline: "தனிப்பட்ட வாழ்க்கைப் பயணம்",
        sbSmartReminder: "ஸ்மார்ட் நினைவூட்டல்",
        sbgHealth: "ஆரோக்கியம் & மருத்துவத் தொகுப்பு",
        sbMedicalHistory: "மருத்துவ வரலாறு",
        sbChronicDiseases: "நீடித்த / மரபுரீதியான நோய்கள்",
        sbCancerRegistry: "புற்றுநோய் பதிவேடு",
        sbGeneticCongenital: "மரபணு & பிறவிக்குறி",
        sbAllergies: "அலர்ஜி & சகிப்புத்தன்மை குறைபாடுகள்",
        sbVaccination: "தடுப்பூசி பதிவுகள்",
        sbLifestyle: "வாழ்க்கை முறை பலகை",
        sbPregnancyBirth: "கர்ப்ப காலமும் பிறப்பு வரலாறும்",
        sbOrganDonation: "உடல் உறுப்பு தான விருப்பம்",
        sbBloodGroup: "இரத்த வகை பதிவேடு",
        sbgVault: "தனிப்பட்ட பாதுகாப்பு சேமிப்பு",
        sbDocumentVault: "ஆவண பாதுகாப்பு சேமிப்பு",
        sbLegalVault: "சட்ட ஆவண பாதுகாப்பு சேமிப்பு",
        sbgDisplay: "காண்பிக்கும் & பதிவிறக்கம்",
        sbDownloadExport: "பதிவிறக்கம் & ஏற்றுமதி",
        sbDigitalFamilyTree: "மின்னணு வம்சக்குடி மரம்",
        sbPackagesPlans: "பேக்கேஜ்கள் & திட்டங்கள்",
        sbPrivacySettings: "தனியுரிமை & அமைப்புகள்",
        hdrDashboard: "பலகை",
        hdrMenu: "பட்டி",
        hdrMyProfile: "என் சுயவிவரம்",
        hdrSettings: "அமைப்புகள்",
        hdrSignOut: "வெளியேறு",
        signOutTitle: "Family Repository இலிருந்து வெளியேற வேண்டுமா?",
        signOutBody: "நீங்கள் உங்கள் மொபைல் OTP மூலம் எப்போது வேண்டுமானாலும் மீண்டும் உள்நுழையலாம்.",
        roleAdministrator: "நிர்வாகி",
        roleMemberPlain: "உறுப்பினர்",
        searchPlaceholder: "எதை வேண்டுமானாலும் தேடுங்கள்…",
        searchNoResults: "இதற்கான முடிவுகள் எதுவும் இல்லை",
        grpMembers: "உறுப்பினர்கள்",
        grpMemories: "நினைவுகள்",
        grpDocuments: "ஆவணங்கள்",
        grpStories: "கதைகள்",
        grpEvents: "நிகழ்வுகள்",
        msgTitle: "செய்திகள் & நினைவூட்டல்கள்",
        msgMarkAll: "அனைத்தையும் படித்ததாக குறிக்கவும்",
        msgEmpty: "இன்னும் எந்தச் செய்திகள் இல்லை — நினைவூட்டல்கள் மற்றும் குடும்ப தகவல்கள் இங்கு தோன்றும்.",
        mbHome: "முகப்பு",
        mbTree: "குடும்ப மரம்",
        mbAdd: "சேர்க்க",
        mbHub: "ஹப்",
        mbMe: "நான்",
        dbMemberSince: "உறுப்பினராக இருந்து",
        dbGrowingTogether: "ஒன்றாக வளர்வோம்",
        dbGrowingSub: "ஒவ்வொரு குடும்பத்திற்கும் ஒரு கதையுண்டு.",
        dbProfileCompleteness: "சுயவிவரத்தின் முழுமைத்தன்மை",
        dbMemoryOfDay: "இன்றைய நினைவு",
        dbMemoryQuote: "இன்றைய குடும்பம் — நாளைய உத்வேகம்.",
        dbViewMemory: "நினைவைப் பார்வையிட",
        dbGoPremium: "பிரீமியமாக மாற்றவும்",
        dbPremiumDesc: "உயர்ந்த அம்சங்களைத் திறந்து, உங்கள் குடும்பத் தரவை பாதுகாப்பாக வைத்திருங்கள்.",
        dbUpgradeNow: "இப்போது அப்க்ரேடு செய்யவும்",
        plMembers: "உறுப்பினர்கள்",
        plGenerations: "தலைமுறைகள்",
        plYearsHistory: "ஆண்டுகளின் வரலாறு",
        plFamilyAccounts: "குடும்பக் கணக்குகள்",
        plTotalRecords: "மொத்த பதிவுகள்",
        plPhotosVideos: "புகைப்படங்கள் & வீடியோக்கள்",
        plDocuments: "ஆவணங்கள்",
        plUpcomingEvents: "வரவிருக்கும் நிகழ்வுகள்",
        plAnalytics: "பகுப்பாய்வு",
        qaTitle: "விரைவுப் பணிகள்",
        qaSubtitle: "அடிக்கடி பயன்படுத்தப்படும் அம்சங்களுக்கான குறுக்கு வழிகள்",
        qaAddFamilyMember: "குடும்ப உறுப்பினரைச் சேர்க்கவும்",
        qaViewFamilyTree: "குடும்ப வம்சவிருட்சத்தைப் பார்க்க",
        qaAddMemory: "ஒரு நினைவைச் சேர்க்க",
        qaScheduleEvent: "நிகழ்வை திட்டமிட",
        qaInviteFamily: "குடும்பத்தாரை அழைக்க",
        chFamilyGrowth: "குடும்ப வளர்ச்சி",
        chRecords: "பதிவுகள்",
        chActivity: "செயல்பாடு",
        chAllTime: "எல்லா காலமும்",
        chWeek: "வாரம்",
        chTotal: "மொத்தம்",
        chActivityEmpty: "உங்கள் குடும்பம் நினைவுகளைச் சேர்க்கும் போது இங்கு செயல்பாடுகள் தோன்றும்",
        recDocuments: "ஆவணங்கள்",
        recPhotosVideos: "புகைப்படங்கள் & காணொளிகள்",
        recHealthRecords: "மருத்துவ பதிவுகள்",
        recLegalRecords: "சட்ட பதிவுகள்",
        recStories: "கதைகள்",
        dbTodaysCelebrations: "இன்றைய கொண்டாட்டங்கள்",
        dbViewAll: "அனைத்தையும் பார்க்க",
        dbNoCelebToday: "இன்று பிறந்தநாள்களோ ஆண்டு நிறைவுகளோ இல்லை.",
        dbTurnsToday: "இன்று {n} வயது ஆகிறார்!",
        dbRemembranceDay: "நினைவுநாள்",
        dbViewCalendar: "நாள்காட்டியைப் பார்க்க",
        dbNoUpcoming60: "அடுத்த 60 நாட்களில் வரும் நிகழ்வுகள் எதுவும் இல்லை.",
        evBirthdayOf: "{name} அவர்களின் பிறந்தநாள்",
        evRemembranceOf: "{name} — நினைவு நாள்",
        evToday: "இன்று",
        evInDays: "இன்னும் {n} நாட்களில்",
        evTurning: "{n} ஆகிறார்",
        dbRecentActivity: "சமீப நிகழ்வுகள்",
        dbNoActivity: "இன்னும் எந்த செயல்பாடும் இல்லை — தொடங்க உறுப்பினர்களையோ நினைவுகளையோ சேர்க்கவும்.",
        actSomeone: "யாரோ ஒருவர்",
        actPersonAdded: "ஒரு புதிய உறுப்பினரை சேர்த்தார்",
        actPersonUpdated: "ஒரு உறுப்பினர் விவரத்தை புதுப்பித்தார்",
        actPersonDeleted: "ஒரு உறுப்பினரை நீக்கினார்",
        actMemoryUploaded: "ஒரு புகைப்படத்தை பதிவேற்றினார்",
        actMemoryDeleted: "ஒரு புகைப்படத்தை நீக்கினார்",
        actDocUploaded: "ஒரு ஆவணத்தை பதிவேற்றினார்",
        actDocViewed: "ஒரு ஆவணத்தை பார்த்தார்",
        actStoryRecorded: "ஒரு கதையை பதிவு செய்தார்",
        actStoryPlayed: "ஒரு கதையை இயக்கினார்",
        actCalendarCreate: "ஒரு நிகழ்வை உருவாக்கினார்",
        actCalendarUpdate: "ஒரு நிகழ்வை புதுப்பித்தார்",
        actCapsuleCreated: "ஒரு நினைவு காப்பகத்தை உருவாக்கினார்",
        actAchievementAdded: "ஒரு சாதனையைச் சேர்த்தார்",
        actDefault: "ஒரு புதுப்பிப்பை செய்தார்",
        taJustNow: "இப்போதுதான்",
        taMinAgo: "{n} நிமி முன்",
        taHourAgo: "{n} மணி முன்",
        taDayAgo: "{n} நாள் முன்",
        gsTitle: "தொடங்குவது எப்படி",
        gsProgress: "{done}/{total} முடிந்தது",
        gsAddMember: "ஒரு குடும்ப உறுப்பினரைச் சேர்க்கவும்",
        gsUploadMemory: "ஒரு நினைவைக் பதிவேற்றவும்",
        gsRecordStory: "ஒரு கதையை பதிவு செய்யவும்",
        gsInviteFamily: "குடும்பத்தைக் கூப்பிடுங்கள்",
        gsSecureDoc: "ஒரு ஆவணத்தை பாதுகாப்பாக வையுங்கள்",
        gsCelebrateTitle: "தொடங்கும் படிகள் அனைத்தும் முடிந்துவிட்டது!",
        gsCelebrateBody: "அனைத்து 5 படிகளும் முடிந்துவிட்டன — உங்கள் குடும்பத்தின் டிஜிட்டல் பாரம்பரியம் அதிகாரப்பூர்வமாக தொடங்கியுள்ளது. உங்கள் குடும்ப மரத்தை தொடர்ந்து வளர்த்துக் கொள்ளுங்கள்!",
        actProfileUpdated: "தங்கள் சுயவிவரத்தை புதுப்பித்துள்ளார்",
        daySun: "ஞாயி",
        dayMon: "தி",
        dayTue: "செ",
        dayWed: "பு",
        dayThu: "வி",
        dayFri: "வெ",
        daySat: "ச",
        hbTitle: "குடும்பம் — எல்லாம் ஒரே இடத்தில்",
        hbCategories: "வகைகள்",
        hbSearchAll: "அனைத்து அம்சங்களிலும் தேடவும்…",
        hbNoMatch: "எந்த அம்சமும் பொருந்தவில்லை",
        hbLive: "நேரடி",
        hbBeta: "BETA",
        hbSoon: "விரைவில்",
        hbNotifyMe: "எனக்கு தெரிவிக்கவும்",
        hbNotified: "தயார் ஆனவுடன் தெரிவிக்கப்படும்",
        hbNotifyTitle: "\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\" செயல்பாட்டில் வந்தவுடன் உங்களுக்கு தெரிவிப்போம்",
        hbNotifyBody: "உங்கள் ஆர்வம் பதிவு செய்யப்பட்டது. அதிக குடும்பங்கள் கேட்கும் அம்சங்களுக்கு எங்கள் குழு முதலில் முன்னுரிமை அளிக்கும்.",
        hbGreat: "சரி",
        rcSearch: "தேடவும்…",
        rcExportCsv: "CSV ஏற்றுமதி",
        rcAddNew: "புதியதைச் சேர்க்கவும்",
        rcCreate: "உருவாக்கவும்",
        rcEdit: "திருத்தவும்",
        rcSave: "சேமிக்கவும்",
        rcCancel: "ரத்து செய்யவும்",
        rcLoading: "ஏற்றப்படுகிறது…",
        rcNoYet: "{label} இன்னும் இல்லை",
        rcClick: "அழுத்தவும்",
        rcFirstEntry: "உங்கள் முதல் பதிவைச் சேர்க்க.",
        rcNew: "புதியது",
        rcFormHint: "கீழே உள்ள புலங்களை நிரப்பவும் — அவசியமான புலங்கள் சிவப்பு நட்சத்திரத்தால் குறிக்கப்பட்டுள்ளன.",
        rcHealthBold: "குடும்பப் பதிவுகளுக்காக மட்டும்",
        rcHealthRest: "— நோய்நிலை கண்டறிதல், சிகிச்சை அல்லது மருத்துவ ஆலோசனைக்காக அல்ல. குடும்ப உறுப்பினர்களின் சம்மதத்துடன் குடும்பத் தலைவர் மட்டுமே உடல்நலப் பதிவுகளைச் சேர்க்கவும் திருத்தவும் முடியும்.",
        rcConfirmTitle: "தயவு செய்து உறுதிப்படுத்தவும்",
        rcConfirmDelete: "இந்த பதிவை நீக்கவா?",
        rcUpdated: "புதுப்பிக்கப்பட்டது.",
        rcSaved: "சேமிக்கப்பட்டது.",
        rcCreated: "உருவாக்கப்பட்டது.",
        rcDeleted: "நீக்கப்பட்டது.",
        rcChoose: "தேர்ந்தெடுக்கவும்…",
        rcUnknownSection: "அறியப்படாத பகுதி",
        rcSectionMissing: "இந்த குடும்பப் பதிவுகள் பகுதி இல்லை.",
        rcBackToHub: "Family Hub-க்கு திரும்ப",
        rcWatchDemo: "டெமோ பார்க்க",
        rcDemoSoon: "டெமோ விரைவில் வருகிறது",
        rcFamilyService: "குடும்ப சேவை",
        rcUntitled: "(தலைப்பிடப்படாதது)",
        setTitle: "அமைப்புகள் & தனியுரிமை",
        setBack: "பின்செல்ல",
        setTabInvites: "அழைப்புகள்",
        setTabSecurity: "பாதுகாப்பு",
        setTabData: "எனது தரவு",
        setTabActivity: "செயல்பாடு",
        setTabHelp: "உதவி",
        setMyAccount: "என் கணக்கு",
        setMyAccountSub: "ரசீதுகள், வரவேற்பு மின்னஞ்சல் மற்றும் உதவி பதில்களைப் பெற உங்கள் மின்னஞ்சலைச் சேர்க்கவும். உங்களுடைய உள்நுழைவு எண் உங்கள் தொலைபேசி எண் தான், அதை இங்கே மாற்ற முடியாது.",
        setName: "பெயர்",
        setEmail: "மின்னஞ்சல்",
        setEmailInvalid: "சரியான மின்னஞ்சல் முகவரியை உள்ளிடுங்கள்.",
        setSaveChanges: "மாற்றங்களைச் சேமிக்க",
        setSaving: "சேமித்து வருகிறது…",
        setSaved: "கணக்கு விவரங்கள் சேமிக்கப்பட்டன.",
        setFamilyMembers: "குடும்ப உறுப்பினர்கள்",
        setRolesHint: "நிர்வாகி: முழு கட்டுப்பாடு · தொகுப்பாளர்: உறுப்பினர்களைச் சேர்க்க/தொகுக்க · பார்வையாளர்: பார்க்க மட்டும்",
        setYou: "(நீங்கள்)",
        setRoleUpdated: "பங்கு மாற்றப்பட்டது.",
        setActiveInvites: "செயலில் உள்ள அழைப்புகள்",
        setInvitesSub: "குடும்ப மரத்தில் ஒரு உறுப்பினரின் சுயவிவர பக்கத்தில் இருந்து அழைப்புகளை அனுப்பலாம். எப்போதும் ரத்து செய்யலாம்.",
        setNoActiveInvites: "செயலில் உள்ள அழைப்புகள் இல்லை.",
        setRevoke: "ரத்து செய்",
        setExpires: "காலாவதி ஆகும்",
        setOnlyAdmins: "குடும்ப நிர்வாகிகள் மட்டுமே அழைப்புகளை நிர்வகிக்கலாம்.",
        setInviteRevoked: "அழைப்பு ரத்து செய்யப்பட்டது.",
        setSeniorSub: "தனியுரிமை விருப்பங்களை 3 பெரிய தேர்வுகளாக எளிமைப்படுத்துகிறது: தனிப்பட்டது / குடும்பம் / பொதுமக்கள்.",
        setWelcomeTour: "வரவேற்பு வழிகாட்டல்",
        setWelcomeTourSub: "3-அடுக்கு அறிமுகத்தை மீண்டும் பாருங்கள்: குடும்ப மரம் → நினைவுகள் → பாதுகாப்பு பகுதிகள் (Vault).",
        setReplayTour: "வழிகாட்டலை மீண்டும் பார்க்க",
        setLivingIn: "வசிப்பது",
        setLivingInSub: "விருப்பத்தேர்வு — NRI குடும்பங்களுக்கு நாங்கள் சிறப்பாக சேவை செய்ய உதவும்.",
        setIndia: "இந்தியா",
        setAbroad: "வெளிநாடு (NRI)",
        setSelect: "தேர்வு செய்",
        setWhatWeStore: "உங்களைப் பற்றிச் சேமிப்பவை",
        setDpdpNote: "DPDP சட்டம்: முழுப்பட்ட வெளிப்படைத் தன்மை, எப்போதும்.",
        setProfileSections: "சுயவிவரப் பகுதிகள்",
        setVaultDocs: "Vault ஆவணங்கள்",
        setInvitesSent: "அழைப்புகள் அனுப்பப்பட்டன",
        setActivityEntries: "செயல்பாட்டு பதிவுகள்",
        setDownloadData: "எனது அனைத்து தரவையும் பதிவிறக்க (JSON)",
        setDeleteTitle: "என் கணக்கும் தரவுகளையும் நீக்கு",
        setDeleteSub: "உங்கள் கணக்கு, உங்கள் கதைகள், பாதுகாப்பு ஆவணங்கள் மற்றும் செயல்பாட்டு பதிவுகள் நிரந்தரமாக நீக்கப்படும். நீங்கள் மற்றவர்களைப் பற்றி உருவாக்கிய குடும்ப மர பதிவுகள் குடும்பத்துடனே இருக்கும்.",
        setDeleteEverything: "அனைத்தையும் நீக்கு",
        setDeleteConfirmTitle: "உங்கள் கணக்கை நிரந்தரமாக நீக்கவா?",
        setDeleteConfirmBody: "இதை மீண்டும் மாற்ற முடியாது. உங்கள் கதைகள், ஆவணங்கள் மற்றும் கணக்கு DPDP சட்டத்தின்படி முற்றிலும் நீக்கப்படும்.",
        setDeleting: "நீக்கப்படுகிறது…",
        setYesDelete: "ஆம், அனைத்தையும் நீக்கு",
        setActivityLog: "செயல்பாட்டு பதிவு",
        setAuditAdminSub: "உங்கள் குடும்பத்தில் யார் என்ன பார்த்தார்கள், என்ன மாற்றினர் என்பதற்கான பதிவு.",
        setAuditOwnSub: "உங்கள் சொந்த செயல்பாடு.",
        setNoActivityYet: "இன்னும் எந்த செயல்பாடும் இல்லை.",
        setSearchFamily: "குடும்பத்தில் தேடுக…",
        ptsEyebrow: "எங்கள் வடிவமைப்பு ஸ்டூடியோவில் இருந்து",
        ptsTitle: "அழகான குடும்ப போஸ்டர் டெம்ப்ளேட்கள்",
        ptsSub: "எங்கள் ஸ்டூடியோவில் அன்புடன் வடிவமைக்கப்பட்ட போஸ்டர்கள் — உங்கள் குடும்பப் புகைப்படங்கள் அதில் நன்றாகப் பொருந்தும். உள்நுழைந்த பிறகு உங்கள் மனப் பிடித்ததைத் தேர்ந்தெடுக்கலாம்.",
        ptsCta: "உங்கள் குடும்ப போஸ்டரை உருவாக்குங்கள்",
        ptsPremium: "பிரீமியம்",
        hcTitle: "உதவி மையம்",
        hcSub: "எங்களுக்கு எதை வேண்டுமானாலும் கேளுங்கள் — நாங்கள் பொதுவாக சில மணி நேரத்திற்குள் பதிலளிக்கிறோம்.",
        hcAskTitle: "ஒரு கேள்வியை கேளுங்கள்",
        hcAskSub: "உங்கள் சிக்கலை விவரமாகச் சொல்லுங்கள்; எங்கள் ஆதரவு குழு உங்களுக்கு பதில் தரும்.",
        hcSubject: "தலைப்பு",
        hcSubjectPh: "உதா. ஒரு படத்தை upload செய்ய முடியவில்லை",
        hcMessage: "உங்கள் கேள்வி",
        hcMessagePh: "என்ன நடந்தது என்பதை படிப்படியாகச் சொல்லுங்கள்…",
        hcSubmit: "கேள்வியை அனுப்புங்கள்",
        hcSubmitting: "அனுப்பிக் கொண்டிருக்கிறது…",
        hcSuccess: "உங்கள் கேள்வி அனுப்பப்பட்டுள்ளது — விரைவில் பதிலளிக்கிறோம்.",
        hcFailed: "உங்கள் கேள்வியை அனுப்ப முடியவில்லை. தயவு செய்து மீண்டும் முயற்சிக்கவும்.",
        hcFillBoth: "தயவு செய்து தலைப்பையும் உங்கள் கேள்வியையும் நிரப்பவும்.",
        hcMyTickets: "என் கேள்விகள்",
        hcNoTickets: "இன்னும் கேள்விகள் இல்லை — மேலே உங்கள் முதல் கேள்வியை கேளுங்கள்.",
        hcStatusOpen: "திறந்துள்ளது",
        hcStatusResolved: "தீர்க்கப்பட்டது",
        hcSupportReply: "ஆதரவு",
        hcQuickHelp: "விரைவு உதவி",
        hcWhatsapp: "WhatsApp ஆதரவு — உடனடி அரட்டை",
        hcCall: "அழைப்பு: +91-99999-99999 (திங்கள்–சனி 10–7)",
        hcFaq: "FAQ — அடிக்கடி கேட்கப்படும் கேள்விகள்",
        hcTipTitle: "உதவி குறிப்பு",
        hcTipBody: "பெரும்பாலான பதில்கள் ஏற்கனவே FAQ-வில் உள்ளன. உங்கள் குடும்பத் தகவலுக்கான ஏதாவது தனிப்பட்ட விஷயம் இருந்தால், இங்கே கேளுங்கள் — அதை எங்கள் ஆதரவு குழுவே மட்டுமே பார்க்கும்.",
        demoAddPhoto: "புகைப்படம் சேர்க்கவும் (விருப்பம்)",
        demoChangePhoto: "புகைப்படத்தை மாற்றவும்",
        demoDownloadPoster: "போஸ்டரை பதிவிறக்கவும்",
        demoDownloading: "தயார் செய்து கொண்டிருக்கிறது…",
    },
    bn: {
        tourListen: "শুনুন",
        tourStop: "থামুন",
        tourEyebrow: "ভংশট্রি-তে স্বাগতম",
        tourStepOf: "ধাপ {n} / {total}",
        tourNext: "পরবর্তী",
        tourStart: "শুরু করুন",
        tourS1Title: "আপনার বংশবৃক্ষ তৈরি করুন",
        tourS1Body: "বাবা-মা, দাদা-দাদি ও সন্তানদের যোগ করে শুরু করুন। প্রতিটি সদস্য আপনার পরিবারের গল্পের একটি জীবন্ত শাখা হয়ে ওঠে।",
        tourS1Cta: "বংশবৃক্ষ খুলুন",
        tourS2Title: "স্মৃতি সংরক্ষণ করুন",
        tourS2Body: "ছবি আপলোড করুন, কণ্ঠস্বর রেকর্ড করুন ও গল্প লিখুন। অ্যালবামে সাজিয়ে রাখুন যাতে ভবিষ্যৎ প্রজন্ম তা আবার অনুভব করতে পারে।",
        tourS2Cta: "স্মৃতি খুলুন",
        tourS3Title: "ভল্ট সুরক্ষিত করুন",
        tourS3Body: "উইল, সম্পত্তির কাগজপত্র ও আইনি নথি ব্যাংক-মানের নিরাপত্তায় রাখুন — এবং সঠিক সময়ে পরবর্তী প্রজন্মের হাতে তুলে দিন।",
        tourS3Cta: "ভল্ট খুলুন",
        back: "পিছনে",
        cancel: "বাতিল",
        delete: "মুছুন",
        logout: "লগআউট",
        optional: "(ঐচ্ছিক)",
        tagline: "আপনার পরিবার। চিরকাল।",
        familyLegacy: "পারিবারিক ঐতিহ্য",
        signInTitle: "Family Repository-তে সাইন ইন করুন",
        otpTitle: "আপনার OTP দিন",
        phoneSub: "আমরা আপনার মোবাইলে একটি OTP পাঠাব।",
        otpSentTo: "OTP পাঠানো হয়েছে",
        expiresIn: "মেয়াদ শেষ হবে",
        mobileNumber: "মোবাইল নম্বর",
        phoneHint: "শুধুমাত্র ভারতীয় নম্বর (6–9 দিয়ে শুরু)।",
        yourName: "আপনার নাম",
        firstTimeOnly: "(শুধু প্রথমবার)",
        haveInviteCode: "পরিবারের ইনভাইট কোড আছে?",
        apply: "প্রয়োগ করুন",
        sendOtp: "OTP পাঠান",
        sendingOtp: "OTP পাঠানো হচ্ছে…",
        otpLabel: "৬ সংখ্যার OTP",
        verifyContinue: "যাচাই করে এগিয়ে যান",
        verifying: "যাচাই হচ্ছে…",
        changeNumber: "নম্বর বদলান",
        resendOtp: "আবার OTP পাঠান",
        resendIn: "আবার পাঠান",
        terms: "চালিয়ে গেলে আপনি Family Repository-র পরিষেবার শর্তাবলী ও গোপনীয়তা নীতিতে সম্মত হন।",
        trustStrip: "এনক্রিপ্টেড • পরিবার-নিয়ন্ত্রিত • ভারতে তৈরি",
        welcome: "স্বাগতম",
        dashSub: "আপনার Family Repository প্রস্তুত। প্রথম সদস্য যোগ করুন বা প্রবীণদের গল্প রেকর্ড করুন।",
        quickActions: "দ্রুত কাজ",
        footerNote: "আপনার ডেটা ব্যক্তিগত, এনক্রিপ্টেড এবং পরিবার-নিয়ন্ত্রিত। ভারতের জন্য তৈরি — সবসময়।",
        roleFamilyHead: "পরিবার প্রধান",
        roleAdmin: "অ্যাডমিন",
        roleEditor: "সম্পাদক",
        roleViewer: "দর্শক",
        roleMember: "সদস্য",
        cardTreeTitle: "বংশবৃক্ষ দেখুন",
        cardTreeDesc: "প্রজন্ম জুড়ে আপনার পুরো পরিবার দেখুন।",
        cardAddTitle: "সদস্য যোগ করুন",
        cardAddDesc: "বাবা-মা, ভাইবোন, সন্তান বা আত্মীয় যোগ করুন।",
        cardMemoriesTitle: "স্মৃতি",
        cardMemoriesDesc: "পারিবারিক মুহূর্তের ছবি আপলোড করুন।",
        cardStoryTitle: "গল্প রেকর্ড করুন",
        cardStoryDesc: "প্রবীণদের কণ্ঠ চিরকালের জন্য সংরক্ষণ করুন।",
        cardVaultTitle: "নথি ভল্ট",
        cardVaultDesc: "উইল, দলিল — সুরক্ষিত, আপনার শর্তে শেয়ার।",
        cardSettingsTitle: "গোপনীয়তা ও সেটিংস",
        cardSettingsDesc: "ভূমিকা, আমন্ত্রণ, আপনার ডেটা ও কার্যকলাপ লগ।",
        memories: "স্মৃতি",
        addMemory: "স্মৃতি যোগ করুন",
        memoriesEmpty: "এখনও কোনো স্মৃতি নেই",
        memoriesEmptySub: "প্রথম পারিবারিক ছবি আপলোড করুন — বিয়ে, উৎসব, প্রতিদিনের আনন্দ।",
        caption: "ক্যাপশন",
        captionPlaceholder: "যেমন — দিদিমার বাড়িতে দীপাবলি, ১৯৯৮",
        memoryDate: "এটা কবেকার?",
        tagMember: "পরিবারের সদস্যকে ট্যাগ করুন",
        choosePhoto: "ছবি বেছে নিন",
        changePhoto: "ছবি বদলান",
        uploadMemory: "স্মৃতি সংরক্ষণ করুন",
        uploading: "আপলোড হচ্ছে…",
        deleteMemoryConfirm: "এই স্মৃতি মুছবেন? এটি ফেরানো যাবে না।",
        confirmDelete: "নিশ্চিত করতে আবার ট্যাপ করুন",
        uploadedBy: "যোগ করেছেন",
        heroEyebrow: "আপনার পরিবার · আপনার ঐতিহ্য",
        heroHeadline1: "আপনার পরিবারের ইতিহাস",
        heroHeadlineAccent: "সংরক্ষিত",
        heroHeadline2: "চিরদিনের জন্য।",
        heroSubline: "আপনার পরিবারের বংশতালিকা বানান, বড়দের কণ্ঠ রেকর্ড করুন, ছবি আর নথি সংরক্ষণ করুন — সব একটিই ব্যক্তিগত জায়গায়, ভারতীয় পরিবারদের জন্য, আপনার নিজের ভাষায়।",
        heroCtaPrimary: "মোবাইল OTP দিয়ে ফ্রি শুরু করুন",
        heroCtaSecondary: "কীভাবে কাজ করে দেখুন",
        heroTrustPrivate: "মূলত ব্যক্তিগত",
        heroTrustLangs: "১০টি ভারতীয় ভাষা",
        heroTrustFree: "শুরুতে ফ্রি",
        heroSocialProof: "১২,৫০০+ ভারতীয় পরিবারের বিশ্বাসের জায়গা",
        heroBadge10: "১০+",
        heroBadge10Cap: "ভারতীয় ভাষা",
        heroStoryAudio: "অডিও গল্প",
        heroStoryPhotos: "ছবি",
        heroStoryDocs: "নথি",
        statHappyFamilies: "খুশি পরিবার",
        statTreesCreated: "সংরক্ষিত আত্মীয়স্বজন",
        statStoriesRecorded: "গল্প ও স্মৃতি",
        statDataPrivacy: "ডেটা গোপনীয়তা",
        demoBadge: "সাইনআপ ছাড়াই ব্যবহার করুন",
        demoHeadline: "৩০ সেকেন্ডেই আপনার বংশবৃক্ষকে জীবন্ত হয়ে উঠতে দেখুন",
        demoSubline: "মোট ৬টা নাম লিখুন। দেখুন এখানেই আপনার ৬-পুরুষের বংশবৃক্ষ নড়েচড়ে উঠছে।",
        demoTrustNoPhone: "ফোন লাগবে না",
        demoTrustNoOtp: "OTP লাগবে না",
        demoTrustNoSignup: "সাইনআপ লাগবে না",
        demoStepPrefix: "ধাপ",
        demoStepOf: "এর মধ্যে ৬ —",
        demoLabelFather: "বাবার নাম",
        demoPhFather: "যেমন, রাজেশ শর্মা",
        demoLabelGrandfather: "দাদার নাম",
        demoPhGrandfather: "যেমন, মোহন শর্মা",
        demoLabelGreatGrand: "প্রদাদার নাম",
        demoPhGreatGrand: "যেমন, কৃষ্ণ শর্মা",
        demoLabelChild: "ছেলে / মেয়ের নাম",
        demoPhChild: "যেমন, অনন্যা শর্মা",
        demoLabelGrandchild: "নাতি / নাতনির নাম",
        demoPhGrandchild: "যেমন, বিহান শর্মা",
        roleFather: "বাবা",
        roleGrandfather: "দাদা",
        roleGreatGrandfather: "পরদাদা / পরদিদা",
        roleChild: "ছেলে / মেয়ে",
        roleGrandchild: "নাতি / নাতনি",
        demoLabelYou: "আপনার নাম",
        demoLabelParent: "আপনার বাবার / মায়ের নাম",
        demoLabelGrand: "আপনার দাদা-দিদা / নানু-নানির নাম",
        demoPhYou: "যেমন, রাহুল শর্মা",
        demoPhParent: "যেমন, সুরেশ শর্মা",
        demoPhGrand: "যেমন, রামচন্দ্র শর্মা",
        demoHint: "আপনার গাছটাকে জীবন্ত দেখতে আরও {n}টি নাম যোগ করুন।",
        demoNext: "পরবর্তী",
        demoSeeTree: "আপনার গাছ দেখুন",
        demoRestart: "আবার চেষ্টা করুন",
        demoStartReal: "আমার আসল গাছ শুরু করি (ফ্রি)",
        demoTreeBadge: "আপনার ছোট গাছ",
        demoChooseDesign: "পোস্টার ডিজাইন বেছে নিন",
        demoClassic: "ক্লাসিক",
        demoFamilyTitle: "{name} পরিবার",
        demoShareWhatsApp: "WhatsApp-এ শেয়ার করুন",
        demoShareText: "আমি ৩০ সেকেন্ডে আমাদের পারিবারিক গাছ বানিয়েছি! 🌳 আপনিও বিনামূল্যে বানান: {link}",
        demoScanToJoin: "স্ক্যান করে বিনামূল্যে বানান",
        demoTreeHeadline: "ছয় প্রজন্ম, এক জীবন্ত গাছ।",
        demoTreeAddMore: "২০+ জন আত্মীয়, ছবি ও স্মৃতি আরও যোগ করুন — একদম ফ্রি।",
        demoFooterPrivacy: "আপনার ডেমোর ফলাফল শুধু আপনার ব্রাউজারেই থাকে। কিছুই সংরক্ষণ বা পাঠানো হয় না।",
        roleGrandparent: "দাদা-দিদা / নানু-নানি",
        roleParent: "বাবা / মা",
        roleYou: "আপনি",
        testHeadline: "সারা ভারতের পরিবারগুলো আমাদের ভরসা করে",
        testSubline: "বাস্তব পরিবার, বাস্তব জীবনের গল্প – এবার চিরদিনের জন্য সংরক্ষিত।",
        testShareBtn: "আপনার পারিবারিক গল্প শেয়ার করুন",
        testShareHint: "Family Repository কি আপনার পরিবারকে সাহায্য করেছে? আপনার গল্প শেয়ার করুন।",
        noOtpTitle: "Family Repository কখনও আপনার OTP চায় না।",
        noOtpBody: "কেউ যদি ফোন বা মেসেজ করে OTP চায় — সঙ্গে সঙ্গে সংযোগ কেটে দিন। আমরা কখনও OTP চাইব না।",
        navLogin: "লগইন",
        navStartFree: "ফ্রি ব্যবহার শুরু করুন",
        navDashboard: "ড্যাশবোর্ড",
        tnHome: "হোম",
        tnFamilyTree: "ফ্যামিলি ট্রি",
        tnFamilyHub: "ফ্যামিলি হাব",
        tnEvents: "ইভেন্টস",
        tnPackages: "প্যাকেজসমূহ",
        tnVault: "ভল্ট",
        tnReports: "রিপোর্টস",
        tnMore: "আরও",
        tnRecords: "রেকর্ডস",
        tnSupport: "সাপোর্ট",
        sbDashboard: "ড্যাশবোর্ড",
        sbMemberProfile: "সদস্যের প্রোফাইল",
        sbgConnections: "সংযোগসমূহ",
        sbConnectFriends: "সম্পর্কিত বিস্তৃত পরিবারের সঙ্গে যুক্ত হোন",
        sbConnectRelatives: "ঘনিষ্ঠ পরিবারের সঙ্গে যুক্ত হোন",
        sbWorldMap: "বিশ্ব মানচিত্র",
        sbgMemories: "স্মৃতিসমূহ",
        sbPhotoMemories: "ছবির স্মৃতিসমূহ",
        sbAudioMemories: "অডিও স্মৃতিসমূহ",
        sbVideoMemories: "ভিডিও স্মৃতিসমূহ",
        sbgEvents: "ইভেন্ট ও রিমাইন্ডার",
        sbFamilyCalendar: "পারিবারিক ক্যালেন্ডার",
        sbTodaysCelebration: "আজকের উদযাপন",
        sbFamilyTimeline: "ব্যক্তিগত জীবনযাত্রার পথচিত্র",
        sbSmartReminder: "স্মার্ট রিমাইন্ডার",
        sbgHealth: "স্বাস্থ্য ও চিকিৎসা সংগ্রহশালা",
        sbMedicalHistory: "চিকিৎসা ইতিহাস",
        sbChronicDiseases: "দীর্ঘস্থায়ী / বংশগত রোগ",
        sbCancerRegistry: "ক্যান্সার নিবন্ধন",
        sbGeneticCongenital: "জিনগত ও জন্মগত",
        sbAllergies: "অ্যালার্জি ও অসহিষ্ণুতা",
        sbVaccination: "টিকাদান নথি",
        sbLifestyle: "লাইফস্টাইল ড্যাশবোর্ড",
        sbPregnancyBirth: "গর্ভধারণ ও জন্মের ইতিহাস",
        sbOrganDonation: "অঙ্গ দানের পছন্দ",
        sbBloodGroup: "রক্তের গ্রুপ নিবন্ধন",
        sbgVault: "ব্যক্তিগত ভল্ট",
        sbDocumentVault: "ডকুমেন্ট ভল্ট",
        sbLegalVault: "আইনগত ভল্ট",
        sbgDisplay: "ডিসপ্লে ও ডাউনলোড",
        sbDownloadExport: "ডাউনলোড ও এক্সপোর্ট",
        sbDigitalFamilyTree: "ডিজিটাল বংশবৃক্ষ",
        sbPackagesPlans: "প্যাকেজ ও প্ল্যান",
        sbPrivacySettings: "গোপনীয়তা ও সেটিংস",
        hdrDashboard: "ড্যাশবোর্ড",
        hdrMenu: "মেনু",
        hdrMyProfile: "আমার প্রোফাইল",
        hdrSettings: "সেটিংস",
        hdrSignOut: "সাইন আউট",
        signOutTitle: "Family Repository থেকে সাইন আউট করবেন?",
        signOutBody: "আপনি যেকোনো সময় আপনার মোবাইল OTP দিয়ে আবার লগ ইন করতে পারবেন।",
        roleAdministrator: "প্রশাসক",
        roleMemberPlain: "সদস্য",
        searchPlaceholder: "যেকোনো কিছু খুঁজুন…",
        searchNoResults: "এর জন্য কোনো ফল পাওয়া যায়নি",
        grpMembers: "সদস্যরা",
        grpMemories: "স্মৃতি",
        grpDocuments: "নথি",
        grpStories: "গল্প",
        grpEvents: "ঘটনা",
        msgTitle: "বার্তা ও অনুস্মারক",
        msgMarkAll: "সবগুলো পঠিত হিসেবে চিহ্নিত করুন",
        msgEmpty: "এখনও কোনো বার্তা নেই — অনুস্মারক ও পরিবারের আপডেট এখানে দেখা যাবে।",
        mbHome: "হোম",
        mbTree: "বংশবৃক্ষ",
        mbAdd: "যোগ করুন",
        mbHub: "হাব",
        mbMe: "আমি",
        dbMemberSince: "সদস্য হওয়ার তারিখ",
        dbGrowingTogether: "একসাথে বেড়ে ওঠা",
        dbGrowingSub: "প্রতিটি পরিবারেরই একটি গল্প আছে।",
        dbProfileCompleteness: "প্রোফাইল কতটা সম্পূর্ণ",
        dbMemoryOfDay: "আজকের স্মৃতি",
        dbMemoryQuote: "আজকের পরিবার — আগামী দিনের প্রেরণা।",
        dbViewMemory: "স্মৃতি দেখুন",
        dbGoPremium: "প্রিমিয়াম নিন",
        dbPremiumDesc: "উন্নত ফিচার আনলক করুন ও আপনার পারিবারিক তথ্য সুরক্ষিত রাখুন।",
        dbUpgradeNow: "এখনই আপগ্রেড করুন",
        plMembers: "সদস্য",
        plGenerations: "প্রজন্ম",
        plYearsHistory: "ইতিহাসের বছর",
        plFamilyAccounts: "পরিবারের অ্যাকাউন্ট",
        plTotalRecords: "মোট রেকর্ড",
        plPhotosVideos: "ছবি ও ভিডিও",
        plDocuments: "ডকুমেন্ট",
        plUpcomingEvents: "আসন্ন অনুষ্ঠান",
        plAnalytics: "অ্যানালিটিক্স",
        qaTitle: "দ্রুত কাজ",
        qaSubtitle: "বারবার ব্যবহৃত ফিচারের শর্টকাট",
        qaAddFamilyMember: "পরিবারের নতুন সদস্য যোগ করুন",
        qaViewFamilyTree: "পরিবারের বংশবৃক্ষ দেখুন",
        qaAddMemory: "স্মৃতি যোগ করুন",
        qaScheduleEvent: "অনুষ্ঠান নির্ধারণ করুন",
        qaInviteFamily: "পরিবারকে আমন্ত্রণ করুন",
        chFamilyGrowth: "পরিবারের বৃদ্ধি",
        chRecords: "নথি",
        chActivity: "কর্মকাণ্ড",
        chAllTime: "সব সময়",
        chWeek: "সপ্তাহ",
        chTotal: "মোট",
        chActivityEmpty: "আপনার পরিবারের সদস্যরা স্মৃতি যোগ করলে এখানে কার্যকলাপ দেখা যাবে",
        recDocuments: "নথি",
        recPhotosVideos: "ছবি ও ভিডিও",
        recHealthRecords: "স্বাস্থ্য সম্পর্কিত নথি",
        recLegalRecords: "আইনি নথি",
        recStories: "কথা ও স্মৃতিচারণ",
        dbTodaysCelebrations: "আজকের উদ্‌যাপন",
        dbViewAll: "সব দেখুন",
        dbNoCelebToday: "আজ কারও জন্মদিন বা বিবাহবার্ষিকী নেই।",
        dbTurnsToday: "আজ {n} বছরে পা দিচ্ছেন!",
        dbRemembranceDay: "স্মরণ দিবস",
        dbViewCalendar: "ক্যালেন্ডার দেখুন",
        dbNoUpcoming60: "আগামী ৬০ দিনের মধ্যে কোনো অনুষ্ঠান নেই।",
        evBirthdayOf: "{name}-এর জন্মদিন",
        evRemembranceOf: "{name} — স্মরণ",
        evToday: "আজ",
        evInDays: "{n} দিনে",
        evTurning: "{n} বছরে পা দিচ্ছেন",
        dbRecentActivity: "সাম্প্রতিক কার্যকলাপ",
        dbNoActivity: "এখনও কোনো কার্যকলাপ নেই — শুরু করতে সদস্য বা স্মৃতি যোগ করুন।",
        actSomeone: "কেউ একজন",
        actPersonAdded: "একজন নতুন সদস্য যোগ করেছেন",
        actPersonUpdated: "একজন সদস্যের প্রোফাইল আপডেট করেছেন",
        actPersonDeleted: "একজন সদস্যকে মুছে দিয়েছেন",
        actMemoryUploaded: "একটি ছবি আপলোড করেছেন",
        actMemoryDeleted: "একটি ছবি মুছে দিয়েছেন",
        actDocUploaded: "একটি নথি আপলোড করেছেন",
        actDocViewed: "একটি নথি দেখেছেন",
        actStoryRecorded: "একটি গল্প রেকর্ড করেছেন",
        actStoryPlayed: "একটি গল্প চালু করেছেন",
        actCalendarCreate: "একটি অনুষ্ঠান তৈরি করেছেন",
        actCalendarUpdate: "একটি অনুষ্ঠানে পরিবর্তন করেছেন",
        actCapsuleCreated: "একটি স্মৃতি-ক্যাপসুল তৈরি করেছেন",
        actAchievementAdded: "একটি সাফল্য যোগ করেছেন",
        actDefault: "একটি আপডেট করেছেন",
        taJustNow: "এইমাত্র",
        taMinAgo: "{n} মিনিট আগে",
        taHourAgo: "{n} ঘন্টা আগে",
        taDayAgo: "{n} দিন আগে",
        gsTitle: "শুরু করি",
        gsProgress: "{done}/{total} সম্পূর্ণ",
        gsAddMember: "একজন পরিবারের সদস্য যোগ করুন",
        gsUploadMemory: "একটি স্মৃতি আপলোড করুন",
        gsRecordStory: "একটি গল্প রেকর্ড করুন",
        gsInviteFamily: "পরিবারকে আমন্ত্রণ করুন",
        gsSecureDoc: "একটি নথি নিরাপদ করুন",
        gsCelebrateTitle: "শুরু করা সম্পূর্ণ!",
        gsCelebrateBody: "সব ৫টি ধাপ শেষ — আপনার পরিবারের ডিজিটাল উত্তরাধিকার এখন আনুষ্ঠানিকভাবে শুরু হয়েছে। আপনার পারিবারিক গাছকে বাড়িয়ে চলুন!",
        actProfileUpdated: "তাঁদের প্রোফাইল আপডেট করেছেন",
        daySun: "রবি",
        dayMon: "সোম",
        dayTue: "মঙ্গল",
        dayWed: "বুধ",
        dayThu: "বৃহস্পতি",
        dayFri: "শুক্র",
        daySat: "শনি",
        hbTitle: "পরিবার — সবকিছু এক জায়গায়",
        hbCategories: "বিভাগসমূহ",
        hbSearchAll: "সব ফিচারে খুঁজুন…",
        hbNoMatch: "কোনও ফিচার মেলেনি",
        hbLive: "লাইভ",
        hbBeta: "বেটা",
        hbSoon: "শীঘ্রই",
        hbNotifyMe: "আমাকে জানাবেন",
        hbNotified: "প্রস্তুত হলে জানানো হবে",
        hbNotifyTitle: "“{title}” লাইভ হলে আমরা আপনাকে জানাব",
        hbNotifyBody: "আপনার আগ্রহ নথিভুক্ত হয়েছে। আমাদের টিম সেই ফিচারগুলো আগে আনে, যেগুলোর জন্য সবচেয়ে বেশি পরিবার অনুরোধ করে।",
        hbGreat: "চমৎকার",
        rcSearch: "খুঁজুন…",
        rcExportCsv: "CSV এক্সপোর্ট করুন",
        rcAddNew: "নতুন যোগ করুন",
        rcCreate: "তৈরি করুন",
        rcEdit: "সম্পাদনা করুন",
        rcSave: "সংরক্ষণ করুন",
        rcCancel: "বাতিল করুন",
        rcLoading: "লোড হচ্ছে…",
        rcNoYet: "এখনও কোনো {label} নেই",
        rcClick: "ক্লিক করুন",
        rcFirstEntry: "আপনার প্রথম এন্ট্রি যোগ করতে।",
        rcNew: "নতুন",
        rcFormHint: "নিচের ঘরগুলো পূরণ করুন — বাধ্যতামূলক ঘরগুলো লাল তারকা দিয়ে চিহ্নিত আছে।",
        rcHealthBold: "শুধু পারিবারিক রেকর্ড রাখার জন্য",
        rcHealthRest: "— রোগ নির্ণয়, চিকিৎসা বা চিকিৎসা-সংক্রান্ত পরামর্শের জন্য নয়। পরিবারের সম্মতিতে শুধুমাত্র পরিবারের প্রধান স্বাস্থ্য-সংক্রান্ত রেকর্ড যোগ বা সম্পাদনা করতে পারেন।",
        rcConfirmTitle: "অনুগ্রহ করে নিশ্চিত করুন",
        rcConfirmDelete: "এই রেকর্ডটি মুছে ফেলবেন?",
        rcUpdated: "আপডেট করা হয়েছে।",
        rcSaved: "সংরক্ষণ করা হয়েছে।",
        rcCreated: "তৈরি করা হয়েছে।",
        rcDeleted: "মুছে ফেলা হয়েছে।",
        rcChoose: "নির্বাচন করুন…",
        rcUnknownSection: "অজানা বিভাগ",
        rcSectionMissing: "এই পারিবারিক রেকর্ডস বিভাগটি নেই।",
        rcBackToHub: "ফ্যামিলি হাবে ফিরে যান",
        rcWatchDemo: "ডেমো দেখুন",
        rcDemoSoon: "ডেমো শীঘ্রই আসছে",
        rcFamilyService: "পারিবারিক পরিষেবা",
        rcUntitled: "(শিরোনামহীন)",
        setTitle: "সেটিংস ও গোপনীয়তা",
        setBack: "পেছনে যান",
        setTabInvites: "আমন্ত্রণ",
        setTabSecurity: "সুরক্ষা",
        setTabData: "আমার ডেটা",
        setTabActivity: "কার্যকলাপ",
        setTabHelp: "সাহায্য",
        setMyAccount: "আমার অ্যাকাউন্ট",
        setMyAccountSub: "রসিদ, স্বাগতম চিঠি এবং সহায়তার জবাব পেতে আপনার ইমেল যোগ করুন। আপনার ফোন নম্বরই আপনার লগইন এবং এটি এখানে পরিবর্তন করা যাবে না।",
        setName: "নাম",
        setEmail: "ইমেল",
        setEmailInvalid: "একটি বৈধ ইমেল ঠিকানা লিখুন।",
        setSaveChanges: "পরিবর্তনগুলো সংরক্ষণ করুন",
        setSaving: "সংরক্ষণ করা হচ্ছে…",
        setSaved: "অ্যাকাউন্টের তথ্য সংরক্ষণ করা হয়েছে।",
        setFamilyMembers: "পরিবারের সদস্যরা",
        setRolesHint: "অ্যাডমিন: সম্পূর্ণ নিয়ন্ত্রণ · এডিটর: সদস্য যোগ/সম্পাদনা · ভিউয়ার: শুধু দেখা",
        setYou: "(আপনি)",
        setRoleUpdated: "ভূমিকা আপডেট হয়েছে।",
        setActiveInvites: "সক্রিয় আমন্ত্রণ",
        setInvitesSub: "গাছের ভিউতে সদস্যের প্রোফাইল ড্রয়ার থেকে আমন্ত্রণ পাঠান। যে কোনো সময় বাতিল করতে পারবেন।",
        setNoActiveInvites: "কোনো সক্রিয় আমন্ত্রণ নেই।",
        setRevoke: "বাতিল করুন",
        setExpires: "মেয়াদ শেষ",
        setOnlyAdmins: "শুধু পরিবার অ্যাডমিনরাই আমন্ত্রণ পরিচালনা করতে পারেন।",
        setInviteRevoked: "আমন্ত্রণ বাতিল হয়েছে।",
        setSeniorSub: "গোপনীয়তার বিকল্পগুলোকে ৩টি বড় পছন্দে সহজ করে: প্রাইভেট / ফ্যামিলি / পাবলিক।",
        setWelcomeTour: "স্বাগত ভ্রমণ",
        setWelcomeTourSub: "৩ ধাপের পরিচিতি আবার দেখুন: Family Tree → Memories → Vault.",
        setReplayTour: "ভ্রমণ আবার দেখুন",
        setLivingIn: "বাস করছেন",
        setLivingInSub: "ঐচ্ছিক — NRI পরিবারগুলোকে আরও ভালোভাবে সাহায্য করতে কাজে লাগে।",
        setIndia: "ভারত",
        setAbroad: "বিদেশ (NRI)",
        setSelect: "নির্বাচন করুন",
        setWhatWeStore: "আমরা আপনার সম্পর্কে কী সংরক্ষণ করি",
        setDpdpNote: "DPDP Act: সবসময় পূর্ণ স্বচ্ছতা।",
        setProfileSections: "প্রোফাইল অংশসমূহ",
        setVaultDocs: "Vault নথি",
        setInvitesSent: "আমন্ত্রণ পাঠানো হয়েছে",
        setActivityEntries: "কর্মকাণ্ডের নথি",
        setDownloadData: "আমার সব ডেটা ডাউনলোড করুন (JSON)",
        setDeleteTitle: "আমার অ্যাকাউন্ট ও ডেটা মুছে দিন",
        setDeleteSub: "স্থায়ীভাবে আপনার অ্যাকাউন্ট, আপনার গল্প, ভল্টের নথি এবং কার্যকলাপের লগ মুছে দেবে। আপনি অন্যদের নিয়ে যে ফ্যামিলি ট্রি এন্ট্রি তৈরি করেছেন, তা পরিবারের সঙ্গে থাকবে।",
        setDeleteEverything: "সবকিছু মুছে দিন",
        setDeleteConfirmTitle: "আপনার অ্যাকাউন্ট স্থায়ীভাবে মুছে ফেলবেন?",
        setDeleteConfirmBody: "এটি আর ফেরানো যাবে না। আপনার গল্প, নথি এবং অ্যাকাউন্ট DPDP আইনের সঙ্গে সামঞ্জস্য রেখে মুছে দেওয়া হবে।",
        setDeleting: "মুছে ফেলা হচ্ছে…",
        setYesDelete: "হ্যাঁ, সবকিছু মুছে দিন",
        setActivityLog: "কর্মকাণ্ডের লগ",
        setAuditAdminSub: "আপনার পুরো পরিবারের মধ্যে কে কী দেখেছে এবং কী বদলেছে।",
        setAuditOwnSub: "আপনার নিজের কার্যকলাপ।",
        setNoActivityYet: "এখনও কোনো কার্যকলাপ নেই।",
        setSearchFamily: "পরিবারে খোঁজ করুন…",
        ptsEyebrow: "আমাদের ডিজাইন স্টুডিও থেকে",
        ptsTitle: "দারুণ সুন্দর ফ্যামিলি পোস্টার টেমপ্লেট",
        ptsSub: "আমাদের স্টুডিওর হাতের বানানো পোস্টার ডিজাইন — আপনার পরিবারিক ছবি গুলো একেবারে মানিয়ে যাবে। সাইন আপ করার পর আপনার পছন্দের ডিজাইনটি বেছে নিন।",
        ptsCta: "আপনার ফ্যামিলি পোস্টার তৈরি করুন",
        ptsPremium: "প্রিমিয়াম",
        hcTitle: "হেল্প সেন্টার",
        hcSub: "আমাদের যেকোনও কিছু জিজ্ঞেস করতে পারেন — আমরা সাধারণত কয়েক ঘন্টার মধ্যেই জবাব দিই।",
        hcAskTitle: "একটি প্রশ্ন করুন",
        hcAskSub: "আপনার সমস্যাটা লিখে জানান, আমাদের সাপোর্ট টিম আপনার সঙ্গে আবার যোগাযোগ করবে।",
        hcSubject: "বিষয়",
        hcSubjectPh: "যেমন: আমি ছবি আপলোড করতে পারছি না",
        hcMessage: "আপনার প্রশ্ন",
        hcMessagePh: "ধাপে ধাপে কী ঘটেছে লিখে জানান…",
        hcSubmit: "প্রশ্ন পাঠান",
        hcSubmitting: "পাঠানো হচ্ছে…",
        hcSuccess: "আপনার প্রশ্নটি পাঠানো হয়েছে — আমরা শীঘ্রই জবাব দেব।",
        hcFailed: "আপনার প্রশ্নটি পাঠানো যায়নি। আবার চেষ্টা করুন, অনুগ্রহ করে।",
        hcFillBoth: "অনুগ্রহ করে বিষয় ও আপনার প্রশ্ন — দুটিই লিখুন।",
        hcMyTickets: "আমার প্রশ্নগুলো",
        hcNoTickets: "এখনও কোনো প্রশ্ন করেননি — উপরে আপনার প্রথম প্রশ্নটি করুন।",
        hcStatusOpen: "খোলা",
        hcStatusResolved: "সমাধান হয়েছে",
        hcSupportReply: "সাপোর্ট",
        hcQuickHelp: "দ্রুত সাহায্য",
        hcWhatsapp: "WhatsApp সাপোর্ট — সঙ্গে সঙ্গে চ্যাট",
        hcCall: "কল: +91-99999-99999 (সোম–শনি ১০–৭)",
        hcFaq: "FAQ — প্রায়ই জিজ্ঞাসা করা প্রশ্ন",
        hcTipTitle: "পরামর্শ",
        hcTipBody: "বেশির ভাগ উত্তর ইতিমধ্যেই FAQ-তে আছে। আপনার পরিবারের ডেটা নিয়ে ব্যক্তিগত কোনো প্রশ্ন থাকলে এখানে জিজ্ঞাসা করুন — শুধু আমাদের সাপোর্ট‑টিমই এটি দেখতে পায়।",
        demoAddPhoto: "ছবি যোগ করুন (ঐচ্ছিক)",
        demoChangePhoto: "ছবি বদলান",
        demoDownloadPoster: "পোস্টার ডাউনলোড করুন",
        demoDownloading: "প্রস্তুত করা হচ্ছে…",
    },
    gu: {
        tourListen: "સાંભળો",
        tourStop: "રોકો",
        tourEyebrow: "વંશટ્રીમાં આપનું સ્વાગત છે",
        tourStepOf: "પગલું {n} / {total}",
        tourNext: "આગળ",
        tourStart: "શરૂ કરો",
        tourS1Title: "તમારું વંશવૃક્ષ બનાવો",
        tourS1Body: "માતા-પિતા, દાદા-દાદી અને બાળકોને ઉમેરીને શરૂઆત કરો. દરેક સભ્ય તમારા પરિવારની વાર્તાની જીવંત ડાળી બને છે.",
        tourS1Cta: "વંશવૃક્ષ ખોલો",
        tourS2Title: "યાદો સાચવો",
        tourS2Body: "ફોટા અપલોડ કરો, અવાજ રેકોર્ડ કરો અને વાર્તાઓ લખો. આલ્બમમાં ગોઠવો જેથી આવનારી પેઢીઓ તેને ફરી માણી શકે.",
        tourS2Cta: "યાદો ખોલો",
        tourS3Title: "વૉલ્ટ સુરક્ષિત કરો",
        tourS3Body: "વસિયત, મિલકતના કાગળો અને કાનૂની દસ્તાવેજો બેંક-કક્ષાની સુરક્ષામાં રાખો — અને યોગ્ય સમયે આગલી પેઢીને સોંપો.",
        tourS3Cta: "વૉલ્ટ ખોલો",
        back: "પાછળ",
        cancel: "રદ કરો",
        delete: "કાઢી નાખો",
        logout: "લૉગઆઉટ",
        optional: "(વૈકલ્પિક)",
        tagline: "તમારો પરિવાર. હંમેશ માટે.",
        familyLegacy: "કૌટુંબિક વારસો",
        signInTitle: "Family Repository માં સાઇન ઇન કરો",
        otpTitle: "તમારો OTP દાખલ કરો",
        phoneSub: "અમે તમારા મોબાઇલ પર OTP મોકલીશું.",
        otpSentTo: "OTP મોકલાયો",
        expiresIn: "સમાપ્ત થશે",
        mobileNumber: "મોબાઇલ નંબર",
        phoneHint: "ફક્ત ભારતીય નંબર (6–9 થી શરૂ).",
        yourName: "તમારું નામ",
        firstTimeOnly: "(ફક્ત પહેલી વાર)",
        haveInviteCode: "કુટુંબનો ઇન્વાઇટ કોડ છે?",
        apply: "લાગુ કરો",
        sendOtp: "OTP મોકલો",
        sendingOtp: "OTP મોકલાઈ રહ્યો છે…",
        otpLabel: "6 અંકનો OTP",
        verifyContinue: "ચકાસો અને આગળ વધો",
        verifying: "ચકાસી રહ્યું છે…",
        changeNumber: "નંબર બદલો",
        resendOtp: "OTP ફરી મોકલો",
        resendIn: "ફરી મોકલો",
        terms: "ચાલુ રાખીને તમે Family Repository ની સેવા શરતો અને ગોપનીયતા નીતિ સાથે સંમત થાઓ છો.",
        trustStrip: "એન્ક્રિપ્ટેડ • કુટુંબ-નિયંત્રિત • ભારતમાં બનેલું",
        welcome: "સ્વાગત છે",
        dashSub: "તમારું Family Repository તૈયાર છે. પહેલો સભ્ય ઉમેરો અથવા વડીલોની વાર્તા રેકોર્ડ કરો.",
        quickActions: "ઝડપી ક્રિયાઓ",
        footerNote: "તમારો ડેટા ખાનગી, એન્ક્રિપ્ટેડ અને કુટુંબ-નિયંત્રિત છે. ભારત માટે બનાવેલું — હંમેશા.",
        roleFamilyHead: "કુટુંબના વડા",
        roleAdmin: "એડમિન",
        roleEditor: "સંપાદક",
        roleViewer: "દર્શક",
        roleMember: "સભ્ય",
        cardTreeTitle: "વંશવૃક્ષ જુઓ",
        cardTreeDesc: "પેઢીઓમાં ફેલાયેલો તમારો આખો પરિવાર જુઓ.",
        cardAddTitle: "સભ્ય ઉમેરો",
        cardAddDesc: "માતા-પિતા, ભાઈ-બહેન, બાળક કે સગાં ઉમેરો.",
        cardMemoriesTitle: "યાદો",
        cardMemoriesDesc: "કૌટુંબિક પળોના ફોટા અપલોડ કરો.",
        cardStoryTitle: "વાર્તા રેકોર્ડ કરો",
        cardStoryDesc: "વડીલોનો અવાજ હંમેશ માટે સાચવો.",
        cardVaultTitle: "દસ્તાવેજ વૉલ્ટ",
        cardVaultDesc: "વસિયત, દસ્તાવેજો — સુરક્ષિત, તમારી શરતો પર.",
        cardSettingsTitle: "ગોપનીયતા અને સેટિંગ્સ",
        cardSettingsDesc: "ભૂમિકાઓ, આમંત્રણો, તમારો ડેટા અને પ્રવૃત્તિ લોગ.",
        memories: "યાદો",
        addMemory: "યાદ ઉમેરો",
        memoriesEmpty: "હજી કોઈ યાદો નથી",
        memoriesEmptySub: "પહેલો કૌટુંબિક ફોટો અપલોડ કરો — લગ્ન, તહેવારો, રોજનો આનંદ.",
        caption: "કૅપ્શન",
        captionPlaceholder: "દા.ત. નાનીના ઘરે દિવાળી, 1998",
        memoryDate: "આ ક્યારનું છે?",
        tagMember: "કુટુંબના સભ્યને ટૅગ કરો",
        choosePhoto: "ફોટો પસંદ કરો",
        changePhoto: "ફોટો બદલો",
        uploadMemory: "યાદ સાચવો",
        uploading: "અપલોડ થઈ રહ્યું છે…",
        deleteMemoryConfirm: "આ યાદ કાઢી નાખવી? આ પાછી લાવી શકાશે નહીં.",
        confirmDelete: "પુષ્ટિ માટે ફરી ટૅપ કરો",
        uploadedBy: "ઉમેર્યું",
        heroEyebrow: "તમારો પરિવાર · તમારું વારસો",
        heroHeadline1: "તમારા પરિવારનો ઇતિહાસ",
        heroHeadlineAccent: "સાચવેલો",
        heroHeadline2: "હમેશા માટે.",
        heroSubline: "તમારું વંશવૃક્ષ બનાવો, વડીલોની અવાજ રેકોર્ડ કરો, તસવીરો અને દસ્તાવેજો સાચવો — આ બધું ભારતીય પરિવાર માટે બનાવેલી, તમારી પોતાની ભાષામાં આવેલી ખાનગી જગ્યામાં.",
        heroCtaPrimary: "મોબાઇલ OTPથી મફત શરૂઆત કરો",
        heroCtaSecondary: "આ કેવી રીતે કામ કરે છે તે જુઓ",
        heroTrustPrivate: "પ્રાઈવેટ, મૂળભૂત રીતે",
        heroTrustLangs: "10 ભારતીય ભાષાઓ",
        heroTrustFree: "શરૂઆત માટે મફત",
        heroSocialProof: "12,500+ ભારતીય પરિવારોનો વિશ્વાસ",
        heroBadge10: "10+",
        heroBadge10Cap: "ભારતીય ભાષાઓ",
        heroStoryAudio: "ઓડિયો વાર્તાઓ",
        heroStoryPhotos: "તસવીરો",
        heroStoryDocs: "દસ્તાવેજો",
        statHappyFamilies: "સંતોષી પરિવારો",
        statTreesCreated: "સાચવાયેલા પરિવાર સભ્યો",
        statStoriesRecorded: "વાર્તાઓ અને યાદો",
        statDataPrivacy: "ડેટા પ્રાઈવસી",
        demoBadge: "સાઇનઅપ કર્યા વગર અજમાવો",
        demoHeadline: "માત્ર 30 સેકન્ડમાં તમારું કુટુંબ વૃક્ષ જીવંત જુઓ",
        demoSubline: "ફક્ત 6 નામ ઉમેરો. અહીં જ તમારું 6 પેઢીનું કુટુંબ વૃક્ષ જીવતું થતું જુઓ.",
        demoTrustNoPhone: "ફોનની જરૂર નથી",
        demoTrustNoOtp: "OTP ની જરૂર નથી",
        demoTrustNoSignup: "સાઇનઅપ કરવાની જરૂર નથી",
        demoStepPrefix: "પગલું",
        demoStepOf: "માંથી 6 —",
        demoLabelFather: "પિતાનું નામ",
        demoPhFather: "જેમ કે: રાજેશ શર્મા",
        demoLabelGrandfather: "દાદા / નાનાનું નામ",
        demoPhGrandfather: "જેમ કે: મોહન શર્મા",
        demoLabelGreatGrand: "પરદાદા / પરનાનાનું નામ",
        demoPhGreatGrand: "જેમ કે: કિશન શર્મા",
        demoLabelChild: "દિકરો / દીકરીનું નામ",
        demoPhChild: "જેમ કે: અનુન્યા શર્મા",
        demoLabelGrandchild: "પૌત્ર / પૌત્રીનું નામ",
        demoPhGrandchild: "જેમ કે: વિહાન શર્મા",
        roleFather: "પિતા",
        roleGrandfather: "દાદા / નાણા",
        roleGreatGrandfather: "પરદાદા",
        roleChild: "દિકરો / દિકરી",
        roleGrandchild: "પૌત્ર / પૌત્રી",
        demoLabelYou: "તમારું નામ",
        demoLabelParent: "તમારા માતા-પિતાનું નામ",
        demoLabelGrand: "તમારા દાદા / દાદીનું નામ",
        demoPhYou: "દા.ત. રાહુલ શર્મા",
        demoPhParent: "દા.ત. સુરેશ શર્મા",
        demoPhGrand: "દા.ત. રામચંદ્ર શર્મા",
        demoHint: "તમારું વૃક્ષ નાજુક રીતે જીવંત જોવા માટે {n} વધુ નામ ઉમેરો.",
        demoNext: "આગળ",
        demoSeeTree: "તમારું વૃક્ષ જુઓ",
        demoRestart: "ફરી પ્રયત્ન કરો",
        demoStartReal: "મારું સાચું વૃક્ષ શરૂ કરો (મફત)",
        demoTreeBadge: "તમારું નાનું વૃક્ષ",
        demoChooseDesign: "પોસ્ટર ડિઝાઇન પસંદ કરો",
        demoClassic: "ક્લાસિક",
        demoFamilyTitle: "{name} પરિવાર",
        demoShareWhatsApp: "WhatsApp પર શેર કરો",
        demoShareText: "મેં 30 સેકન્ડમાં અમારું કુટુંબ વૃક્ષ બનાવ્યું! 🌳 તમે પણ મફતમાં બનાવો: {link}",
        demoScanToJoin: "સ્કેન કરી મફતમાં બનાવો",
        demoTreeHeadline: "છ પેઢીઓ, એક જીવતું વૃક્ષ.",
        demoTreeAddMore: "૨૦થી વધુ સગાઓ, ફોટા અને યાદો ઉમેરો — મફત.",
        demoFooterPrivacy: "તમારા ડેમોના પરિણામો ફક્ત તમારા બ્રાઉઝરમાં જ રહે છે. કંઈપણ સચવાતું કે મોકલાતું નથી.",
        roleGrandparent: "દાદા / દાદી",
        roleParent: "માતા-પિતા",
        roleYou: "તમે",
        testHeadline: "ભારતભરની પરિવારો અમ પર વિશ્વાસ રાખે છે",
        testSubline: "ખરા પરિવાર, સાચી જીવન કથાઓ — હવે હંમેશ માટે સાચવેલી.",
        testShareBtn: "તમારી કુટુંબ કથા શેર કરો",
        testShareHint: "Family Repository એ તમારા પરિવારને મદદ કરી છે? તમારી કથા શેર કરો.",
        noOtpTitle: "Family Repository તમારા OTP ક્યારેય નથી પૂછતું.",
        noOtpBody: "કોઈ ફોન કે મેસેજ કરીને OTP માંગે તો તરત કૉલ કપાઈ દો અથવા અવગણો. અમે ક્યારેય OTP નહીં માગીએ.",
        navLogin: "લૉગિન",
        navStartFree: "મફતમાં શરૂઆત કરો",
        navDashboard: "ડેશબોર્ડ",
        tnHome: "હોમ",
        tnFamilyTree: "ફેમિલી ટ્રી",
        tnFamilyHub: "ફેમિલી હબ",
        tnEvents: "ઇવેન્ટ્સ",
        tnPackages: "પેકેજિસ",
        tnVault: "વૉલ્ટ",
        tnReports: "રિપોર્ટ્સ",
        tnMore: "વધુ",
        tnRecords: "રેકોર્ડ્સ",
        tnSupport: "સપોર્ટ",
        sbDashboard: "ડેશબોર્ડ",
        sbMemberProfile: "સભ્ય પ્રોફાઇલ",
        sbgConnections: "સંપર્કો",
        sbConnectFriends: "વિસ્તૃત પરિવાર સાથે જોડાઓ",
        sbConnectRelatives: "નજીકના પરિવાર સાથે જોડાઓ",
        sbWorldMap: "વિશ્વનો નકશો",
        sbgMemories: "યાદો",
        sbPhotoMemories: "ફોટા યાદો",
        sbAudioMemories: "ઑડિયો યાદો",
        sbVideoMemories: "વિડિયો યાદો",
        sbgEvents: "ઘટનાઓ અને રિમાઇન્ડર્સ",
        sbFamilyCalendar: "કુટુંબ કેલેન્ડર",
        sbTodaysCelebration: "આજનો ઉત્સવ",
        sbFamilyTimeline: "વ્યક્તિગત જીવનપ્રવાસ",
        sbSmartReminder: "સ્માર્ટ રિમાઇન્ડર",
        sbgHealth: "આરોગ્ય અને મેડિકલ રિપોઝિટરી",
        sbMedicalHistory: "મેડિકલ હિસ્ટ્રી",
        sbChronicDiseases: "જીર્ણ / વંશાનુગત રોગો",
        sbCancerRegistry: "કેન્સર રજીસ્ટ્રી",
        sbGeneticCongenital: "જનિતિક અને જન્મજાત",
        sbAllergies: "ઍલર્જી અને અસહિશ્રુતા",
        sbVaccination: "ટીકાકરણનો રેકોર્ડ",
        sbLifestyle: "લાઇફસ્ટાઇલ ડેશબોર્ડ",
        sbPregnancyBirth: "ગર્ભાવસ્થા અને જન્મનો ઇતિહાસ",
        sbOrganDonation: "અવયવ દાનની પસંદગી",
        sbBloodGroup: "બ્લડ ગ્રુપ રજીસ્ટ્રી",
        sbgVault: "વ્યક્તિગત વૉલ્ટ",
        sbDocumentVault: "ડોક્યુમેન્ટ વૉલ્ટ",
        sbLegalVault: "કાનૂની દસ્તાવેજોનો વૉલ્ટ",
        sbgDisplay: "જુઓ અને ડાઉનલોડ કરો",
        sbDownloadExport: "ડાઉનલોડ અને એક્સપોર્ટ",
        sbDigitalFamilyTree: "ડિજિટલ વંશવૃક્ષ",
        sbPackagesPlans: "પેકેજ અને પ્લાન્સ",
        sbPrivacySettings: "પ્રાઇવસી અને સેટિંગ્સ",
        hdrDashboard: "ડેશબોર્ડ",
        hdrMenu: "મેનૂ",
        hdrMyProfile: "મારું પ્રોફાઇલ",
        hdrSettings: "સેટિંગ્સ",
        hdrSignOut: "સાઇન આઉટ",
        signOutTitle: "Family Repositoryમાંથી સાઇન આઉટ કરશો?",
        signOutBody: "તમે તમારા મોબાઇલ OTPથી કોઈપણ સમયે ફરી લોગિન કરી શકો છો.",
        roleAdministrator: "એડમિનિસ્ટ્રેટર",
        roleMemberPlain: "સભ્ય",
        searchPlaceholder: "કંઈ પણ શોધો…",
        searchNoResults: "આ માટે કોઈ પરિણામ નથી",
        grpMembers: "સભ્યો",
        grpMemories: "યાદો",
        grpDocuments: "દસ્તાવેજો",
        grpStories: "કથાઓ",
        grpEvents: "પ્રસંગો",
        msgTitle: "સંદેશા અને યાદ અપાવણીઓ",
        msgMarkAll: "બધાને વાંચેલું ગણાવો",
        msgEmpty: "હજુ સુધી કોઈ સંદેશો નથી — યાદ અપાવણીઓ અને પરિવારના અપડેટ્સ અહીં દેખાશે.",
        mbHome: "હોમ",
        mbTree: "વંશવૃક્ષ",
        mbAdd: "ઉમેરો",
        mbHub: "હબ",
        mbMe: "હું",
        dbMemberSince: "આથી સભ્ય",
        dbGrowingTogether: "એક સાથે વિકસતા",
        dbGrowingSub: "દરેક પરિવારની પોતાની એક વાર્તા હોય છે.",
        dbProfileCompleteness: "પ્રોફાઇલ પૂર્ણતા",
        dbMemoryOfDay: "આજની યાદ",
        dbMemoryQuote: "આજનો પરિવાર — આવતીકાલની પ્રેરણા.",
        dbViewMemory: "યાદ જુઓ",
        dbGoPremium: "પ્રીમિયમ લો",
        dbPremiumDesc: "અદ્યતન સુવિધાઓ ખોલો અને તમારા પરિવારનો ડેટા સુરક્ષિત રાખો.",
        dbUpgradeNow: "હમણાં અપગ્રેડ કરો",
        plMembers: "સભ્યો",
        plGenerations: "પેઢીઓ",
        plYearsHistory: "વર્ષોની ઇતિહાસ",
        plFamilyAccounts: "પરિવાર એકાઉન્ટ્સ",
        plTotalRecords: "કુલ રેકોર્ડ્સ",
        plPhotosVideos: "ફોટા અને વિડિઓઝ",
        plDocuments: "દસ્તાવેજો",
        plUpcomingEvents: "આગામી કાર્યક્રમો",
        plAnalytics: "વિશ્લેષણ",
        qaTitle: "ઝડપી ક્રિયાઓ",
        qaSubtitle: "વારંવાર ઉપયોગમાં આવતી સુવિધાઓનાં શૉર્ટકટ્સ",
        qaAddFamilyMember: "પરિવારનો સભ્ય ઉમેરો",
        qaViewFamilyTree: "કુટુંબ વૃક્ષ જુઓ",
        qaAddMemory: "યાદ ઉમેરો",
        qaScheduleEvent: "પ્રોગ્રામ નક્કી કરો",
        qaInviteFamily: "કુટુંબને આમંત્રિત કરો",
        chFamilyGrowth: "કુટુંબનો વિકાસ",
        chRecords: "રેકોર્ડ્સ",
        chActivity: "પ્રવૃત્તિ",
        chAllTime: "હંમેશા",
        chWeek: "અઠવાડિયું",
        chTotal: "કુલ",
        chActivityEmpty: "તમારું કુટુંબ યાદો ઉમેરે તેમ પ્રવૃત્તિ અહીં દેખાશે",
        recDocuments: "દસ્તાવેજો",
        recPhotosVideos: "ફોટા અને વિડિયો",
        recHealthRecords: "હેલ્થ રેકોર્ડ્સ",
        recLegalRecords: "કાનૂની રેકોર્ડ્સ",
        recStories: "વાર્તાઓ",
        dbTodaysCelebrations: "આજના સમારંભો",
        dbViewAll: "બધુ જુઓ",
        dbNoCelebToday: "આજે કોઈ જન્મદિવસ કે લગ્નવાર્ષિકી નથી.",
        dbTurnsToday: "આજે {n} વર્ષનું થાય છે!",
        dbRemembranceDay: "સ્મૃતિ દિવસ",
        dbViewCalendar: "કેલેન્ડર જુઓ",
        dbNoUpcoming60: "આગામી 60 દિવસમાં કોઈ આવનારી ઘટનાઓ નથી.",
        evBirthdayOf: "{name} નો જન્મદિવસ",
        evRemembranceOf: "{name} — સ્મરણ",
        evToday: "આજે",
        evInDays: "{n} દિવસમાં",
        evTurning: "{n} વર્ષના થવા જઈ રહ્યા છે",
        dbRecentActivity: "તાજેતરની પ્રવૃત્તિ",
        dbNoActivity: "હજી સુધી કોઈ પ્રવૃત્તિ નથી — શરૂઆત કરવા માટે સભ્યો અથવા યાદો ઉમેરો.",
        actSomeone: "કોઈક વ્યક્તિ",
        actPersonAdded: "નવો સભ્ય ઉમેર્યો",
        actPersonUpdated: "સભ્યની પ્રોફાઇલ સુધારી",
        actPersonDeleted: "સભ્યને દૂર કર્યો",
        actMemoryUploaded: "ફોટો અપલોડ કર્યો",
        actMemoryDeleted: "ફોટો કાઢી નાખ્યો",
        actDocUploaded: "દસ્તાવેજ અપલોડ કર્યો",
        actDocViewed: "દસ્તાવેજ જોયો",
        actStoryRecorded: "વાર્તા રેકોર્ડ કરી",
        actStoryPlayed: "વાર્તા ચલાવી",
        actCalendarCreate: "એક કાર્યક્રમ બનાવ્યો",
        actCalendarUpdate: "એક કાર્યક્રમ સુધાર્યો",
        actCapsuleCreated: "એક યાદોની કેપ્સ્યુલ બનાવી",
        actAchievementAdded: "એક સિદ્ધિ ઉમેરેલી",
        actDefault: "એક સુધારો કર્યો",
        taJustNow: "હમણાં જ",
        taMinAgo: "{n} મિનિટ પહેલાં",
        taHourAgo: "{n} કલાક પહેલાં",
        taDayAgo: "{n} દિવસ પહેલાં",
        gsTitle: "શરૂઆત કરીએ",
        gsProgress: "{done}/{total} પૂરું થયું",
        gsAddMember: "પરિવારના સભ્ય ઉમેરો",
        gsUploadMemory: "યાદ અપલોડ કરો",
        gsRecordStory: "કથા રેકોર્ડ કરો",
        gsInviteFamily: "પરિવારને આમંત્રિત કરો",
        gsSecureDoc: "દસ્તાવેજ સુરક્ષિત કરો",
        gsCelebrateTitle: "શરૂઆતની તમામ સુચનાઓ પૂરી!",
        gsCelebrateBody: "બધા 5 પગલાં પૂર્ણ — તમારા પરિવારની ડિજિટલ વારસાગાથા હવે સત્તાવાર રીતે શરુ થઈ ચૂકેલી છે. તમારું વંશવૃક્ષ વધારતા રહો!",
        actProfileUpdated: "તેમનો પ્રોફાઇલ અપડેટ કર્યો",
        daySun: "રવિ",
        dayMon: "સોમ",
        dayTue: "મંગળ",
        dayWed: "બુધ",
        dayThu: "ગુરૂ",
        dayFri: "શુક્ર",
        daySat: "શનિ",
        hbTitle: "પરિવાર — બધું એક જ જગ્યાએ",
        hbCategories: "કેટેગરીઝ",
        hbSearchAll: "બધાં ફીચર્સમાં શોધો…",
        hbNoMatch: "કોઈ ફીચર્સ મેળ ખાતા નથી",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "જલ્દી",
        hbNotifyMe: "મને જાણ કરશો",
        hbNotified: "તૈયાર થાય ત્યારે જાણ કરવામાં આવી",
        hbNotifyTitle: "જ્યારે \\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\" LIVE થશે ત્યારે અમે તમને જાણ કરીશું",
        hbNotifyBody: "તમારી રસદારી નોંધાઈ ગઈ છે. અમારી ટીમ પહેલા તે ફીચર્સ પર કામ કરે છે, જેની સૌથી વધુ પરિવારો માંગ કરે છે.",
        hbGreat: "સારું",
        rcSearch: "શોધો…",
        rcExportCsv: "CSV એક્સપોર્ટ કરો",
        rcAddNew: "નવું ઉમેરો",
        rcCreate: "બનાવો",
        rcEdit: "ફેરફાર કરો",
        rcSave: "સાચવો",
        rcCancel: "રદ કરો",
        rcLoading: "લોડ થઈ રહ્યું છે…",
        rcNoYet: "હજી સુધી કોઈ {label} નથી",
        rcClick: "ક્લિક કરો",
        rcFirstEntry: "તમારી પહેલી એન્ટ્રી ઉમેરવા માટે.",
        rcNew: "નવી",
        rcFormHint: "નીચેના ફોર્મ ભરો — જરૂરી ફિલ્ડ પર લાલ તારાનું નિશાન છે.",
        rcHealthBold: "ફક્ત કુટુંબ માટેનો રેકોર્ડ રાખવા માટે",
        rcHealthRest: "— નિદાન, સારવાર કે કોઈ પણ તબીબી સલાહ માટે નથી. કુટુંબની સંમતિથી ફેમિલી હેડ સ્વાસ્થ્ય સંબંધિત રેકોર્ડ ઉમેરવા અથવા સુધારવા શકે છે.",
        rcConfirmTitle: "કૃપા કરીને ખાતરી કરો",
        rcConfirmDelete: "આ રેકોર્ડ ડિલીટ કરવો?",
        rcUpdated: "અપડેટ થયું.",
        rcSaved: "સાચવાયું.",
        rcCreated: "બનાવાયું.",
        rcDeleted: "કાઢી નાખ્યું.",
        rcChoose: "પસંદ કરો…",
        rcUnknownSection: "અજાણી વિભાગ",
        rcSectionMissing: "આ ફેમિલી રેકોર્ડ્સ વિભાગ અસ્તિત્વમાં નથી.",
        rcBackToHub: "ફેમિલી હબ પર પાછા જાઓ",
        rcWatchDemo: "ડેમો જુઓ",
        rcDemoSoon: "ડેમો જલ્દી જ આવી રહ્યો છે",
        rcFamilyService: "ફેમિલી સર્વિસ",
        rcUntitled: "(શીર્ષક નથી)",
        setTitle: "સેટિંગ્સ અને ગોપનીયતા",
        setBack: "પાછા",
        setTabInvites: "આમંત્રણો",
        setTabSecurity: "સુરક્ષા",
        setTabData: "મારો ડેટા",
        setTabActivity: "ક્રિયાઓ",
        setTabHelp: "મદદ",
        setMyAccount: "મારું એકાઉન્ટ",
        setMyAccountSub: "રસીદ, સ્વાગત-પત્ર અને સપોર્ટના જવાબ મેળવવા માટે તમારું ઇમેલ ઉમેરો. તમારો ફોન નંબર તમારો લોગિન છે અને તેને અહીં બદલાવી શકાતો નથી.",
        setName: "નામ",
        setEmail: "ઇમેલ",
        setEmailInvalid: "માન્ય ઇમેલ સરનામું દાખલ કરો.",
        setSaveChanges: "ફેરફારો સચવો",
        setSaving: "સાચવાઈ રહ્યું છે…",
        setSaved: "એકાઉન્ટની વિગતો સાચવાઈ ગઈ.",
        setFamilyMembers: "કુટુંબના સભ્યો",
        setRolesHint: "એડમિન: પૂર્ણ નિયંત્રણ · એડિટર: સભ્યો ઉમેરો/ફેરફાર કરો · વ્યૂઅર: ફક્ત વાંચન",
        setYou: "(તમે)",
        setRoleUpdated: "ભૂમિકા અપડેટ થઈ.",
        setActiveInvites: "સક્રિય આમંત્રણો",
        setInvitesSub: "વૃક્ષમાં સભ્યની પ્રોફાઇલ ડ્રોઅરમાંથી આમંત્રણો મોકલો. ક્યારે પણ રદ કરી શકો છો.",
        setNoActiveInvites: "કોઈ સક્રિય આમંત્રણો નથી.",
        setRevoke: "રદ કરો",
        setExpires: "સમાપ્ત થાય છે",
        setOnlyAdmins: "ફક્ત પરિવાર એડમિન્સ આમંત્રણો મેનેજ કરી શકે.",
        setInviteRevoked: "આમંત્રણ રદ થયું.",
        setSeniorSub: "પ્રાયવસી વિકલ્પોને ૩ મોટા વિકલ્પોમાં સરળ બનાવે છે: ખાનગી / પરિવાર / જાહેર.",
        setWelcomeTour: "સ્વાગત ટૂર",
        setWelcomeTourSub: "૩-પગલું પરિચય ફરીથી જુઓ: Family Tree → Memories → Vault.",
        setReplayTour: "ટૂર ફરીથી ચલાવો",
        setLivingIn: "રહે છે અહીં",
        setLivingInSub: "વૈકલ્પિક — અમને NRI પરિવારોને વધુ સારી રીતે સેવા આપવા મદદ કરે છે.",
        setIndia: "ભારત",
        setAbroad: "વિદેશ (NRI)",
        setSelect: "પસંદ કરો",
        setWhatWeStore: "તમારા વિશે અમે શું સંગ્રહીએ છીએ",
        setDpdpNote: "DPDP અધિનિયમ: સંપૂર્ણ પારદર્શિતા, હંમેશા.",
        setProfileSections: "પ્રોફાઇલ વિભાગો",
        setVaultDocs: "વોલ્ટ દસ્તાવેજો",
        setInvitesSent: "નિમંત્રણો મોકલ્યા",
        setActivityEntries: "પ્રવૃત્તિ એન્ટ્રીઓ",
        setDownloadData: "મારા બધા ડેટા ડાઉનલોડ કરો (JSON)",
        setDeleteTitle: "મારું ખાતું અને ડેટા કાઢી નાખો",
        setDeleteSub: "આપનું ખાતું, તમારી વાર્તાઓ, તિજોરીના દસ્તાવેજો અને પ્રવૃત્તિનો દાખલાખાતો કાયમી રીતે દૂર થશે. તમે પરિવારના અન્ય સભ્યો વિશે પરિવારવૃક્ષમાં કરેલી નોંધો પરિવારમાં જ રહેશે.",
        setDeleteEverything: "બધું કાઢી નાખો",
        setDeleteConfirmTitle: "શું તમારું ખાતું કાયમ માટે કાઢી નાખવું છે?",
        setDeleteConfirmBody: "આ પાછું કરવું શક્ય નથી. તમારી વાર્તાઓ, દસ્તાવેજો અને ખાતું DPDP કાયદા મુજબ કાયમ માટે કાઢી નાખવામાં આવશે.",
        setDeleting: "કાઢી રહ્યા છીએ…",
        setYesDelete: "હા, બધું કાઢી નાખો",
        setActivityLog: "પ્રવૃત્તિનો દાખલાખાતો",
        setAuditAdminSub: "તમારા પરિવાર માટે કોણે શું જોયું અને શું બદલ્યું.",
        setAuditOwnSub: "તમારી પોતાની પ્રવૃત્તિ.",
        setNoActivityYet: "હજુ કોઈ પ્રવૃત્તિ નથી.",
        setSearchFamily: "પરિવાર શોધો…",
        ptsEyebrow: "અમારા ડિઝાઇન સ્ટુડિયોમાંથી",
        ptsTitle: "સુંદર ફેમિલી પોસ્ટર ટેમ્પલેટ્સ",
        ptsSub: "અમારા સ્ટુડિયોથી હાથથી બનાવેલા પોસ્ટર ડિઝાઇન — તેમાં તમારી ફેમિલીના ફોટા એકદમ સરસ જામે છે. સાઇન અપ કર્યા પછી તમારો મનપસંદ ડિઝાઇન પસંદ કરો.",
        ptsCta: "તમારું ફેમિલી પોસ્ટર બનાવો",
        ptsPremium: "પ્રીમિયમ",
        hcTitle: "હેલ્પ સેન્ટર",
        hcSub: "અમને કંઈ પણ પૂછો — અમે સામાન્ય રીતે થોડા જ કલાકોમાં જવાબ આપીએ છીએ.",
        hcAskTitle: "પ્રશ્ન પૂછો",
        hcAskSub: "તમારી સમસ્યા વર્ણવો, અમારી સપોર્ટ ટીમ તમને જવાબ આપશે.",
        hcSubject: "વિષય",
        hcSubjectPh: "જેમ કે: હું ફોટો અપલોડ કરી શકતો નથી",
        hcMessage: "તમારો પ્રશ્ન",
        hcMessagePh: "પગલું દર પગલું અમને કહો શું થયું…",
        hcSubmit: "પ્રશ્ન મોકલો",
        hcSubmitting: "મોકલી રહ્યા છીએ…",
        hcSuccess: "તમારો પ્રશ્ન મોકલી દેવાયો છે — અમે જલ્દી જ જવાબ મોકલીશું.",
        hcFailed: "તમારો પ્રશ્ન મોકલી શક્યા નથી. કૃપા કરીને ફરી પ્રયત્ન કરો.",
        hcFillBoth: "કૃપા કરીને વિષય અને તમારો પ્રશ્ન બંને ભરો.",
        hcMyTickets: "મારા પ્રશ્નો",
        hcNoTickets: "હજુ સુધી કોઈ પ્રશ્ન નથી — ઉપર તમારો પહેલો પ્રશ્ન પૂછો.",
        hcStatusOpen: "ખુલ્લું",
        hcStatusResolved: "ઉકેલાયું",
        hcSupportReply: "સપોર્ટ",
        hcQuickHelp: "ઝડપી મદદ",
        hcWhatsapp: "WhatsApp સપોર્ટ — તરત વાતચીત",
        hcCall: "કૉલ: +91-99999-99999 (સોમ–શનિ 10–7)",
        hcFaq: "FAQ — વારંવાર પૂછાતા પ્રશ્નો",
        hcTipTitle: "સલાહ",
        hcTipBody: "ઘણા જવાબો પહેલા જ FAQ માં આપેલા છે. તમારા પરિવારના ડેટા સંબંધિત કોઇ વ્યક્તિગત બાબત માટે, અહીં પૂછો — તેને ફક્ત અમારી સપોર્ટ ટીમ જ જોઈ શકે છે.",
        demoAddPhoto: "ફોટો ઉમેરો (વૈકલ્પિક)",
        demoChangePhoto: "ફોટો બદલો",
        demoDownloadPoster: "પોસ્ટર ડાઉનલોડ કરો",
        demoDownloading: "તૈયાર થઈ રહ્યું છે…",
    },
    te: {
        tourListen: "వినండి",
        tourStop: "ఆపండి",
        tourEyebrow: "వంశ్‌ట్రీకి స్వాగతం",
        tourStepOf: "దశ {n} / {total}",
        tourNext: "తర్వాత",
        tourStart: "ప్రారంభించండి",
        tourS1Title: "మీ వంశవృక్షాన్ని నిర్మించండి",
        tourS1Body: "తల్లిదండ్రులు, తాతయ్య-అమ్మమ్మలు మరియు పిల్లలను చేర్చడం ద్వారా ప్రారంభించండి. మీరు చేర్చే ప్రతి వ్యక్తి మీ కుటుంబ కథలో సజీవ శాఖ అవుతారు.",
        tourS1Cta: "వంశవృక్షం తెరవండి",
        tourS2Title: "జ్ఞాపకాలను భద్రపరచండి",
        tourS2Body: "ఫోటోలు అప్‌లోడ్ చేయండి, స్వరాలను రికార్డ్ చేయండి, కథలు రాయండి. భవిష్యత్ తరాలు మళ్లీ అనుభవించేలా ఆల్బమ్‌లుగా అమర్చండి.",
        tourS2Cta: "జ్ఞాపకాలు తెరవండి",
        tourS3Title: "వాల్ట్‌ను భద్రపరచండి",
        tourS3Body: "వీలునామాలు, ఆస్తి పత్రాలు మరియు చట్టపరమైన పత్రాలను బ్యాంక్-స్థాయి భద్రతతో ఉంచండి — సరైన సమయంలో తర్వాతి తరానికి అందించండి.",
        tourS3Cta: "వాల్ట్ తెరవండి",
        back: "వెనుకకు",
        cancel: "రద్దు చేయండి",
        delete: "తొలగించండి",
        logout: "లాగౌట్",
        optional: "(ఐచ్ఛికం)",
        tagline: "మీ కుటుంబం. ఎల్లప్పుడూ.",
        familyLegacy: "కుటుంబ వారసత్వం",
        signInTitle: "Family Repository లో సైన్ ఇన్ చేయండి",
        otpTitle: "మీ OTP నమోదు చేయండి",
        phoneSub: "మేము మీ మొబైల్‌కు OTP పంపుతాము.",
        otpSentTo: "OTP పంపబడింది",
        expiresIn: "గడువు ముగుస్తుంది",
        mobileNumber: "మొబైల్ నంబర్",
        phoneHint: "భారతీయ నంబర్‌లు మాత్రమే (6–9తో మొదలు).",
        yourName: "మీ పేరు",
        firstTimeOnly: "(మొదటి సారి మాత్రమే)",
        haveInviteCode: "కుటుంబ ఆహ్వాన కోడ్ ఉందా?",
        apply: "వర్తించు",
        sendOtp: "OTP పంపండి",
        sendingOtp: "OTP పంపబడుతోంది…",
        otpLabel: "6-అంకెల OTP",
        verifyContinue: "ధృవీకరించి కొనసాగించు",
        verifying: "ధృవీకరిస్తోంది…",
        changeNumber: "నంబర్ మార్చండి",
        resendOtp: "OTP మళ్లీ పంపండి",
        resendIn: "మళ్లీ పంపండి",
        terms: "కొనసాగించడం ద్వారా మీరు Family Repository సేవా నిబంధనలు మరియు గోప్యతా విధానానికి అంగీకరిస్తున్నారు.",
        trustStrip: "ఎన్‌క్రిప్టెడ్ • కుటుంబ నియంత్రణ • భారతదేశంలో నిర్మించబడింది",
        welcome: "స్వాగతం",
        dashSub: "మీ Family Repository సిద్ధంగా ఉంది. మొదటి సభ్యుని జోడించండి లేదా పెద్దల కథను రికార్డ్ చేయండి.",
        quickActions: "త్వరిత చర్యలు",
        footerNote: "మీ డేటా వ్యక్తిగతం, ఎన్‌క్రిప్టెడ్ మరియు కుటుంబ నియంత్రణలో ఉంది. భారతదేశం కోసం — ఎల్లప్పుడూ.",
        roleFamilyHead: "కుటుంబ ప్రముఖుడు",
        roleAdmin: "అడ్మిన్",
        roleEditor: "సంపాదకుడు",
        roleViewer: "వీక్షకుడు",
        roleMember: "సభ్యుడు",
        cardTreeTitle: "కుటుంబ వృక్షం చూడండి",
        cardTreeDesc: "తరాల మీద విస్తరించిన మీ కుటుంబాన్ని చూడండి.",
        cardAddTitle: "సభ్యుని జోడించండి",
        cardAddDesc: "తల్లిదండ్రులు, తోబుట్టువులు, పిల్లలు లేదా బంధువులను జోడించండి.",
        cardMemoriesTitle: "జ్ఞాపకాలు",
        cardMemoriesDesc: "కుటుంబ క్షణాల ఫోటోలు అప్‌లోడ్ చేయండి.",
        cardStoryTitle: "కథ రికార్డ్ చేయండి",
        cardStoryDesc: "పెద్దల స్వరాన్ని శాశ్వతంగా దాచండి.",
        cardVaultTitle: "డాక్యుమెంట్ వాల్ట్",
        cardVaultDesc: "విల్లులు, పత్రాలు — సురక్షితం, మీ నిబంధనలపై.",
        cardSettingsTitle: "గోప్యత & సెట్టింగ్‌లు",
        cardSettingsDesc: "పాత్రలు, ఆహ్వానాలు, మీ డేటా మరియు యాక్టివిటీ లాగ్.",
        memories: "జ్ఞాపకాలు",
        addMemory: "జ్ఞాపకాన్ని జోడించండి",
        memoriesEmpty: "ఇంకా జ్ఞాపకాలు లేవు",
        memoriesEmptySub: "మొదటి కుటుంబ ఫోటోను అప్‌లోడ్ చేయండి — వివాహాలు, పండుగలు, రోజువారీ సంతోషం.",
        caption: "క్యాప్షన్",
        captionPlaceholder: "ఉదా. నానమ్మ ఇంట్లో దీపావళి, 1998",
        memoryDate: "ఇది ఎప్పటిది?",
        tagMember: "కుటుంబ సభ్యుని ట్యాగ్ చేయండి",
        choosePhoto: "ఫోటో ఎంచుకోండి",
        changePhoto: "ఫోటో మార్చండి",
        uploadMemory: "జ్ఞాపకం సేవ్ చేయండి",
        uploading: "అప్‌లోడ్ అవుతోంది…",
        deleteMemoryConfirm: "ఈ జ్ఞాపకాన్ని తొలగించాలా? ఇది తిరిగి పొందలేరు.",
        confirmDelete: "నిర్ధారించడానికి మళ్లీ ట్యాప్ చేయండి",
        uploadedBy: "జోడించినవారు",
        heroEyebrow: "మీ కుటుంబం · మీ వారసత్వం",
        heroHeadline1: "మీ కుటుంబ చరిత్ర",
        heroHeadlineAccent: "శాశ్వతంగా",
        heroHeadline2: "భద్రపరచండి.",
        heroSubline: "మీ కుటుంబ వృక్షాన్ని నిర్మించండి, పెద్దల స్వరాలను రికార్డ్ చేయండి, ఫోటోలు మరియు పత్రాలను భద్రపరచండి — ఇవన్నీ భారతీయ కుటుంబాల కోసం, మీ స్వంత భాషలో రూపొందించిన ఒక వ్యక్తిగత స్థలంలో.",
        heroCtaPrimary: "మొబైల్ OTP తో ఉచితంగా ప్రారంభించండి",
        heroCtaSecondary: "ఇది ఎలా పని చేస్తుందో చూడండి",
        heroTrustPrivate: "ప్రమేయం లేకుండా మొదటి నుండే వ్యక్తిగతం",
        heroTrustLangs: "10 భారతీయ భాషలు",
        heroTrustFree: "ఉచితంగా ప్రారంభించండి",
        heroSocialProof: "12,500+ భారతీయ కుటుంబాల నమ్మకం",
        heroBadge10: "10+",
        heroBadge10Cap: "భారతీయ భాషలు",
        heroStoryAudio: "ఆడియో కథలు",
        heroStoryPhotos: "ఫోటోలు",
        heroStoryDocs: "పత్రాలు",
        statHappyFamilies: "సంతోషకరమైన కుటుంబాలు",
        statTreesCreated: "భద్రపరచిన కుటుంబ సభ్యులు",
        statStoriesRecorded: "కథలు & జ్ఞాపకాలు",
        statDataPrivacy: "డేటా గోప్యత",
        demoBadge: "సైన్ అప్ లేకుండానే ప్రయత్నించండి",
        demoHeadline: "మీ వంశవృక్షం 30 సెకండ్లలో కనిపించేలా చూడండి",
        demoSubline: "6 పేర్లు జోడించండి. మీ 6 తరాల వంశవృక్షం ఇక్కడే ఎలా కదులుతుందో చూడండి.",
        demoTrustNoPhone: "ఫోన్ అవసరం లేదు",
        demoTrustNoOtp: "OTP అవసరం లేదు",
        demoTrustNoSignup: "సైన్ అప్ అవసరం లేదు",
        demoStepPrefix: "దశ",
        demoStepOf: "6లో —",
        demoLabelFather: "తండ్రి పేరు",
        demoPhFather: "ఉదా. రాజేష్ శర్మ",
        demoLabelGrandfather: "తాతయ్య పేరు",
        demoPhGrandfather: "ఉదా. మోహన్ శర్మ",
        demoLabelGreatGrand: "బాబు తాతయ్య పేరు",
        demoPhGreatGrand: "ఉదా. కిషన్ శర్మ",
        demoLabelChild: "కొడుకు / కూతురు పేరు",
        demoPhChild: "ఉదా. అనన్య శర్మ",
        demoLabelGrandchild: "మనుమడు / మనవరాలు పేరు",
        demoPhGrandchild: "ఉదా. విహాన్ శర్మ",
        roleFather: "తండ్రి",
        roleGrandfather: "తాతయ్య",
        roleGreatGrandfather: "ముత్తాత",
        roleChild: "కొడుకు/కూతురు",
        roleGrandchild: "మనవడు/మనవరాలు",
        demoLabelYou: "మీ పేరు",
        demoLabelParent: "మీ తల్లిదండ్రి పేరు",
        demoLabelGrand: "మీ తాత/అమ్మమ్మ పేరు",
        demoPhYou: "ఉదా. రాహుల్ శర్మ",
        demoPhParent: "ఉదా. సురేష్ శర్మ",
        demoPhGrand: "ఉదా. రామ్‌చంద్ర శర్మ",
        demoHint: "మీ వృక్షం నిజంగా కనిపించడానికి ఇంకో {n} పేర్లు జోడించండి.",
        demoNext: "తరువాత",
        demoSeeTree: "మీ వృక్షం చూడండి",
        demoRestart: "మళ్లీ ప్రయత్నించండి",
        demoStartReal: "నా నిజమైన వృక్షాన్ని మొదలు పెట్టు (ఉచితం)",
        demoTreeBadge: "మీ చిన్న వృక్షం",
        demoChooseDesign: "పోస్టర్ డిజైన్ ఎంచుకోండి",
        demoClassic: "క్లాసిక్",
        demoFamilyTitle: "{name} కుటుంబం",
        demoShareWhatsApp: "WhatsApp లో షేర్ చేయండి",
        demoShareText: "నేను 30 సెకన్లలో మా కుటుంబ వృక్షాన్ని తయారు చేశాను! 🌳 మీరూ ఉచితంగా చేయండి: {link}",
        demoScanToJoin: "స్కాన్ చేసి ఉచితంగా చేయండి",
        demoTreeHeadline: "ఆరు తరాలు, ఒక జీవించే వృక్షం.",
        demoTreeAddMore: "ఇంకా 20+ మంది బంధువులు, ఫోటోలు, జ్ఞాపకాలు జోడించండి — ఉచితం.",
        demoFooterPrivacy: "మీ డెమో ఫలితాలు మీ బ్రౌజర్‌లోనే ఉంటాయి. ఏదీ సేవ్ చేయబడదు లేదా పంపబడదు.",
        roleGrandparent: "తాత/అమ్మమ్మ",
        roleParent: "తల్లిదండ్రి",
        roleYou: "మీరు",
        testHeadline: "భారతదేశం నలుమూలల కుటుంబాలు మమ్మల్ని నమ్ముతున్నాయి",
        testSubline: "నిజమైన కుటుంబాలు, నిజజీవిత కథలు – శాశ్వతంగా సంరక్షించబడ్డాయి.",
        testShareBtn: "మీ కుటుంబ కథను పంచుకోండి",
        testShareHint: "Family Repository మీ కుటుంబానికి ఉపయోగపడిందా? మీ కథను పంచుకోండి.",
        noOtpTitle: "Family Repository ఎప్పుడూ మీ OTP‌ను అడగదు.",
        noOtpBody: "ఎవరైనా ఫోన్ లేదా మెసేజ్ ద్వారా మీ OTP అడిగితే – వెంటనే సంభాషణను నిలిపివేయండి. మేము ఎప్పుడూ ఆడగం.",
        navLogin: "లాగిన్",
        navStartFree: "ఉచితంగా ప్రారంభించండి",
        navDashboard: "డాష్‌బోర్డ్",
        tnHome: "హోమ్",
        tnFamilyTree: "కుటుంబ వృక్షం",
        tnFamilyHub: "ఫ్యామిలీ హబ్",
        tnEvents: "ఈవెంట్లు",
        tnPackages: "ప్యాకేజీలు",
        tnVault: "వాల్ట్",
        tnReports: "రిపోర్టులు",
        tnMore: "ఇంకా",
        tnRecords: "రికార్డులు",
        tnSupport: "సపోర్ట్",
        sbDashboard: "డ్యాష్‌బోర్డ్",
        sbMemberProfile: "సభ్యుడి ప్రొఫైల్",
        sbgConnections: "కనెక్షన్లు",
        sbConnectFriends: "దూరపు కుటుంబాలతో కలుసుకోండి",
        sbConnectRelatives: "సన్నిహిత కుటుంబాలతో కలుసుకోండి",
        sbWorldMap: "ప్రపంచ పటం",
        sbgMemories: "స్మృతులు",
        sbPhotoMemories: "ఫోటో స్మృతులు",
        sbAudioMemories: "ఆడియో స్మృతులు",
        sbVideoMemories: "వీడియో స్మృతులు",
        sbgEvents: "ఈవెంట్లు & రిమైండర్లు",
        sbFamilyCalendar: "కుటుంబ క్యాలెండర్",
        sbTodaysCelebration: "ఈరోజు జరుపుకుంటున్నవి",
        sbFamilyTimeline: "వ్యక్తిగత జీవిత ప్రయాణం",
        sbSmartReminder: "స్మార్ట్ రిమైండర్",
        sbgHealth: "ఆరోగ్య & వైద్య నిల్వ",
        sbMedicalHistory: "వైద్య చరిత్ర",
        sbChronicDiseases: "దీర్ఘకాలిక / వారసత్వ వ్యాధులు",
        sbCancerRegistry: "క్యాన్సర్ రిజిస్ట్రీ",
        sbGeneticCongenital: "ఆనువంశిక & జన్మనుండి ఉన్నవి",
        sbAllergies: "అలర్జీలు & అసహ్యతలు",
        sbVaccination: "టీకా రికార్డులు",
        sbLifestyle: "జీవనశైలి డాష్‌బోర్డ్",
        sbPregnancyBirth: "గర్భధారణ & ప్రసవ చరిత్ర",
        sbOrganDonation: "అవయవ దానం అభిరుచి",
        sbBloodGroup: "రక్తగ్రూప్ రిజిస్ట్రీ",
        sbgVault: "వ్యక్తిగత వాల్ట్",
        sbDocumentVault: "పత్రాల వాల్ట్",
        sbLegalVault: "చట్టపరమైన వాల్ట్",
        sbgDisplay: "ప్రదర్శన & డౌన్‌లోడ్",
        sbDownloadExport: "డౌన్‌లోడ్ & ఎగుమతి",
        sbDigitalFamilyTree: "డిజిటల్ ఫ్యామిలీ ట్రీ",
        sbPackagesPlans: "ప్యాకేజీలు & ప్లాన్‌లు",
        sbPrivacySettings: "గోప్యత & సెట్టింగ్‌లు",
        hdrDashboard: "డాష్‌బోర్డ్",
        hdrMenu: "మెనూ",
        hdrMyProfile: "నా ప్రొఫైల్",
        hdrSettings: "సెట్టింగ్‌లు",
        hdrSignOut: "సైన్ అవుట్",
        signOutTitle: "Family Repository నుండి సైన్ అవుట్ అవాలా?",
        signOutBody: "మీరు ఎప్పుడైనా మీ మొబైల్ OTPతో మళ్లీ లాగిన్ అవవచ్చు.",
        roleAdministrator: "నిర్వాహకుడు",
        roleMemberPlain: "సభ్యుడు",
        searchPlaceholder: "ఏదైనా వెతకండి…",
        searchNoResults: "దీనికి ఫలితాలు లేవు",
        grpMembers: "సభ్యులు",
        grpMemories: "స్మృతులు",
        grpDocuments: "పత్రాలు",
        grpStories: "కథలు",
        grpEvents: "కార్యక్రమాలు",
        msgTitle: "సందేశాలు & రిమైండర్లు",
        msgMarkAll: "అన్నింటినీ చదివినట్లుగా గుర్తించు",
        msgEmpty: "ఇంకా సందేశాలు లేవు — రిమైండర్లు మరియు కుటుంబ అప్డేట్లు ఇక్కడ కనిపిస్తాయి.",
        mbHome: "హోమ్",
        mbTree: "వంశవృక్షం",
        mbAdd: "జోడించు",
        mbHub: "హబ్",
        mbMe: "నేను",
        dbMemberSince: "సభ్యత్వం ప్రారంభమైన తేదీ",
        dbGrowingTogether: "కలిసి ఎదుగుదాం",
        dbGrowingSub: "ప్రతి కుటుంబానికీ ఒక కథ ఉంటుంది.",
        dbProfileCompleteness: "ప్రొఫైల్ పూర్తితనం",
        dbMemoryOfDay: "ఈ రోజు జ్ఞాపకం",
        dbMemoryQuote: "ఈ రోజు కుటుంబం — రేపటి ప్రేరణ.",
        dbViewMemory: "జ్ఞాపకం చూడండి",
        dbGoPremium: "ప్రీమియంకు మారండి",
        dbPremiumDesc: "అధునాతన ఫీచర్లను తెరవండి, మీ కుటుంబ డేటాను సురక్షితంగా ఉంచండి.",
        dbUpgradeNow: "ఇప్పుడే అప్‌గ్రేడ్ చేయండి",
        plMembers: "సభ్యులు",
        plGenerations: "తరాలు",
        plYearsHistory: "చరిత్ర సంవత్సరాలు",
        plFamilyAccounts: "కుటుంబ ఖాతాలు",
        plTotalRecords: "మొత్తం రికార్డులు",
        plPhotosVideos: "ఫోటోలు & వీడియోలు",
        plDocuments: "పత్రాలు",
        plUpcomingEvents: "రాబోయే కార్యక్రమాలు",
        plAnalytics: "విశ్లేషణ",
        qaTitle: "త్వరిత చర్యలు",
        qaSubtitle: "తరచూ ఉపయోగించే ఫీచర్లకు షార్ట్‌కట్లు",
        qaAddFamilyMember: "కుటుంబ సభ్యుడిని చేర్చండి",
        qaViewFamilyTree: "కుటుంబ వంశ వృక్షాన్ని చూడండి",
        qaAddMemory: "ఒక జ్ఞాపకాన్ని జోడించండి",
        qaScheduleEvent: "కార్యక్రమాన్ని నిర్ణయించండి",
        qaInviteFamily: "కుటుంబాన్ని ఆహ్వానించండి",
        chFamilyGrowth: "కుటుంబ విస్తరణ",
        chRecords: "రికార్డులు",
        chActivity: "చర్యలు",
        chAllTime: "మొత్తం కాలం",
        chWeek: "వారం",
        chTotal: "మొత్తం",
        chActivityEmpty: "మీ కుటుంబ సభ్యులు జ్ఞాపకాలు జోడించినప్పుడు ఇక్కడ కార్యకలాపాలు కనిపిస్తాయి",
        recDocuments: "పత్రాలు",
        recPhotosVideos: "ఫోటోలు & వీడియోలు",
        recHealthRecords: "ఆరోగ్య రికార్డులు",
        recLegalRecords: "చట్టపరమైన రికార్డులు",
        recStories: "కథలు",
        dbTodaysCelebrations: "ఈ రోజు వేడుకలు",
        dbViewAll: "అన్నీ చూడండి",
        dbNoCelebToday: "ఈ రోజు జన్మదినాలు లేదా వార్షికోత్సవాలు లేవు.",
        dbTurnsToday: "ఈ రోజు {n} ఏళ్లకు చేరుకుంటున్నారు!",
        dbRemembranceDay: "స్మరణ దినం",
        dbViewCalendar: "క్యాలెండర్ చూడండి",
        dbNoUpcoming60: "వచ్చే 60 రోజుల్లో ఎటువంటి ఈవెంట్లు లేవు.",
        evBirthdayOf: "{name} గారి పుట్టినరోజు",
        evRemembranceOf: "{name} — స్మరణ",
        evToday: "ఈ రోజు",
        evInDays: "{n} రోజుల్లో",
        evTurning: "{n} సంవత్సరాలు పూర్తవుతున్నారు",
        dbRecentActivity: "ఇటీవలి కార్యాచరణ",
        dbNoActivity: "ఇంకా ఏ కార్యాచరణ లేదు — ప్రారంభించడానికి సభ్యులను లేదా జ్ఞాపకాలను జోడించండి.",
        actSomeone: "ఎవరైనా",
        actPersonAdded: "కొత్త సభ్యుణ్ని జోడించారు",
        actPersonUpdated: "సభ్యుని ప్రొఫైల్‌ను మార్చారు",
        actPersonDeleted: "ఒక సభ్యుణ్ని తీసేశారు",
        actMemoryUploaded: "ఒక ఫోటోను ఎక్కించారు",
        actMemoryDeleted: "ఒక ఫోటోను తొలగించారు",
        actDocUploaded: "ఒక పత్రాన్ని ఎక్కించారు",
        actDocViewed: "ఒక పత్రాన్ని చూశారు",
        actStoryRecorded: "ఒక కథను రికార్డ్ చేశారు",
        actStoryPlayed: "ఒక కథను ప్లే చేశారు",
        actCalendarCreate: "ఒక కార్యక్రమాన్ని సృష్టించారు",
        actCalendarUpdate: "ఒక కార్యక్రమాన్ని మార్చారు",
        actCapsuleCreated: "ఒక మెమరీ క్యాప్సూల్‌ను సృష్టించారు",
        actAchievementAdded: "ఒక విజయాన్ని చేర్చారు",
        actDefault: "ఒక అప్డేట్ చేశారు",
        taJustNow: "ఇప్పుడే",
        taMinAgo: "{n}ని. క్రితం",
        taHourAgo: "{n}గం. క్రితం",
        taDayAgo: "{n}రోజుల క్రితం",
        gsTitle: "ప్రారంభిద్దాం",
        gsProgress: "{done}/{total} పూర్తయ్యాయి",
        gsAddMember: "ఒక కుటుంబ సభ్యుడిని చేర్చండి",
        gsUploadMemory: "ఒక జ్ఞాపకాన్ని అప్లోడ్ చేయండి",
        gsRecordStory: "ఒక కథను రికార్డు చేయండి",
        gsInviteFamily: "కుటుంబాన్ని ఆహ్వానించండి",
        gsSecureDoc: "ఒక పత్రాన్ని సురక్షితంగా ఉంచండి",
        gsCelebrateTitle: "ప్రారంభ దశ పూర్తైంది!",
        gsCelebrateBody: "అన్ని 5 దశలు పూర్తయ్యాయి — మీ కుటుంబం యొక్క డిజిటల్ వారసత్వం అధికారికంగా ప్రారంభమైంది. మీ వంశవృక్షాన్ని ఇలానే పెంచుకోండి!",
        actProfileUpdated: "తమ ప్రొఫైల్‌ను నవీకరించారు",
        daySun: "ఆది",
        dayMon: "సోమ",
        dayTue: "మంగళ",
        dayWed: "బుధ",
        dayThu: "గురు",
        dayFri: "శుక్ర",
        daySat: "శని",
        hbTitle: "కుటుంబం — అన్నీ ఒకే చోట",
        hbCategories: "విభాగాలు",
        hbSearchAll: "అన్ని ఫీచర్లలో వెతకండి…",
        hbNoMatch: "ఫీచర్లు ఏవీ సరిపోలలేదు",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "త్వరలో",
        hbNotifyMe: "నాకు తెలియజేయండి",
        hbNotified: "సిద్ధమైనప్పుడు తెలపబడుతుంది",
        hbNotifyTitle: "\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\" ప్రత్యక్షంలోకి వచ్చినప్పుడు మేము మీకు తెలియజేస్తాము",
        hbNotifyBody: "మీ ఆసక్తిని నమోదు చేశాము. ఎక్కువ కుటుంబాలు కోరిన ఫీచర్లను మా బృందం ముందుగా ప్రాధాన్యత ఇస్తుంది.",
        hbGreat: "బాగుంది",
        rcSearch: "వెతకండి…",
        rcExportCsv: "CSV ఎగుమతి",
        rcAddNew: "కొత్తది జోడించండి",
        rcCreate: "సృష్టించండి",
        rcEdit: "మార్చండి",
        rcSave: "సేవ్ చేయండి",
        rcCancel: "రద్దు చేయండి",
        rcLoading: "లోడ్ అవుతోంది…",
        rcNoYet: "ఇప్పటికీ {label} లేదు",
        rcClick: "క్లిక్ చేయండి",
        rcFirstEntry: "మీ మొదటి నమోదు జోడించడానికి.",
        rcNew: "కొత్తది",
        rcFormHint: "కింద ఉన్న ఖాళీలను పూరించండి — తప్పనిసరి ఖాళీలను ఎరుపు నక్షత్రంతో గుర్తించారు.",
        rcHealthBold: "కేవలం కుటుంబ రికార్డు కోసం మాత్రమే",
        rcHealthRest: "— నిర్ధారణ, చికిత్స లేదా వైద్య సలహా కోసం కాదు. కుటుంబ సభ్యుల అనుమతితో ఆరోగ్య రికార్డులను కుటుంబ అధిపతి మాత్రమే జోడించవచ్చు లేదా మార్చవచ్చు.",
        rcConfirmTitle: "దయచేసి నిర్ధారించండి",
        rcConfirmDelete: "ఈ రికార్డును తొలగించాలా?",
        rcUpdated: "నవీకరించబడింది.",
        rcSaved: "దాచబడింది.",
        rcCreated: "సృష్టించబడింది.",
        rcDeleted: "తొలగించబడింది.",
        rcChoose: "ఎంచుకోండి…",
        rcUnknownSection: "తెలియని విభాగం",
        rcSectionMissing: "ఈ కుటుంబ రికార్డుల విభాగం లేదు.",
        rcBackToHub: "కుటుంబ హబ్‌కి తిరిగి వెళ్లండి",
        rcWatchDemo: "డెమో చూడండి",
        rcDemoSoon: "డెమో త్వరలో రాబోతోంది",
        rcFamilyService: "కుటుంబ సేవ",
        rcUntitled: "(శీర్షిక లేదు)",
        setTitle: "సెట్టింగ్‌లు & గోప్యత",
        setBack: "వెనక్కి",
        setTabInvites: "ఆహ్వానాలు",
        setTabSecurity: "భద్రత",
        setTabData: "నా డేటా",
        setTabActivity: "కార్యకలాపం",
        setTabHelp: "సహాయం",
        setMyAccount: "నా ఖాతా",
        setMyAccountSub: "రసీదులు, స్వాగత మెయిల్ మరియు సహాయ సమాధానాలు పొందడానికి మీ ఈమెయిల్‌ను జోడించండి. మీ ఫోన్ నంబర్ మీ లాగిన్‌గా ఉంటుంది మరియు ఇక్కడ మార్చలేరు.",
        setName: "పేరు",
        setEmail: "ఈమెయిల్",
        setEmailInvalid: "దయచేసి సరైన ఈమెయిల్ చిరునామాను ఇవ్వండి.",
        setSaveChanges: "మార్పులను సేవ్ చేయండి",
        setSaving: "సేవ్ చేస్తున్నారు…",
        setSaved: "ఖాతా వివరాలు సేవ్ అయ్యాయి.",
        setFamilyMembers: "కుటుంబ సభ్యులు",
        setRolesHint: "అడ్మిన్: పూర్తి నియంత్రణ · ఎడిటర్: సభ్యులను చేర్చడం/మార్చడం · వీయర్: చదవటానికి మాత్రమే",
        setYou: "(మీరు)",
        setRoleUpdated: "పాత్ర అప్డేట్ చేయబడింది.",
        setActiveInvites: "సక్రియ ఆహ్వానాలు",
        setInvitesSub: "వృక్షంలో సభ్యుడి ప్రొఫైల్ డ్రాయర్ నుంచి ఆహ్వానాలు పంపండి. ఎప్పుడైనా రద్దు చేయవచ్చు.",
        setNoActiveInvites: "సక్రియ ఆహ్వానాలు లేవు.",
        setRevoke: "రద్దు చేయండి",
        setExpires: "గడువు ముగియే తేదీ",
        setOnlyAdmins: "కేవలం ఫ్యామిలీ నిర్వాహకులే ఆహ్వానాలను నిర్వహించగలరు.",
        setInviteRevoked: "ఆహ్వానం రద్దు చేయబడింది.",
        setSeniorSub: "గోప్యత ఎంపికలను 3 పెద్ద ఎంపికలుగా సరళీకరిస్తుంది: ప్రైవేట్ / ఫ్యామిలీ / పబ్లిక్.",
        setWelcomeTour: "స్వాగత యాత్ర",
        setWelcomeTourSub: "3-దశల పరిచయాన్ని మళ్లీ చూడండి: Family Tree → Memories → Vault.",
        setReplayTour: "యాత్రను మళ్లీ చూడండి",
        setLivingIn: "ఎక్కడ ఉంటున్నారు",
        setLivingInSub: "ఐచ్చికం — NRI కుటుంబాలకు మెరుగ్గా సేవ ఇవ్వడానికి మాకు సహాయపడుతుంది.",
        setIndia: "భారతదేశం",
        setAbroad: "విదేశం (NRI)",
        setSelect: "ఎంచుకోండి",
        setWhatWeStore: "మీ గురించి మేము ఏం భద్రపరుస్తాము",
        setDpdpNote: "DPDP చట్టం: సంపూర్ణ పారదర్శకత, ఎప్పుడూ.",
        setProfileSections: "ప్రొఫైల్ విభాగాలు",
        setVaultDocs: "వాల్ట్ పత్రాలు",
        setInvitesSent: "ఆహ్వానాలు పంపబడ్డాయి",
        setActivityEntries: "క్రియల నమోదు",
        setDownloadData: "నా మొత్తం డాటాను డౌన్‌లోడ్ చేయండి (JSON)",
        setDeleteTitle: "నా ఖాతా & డాటా తొలగించండి",
        setDeleteSub: "మీ ఖాతా, మీ కథలు, వాల్ట్ పత్రాలు మరియు క్రియల లాగ్ శాశ్వతంగా తొలగించబడతాయి. మీరు ఇతరుల గురించి సృష్టించిన కుటుంబ వృక్షం నమోదులు కుటుంబంలోనే కొనసాగుతాయి.",
        setDeleteEverything: "అన్నీ తొలగించండి",
        setDeleteConfirmTitle: "మీ ఖాతాను శాశ్వతంగా తొలగించాలా?",
        setDeleteConfirmBody: "ఇది తిరిగి చేయలేము. మీ కథలు, పత్రాలు మరియు ఖాతా DPDP చట్టం ప్రకారం తొలగించబడతాయి.",
        setDeleting: "తొలగిస్తున్నాం…",
        setYesDelete: "అవును, అన్నీ తొలగించండి",
        setActivityLog: "క్రియల లాగ్",
        setAuditAdminSub: "మీ కుటుంబంలో ఎవరు ఏమి చూశారు, ఏమి మార్చారు.",
        setAuditOwnSub: "మీ స్వంత క్రియలు.",
        setNoActivityYet: "ఇంకా ఏ క్రియలు లేవు.",
        setSearchFamily: "కుటుంబాన్ని వెతకండి…",
        ptsEyebrow: "మా డిజైన్ స్టూడియో నుంచి",
        ptsTitle: "అందమైన కుటుంబ పోస్టర్ టెంప్లేట్లు",
        ptsSub: "మా స్టూడియో నుంచి చేతివృత్తితో రూపొందించిన పోస్టర్ డిజైన్‌లు — మీ కుటుంబ ఫోటోలు వాటిలో అద్బుతంగా సరిపోతాయి. మీరు సైన్ అప్ అయిన తర్వాత మీకు నచ్చిన డిజైన్‌ని ఎంచుకోండి.",
        ptsCta: "మీ కుటుంబ పోస్టర్‌ను సృష్టించండి",
        ptsPremium: "ప్రీమియం",
        hcTitle: "సహాయ కేంద్రం",
        hcSub: "ఏమైనా అడగండి — మేము సాధారణంగా కొన్ని గంటల్లో ప్రత్యుత్తరం ఇస్తాము.",
        hcAskTitle: "ఒక ప్రశ్న అడగండి",
        hcAskSub: "మీ సమస్యను వివరించండి, మా సపోర్ట్ బృందం మీకు తిరిగి స్పందిస్తుంది.",
        hcSubject: "విషయం",
        hcSubjectPh: "ఉదా: నేను ఫోటో అప్లోడ్ చేయలేకపోతున్నాను",
        hcMessage: "మీ ప్రశ్న",
        hcMessagePh: "ఏం జరిగిందో దశలవారీగా చెప్పండి…",
        hcSubmit: "ప్రశ్నను పంపండి",
        hcSubmitting: "పంపుతున్నాం…",
        hcSuccess: "మీ ప్రశ్న మాకు చేరింది — మేము త్వరలో ప్రత్యుత్తరం ఇస్తాము.",
        hcFailed: "మీ ప్రశ్నను పంపలేకపోయాము. దయచేసి మళ్లీ ప్రయత్నించండి.",
        hcFillBoth: "దయచేసి విషయం మరియు మీ ప్రశ్న రెండూ పూరించండి.",
        hcMyTickets: "నా ప్రశ్నలు",
        hcNoTickets: "ఇప్పటివరకు ఎలాంటి ప్రశ్నలు లేవు — పైన మీ మొదటి ప్రశ్నను అడగండి.",
        hcStatusOpen: "తెరిచి ఉంది",
        hcStatusResolved: "పరిష్కరించబడింది",
        hcSupportReply: "సపోర్ట్",
        hcQuickHelp: "త్వరిత సహాయం",
        hcWhatsapp: "WhatsApp సపోర్ట్ — వెంటనే చాట్",
        hcCall: "కాల్: +91-99999-99999 (సోమ–శని 10–7)",
        hcFaq: "FAQ — తరచుగా అడిగే ప్రశ్నలు",
        hcTipTitle: "సూచన",
        hcTipBody: "చాలా సమాధానాలు ఇప్పటికే FAQ లో ఉన్నాయి. మీ కుటుంబానికి సంబంధించిన వ్యక్తిగత విషయాల కోసం ఇక్కడ అడగండి — దీన్ని మా సపోర్ట్ బృందం మాత్రమే చూస్తుంది.",
        demoAddPhoto: "ఫోటో జోడించండి (ఐచ్చికం)",
        demoChangePhoto: "ఫోటో మార్చండి",
        demoDownloadPoster: "పోస్టర్‌ను డౌన్‌లోడ్ చేయండి",
        demoDownloading: "సిద్ధం చేస్తున్నాం…",
    },
    kn: {
        tourListen: "ಕೇಳಿ",
        tourStop: "ನಿಲ್ಲಿಸಿ",
        tourEyebrow: "ವಂಶ್‌ಟ್ರೀಗೆ ಸುಸ್ವಾಗತ",
        tourStepOf: "ಹಂತ {n} / {total}",
        tourNext: "ಮುಂದೆ",
        tourStart: "ಪ್ರಾರಂಭಿಸಿ",
        tourS1Title: "ನಿಮ್ಮ ವಂಶವೃಕ್ಷವನ್ನು ನಿರ್ಮಿಸಿ",
        tourS1Body: "ಪೋಷಕರು, ಅಜ್ಜ-ಅಜ್ಜಿ ಮತ್ತು ಮಕ್ಕಳನ್ನು ಸೇರಿಸಿ ಪ್ರಾರಂಭಿಸಿ. ನೀವು ಸೇರಿಸುವ ಪ್ರತಿಯೊಬ್ಬರೂ ನಿಮ್ಮ ಕುಟುಂಬದ ಕಥೆಯ ಜೀವಂತ ಶಾಖೆಯಾಗುತ್ತಾರೆ.",
        tourS1Cta: "ವಂಶವೃಕ್ಷ ತೆರೆಯಿರಿ",
        tourS2Title: "ನೆನಪುಗಳನ್ನು ಕಾಪಾಡಿ",
        tourS2Body: "ಫೋಟೋಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ, ಧ್ವನಿಗಳನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ, ಕಥೆಗಳನ್ನು ಬರೆಯಿರಿ. ಮುಂದಿನ ಪೀಳಿಗೆಗಳು ಮತ್ತೆ ಅನುಭವಿಸುವಂತೆ ಆಲ್ಬಮ್‌ಗಳಲ್ಲಿ ಜೋಡಿಸಿ.",
        tourS2Cta: "ನೆನಪುಗಳು ತೆರೆಯಿರಿ",
        tourS3Title: "ವಾಲ್ಟ್ ಸುರಕ್ಷಿತಗೊಳಿಸಿ",
        tourS3Body: "ಉಯಿಲುಗಳು, ಆಸ್ತಿ ದಾಖಲೆಗಳು ಮತ್ತು ಕಾನೂನು ದಾಖಲೆಗಳನ್ನು ಬ್ಯಾಂಕ್-ದರ್ಜೆಯ ಭದ್ರತೆಯಲ್ಲಿ ಇರಿಸಿ — ಸರಿಯಾದ ಸಮಯದಲ್ಲಿ ಮುಂದಿನ ಪೀಳಿಗೆಗೆ ಹಸ್ತಾಂತರಿಸಿ.",
        tourS3Cta: "ವಾಲ್ಟ್ ತೆರೆಯಿರಿ",
        back: "ಹಿಂದೆ",
        cancel: "ರದ್ದುಮಾಡಿ",
        delete: "ಅಳಿಸಿ",
        logout: "ಲಾಗ್‌ಔಟ್",
        optional: "(ಐಚ್ಛಿಕ)",
        tagline: "ನಿಮ್ಮ ಕುಟುಂಬ. ಶಾಶ್ವತವಾಗಿ.",
        familyLegacy: "ಕುಟುಂಬದ ಪರಂಪರೆ",
        signInTitle: "Family Repository ಗೆ ಸೈನ್ ಇನ್ ಮಾಡಿ",
        otpTitle: "ನಿಮ್ಮ OTP ನಮೂದಿಸಿ",
        phoneSub: "ನಾವು ನಿಮ್ಮ ಮೊಬೈಲ್‌ಗೆ OTP ಕಳುಹಿಸುತ್ತೇವೆ.",
        otpSentTo: "OTP ಕಳುಹಿಸಲಾಗಿದೆ",
        expiresIn: "ಇದು ಮುಕ್ತಾಯವಾಗುತ್ತದೆ",
        mobileNumber: "ಮೊಬೈಲ್ ಸಂಖ್ಯೆ",
        phoneHint: "ಭಾರತೀಯ ಸಂಖ್ಯೆಗಳು ಮಾತ್ರ (6–9ರಿಂದ ಆರಂಭ).",
        yourName: "ನಿಮ್ಮ ಹೆಸರು",
        firstTimeOnly: "(ಮೊದಲ ಬಾರಿ ಮಾತ್ರ)",
        haveInviteCode: "ಕುಟುಂಬ ಆಮಂತ್ರಣ ಕೋಡ್ ಇದೆಯೇ?",
        apply: "ಅನ್ವಯಿಸಿ",
        sendOtp: "OTP ಕಳುಹಿಸಿ",
        sendingOtp: "OTP ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…",
        otpLabel: "6 ಅಂಕಿಯ OTP",
        verifyContinue: "ದೃಢೀಕರಿಸಿ ಮುಂದುವರಿಸಿ",
        verifying: "ದೃಢೀಕರಿಸುತ್ತಿದೆ…",
        changeNumber: "ಸಂಖ್ಯೆ ಬದಲಾಯಿಸಿ",
        resendOtp: "OTP ಮರು-ಕಳುಹಿಸಿ",
        resendIn: "ಮರು-ಕಳುಹಿಸಿ",
        terms: "ಮುಂದುವರಿಯುವ ಮೂಲಕ ನೀವು Family Repository ಸೇವಾ ನಿಯಮಗಳು ಮತ್ತು ಗೌಪ್ಯತಾ ನೀತಿಗೆ ಒಪ್ಪುತ್ತೀರಿ.",
        trustStrip: "ಎನ್‌ಕ್ರಿಪ್ಟೆಡ್ • ಕುಟುಂಬ-ನಿಯಂತ್ರಿತ • ಭಾರತದಲ್ಲಿ ತಯಾರಿಸಲಾಗಿದೆ",
        welcome: "ಸ್ವಾಗತ",
        dashSub: "ನಿಮ್ಮ Family Repository ಸಿದ್ಧವಾಗಿದೆ. ಮೊದಲ ಸದಸ್ಯನನ್ನು ಸೇರಿಸಿ ಅಥವಾ ಹಿರಿಯರ ಕಥೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ.",
        quickActions: "ತ್ವರಿತ ಕ್ರಿಯೆಗಳು",
        footerNote: "ನಿಮ್ಮ ಡೇಟಾ ಖಾಸಗಿ, ಎನ್‌ಕ್ರಿಪ್ಟೆಡ್ ಮತ್ತು ಕುಟುಂಬ-ನಿಯಂತ್ರಿತವಾಗಿದೆ. ಭಾರತಕ್ಕಾಗಿ — ಯಾವಾಗಲೂ.",
        roleFamilyHead: "ಕುಟುಂಬದ ಮುಖ್ಯಸ್ಥ",
        roleAdmin: "ಆಡ್ಮಿನ್",
        roleEditor: "ಸಂಪಾದಕ",
        roleViewer: "ವೀಕ್ಷಕ",
        roleMember: "ಸದಸ್ಯ",
        cardTreeTitle: "ಕುಟುಂಬ ವೃಕ್ಷವನ್ನು ನೋಡಿ",
        cardTreeDesc: "ಪೀಳಿಗೆಗಳಲ್ಲಿ ವಿಸ್ತರಿಸಿರುವ ನಿಮ್ಮ ಸಂಪೂರ್ಣ ಕುಟುಂಬವನ್ನು ನೋಡಿ.",
        cardAddTitle: "ಸದಸ್ಯ ಸೇರಿಸಿ",
        cardAddDesc: "ಪೋಷಕರು, ಒಡಹುಟ್ಟಿದವರು, ಮಕ್ಕಳು ಅಥವಾ ಸಂಬಂಧಿಕರನ್ನು ಸೇರಿಸಿ.",
        cardMemoriesTitle: "ನೆನಪುಗಳು",
        cardMemoriesDesc: "ಕುಟುಂಬದ ಕ್ಷಣಗಳ ಫೋಟೋಗಳನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ.",
        cardStoryTitle: "ಕಥೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ",
        cardStoryDesc: "ಹಿರಿಯರ ಧ್ವನಿಯನ್ನು ಶಾಶ್ವತವಾಗಿ ಉಳಿಸಿ.",
        cardVaultTitle: "ದಾಖಲೆ ವಾಲ್ಟ್",
        cardVaultDesc: "ಉಯಿಲುಗಳು, ದಾಖಲೆಗಳು — ಸುರಕ್ಷಿತ, ನಿಮ್ಮ ಷರತ್ತುಗಳ ಮೇಲೆ.",
        cardSettingsTitle: "ಗೌಪ್ಯತೆ & ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
        cardSettingsDesc: "ಪಾತ್ರಗಳು, ಆಮಂತ್ರಣಗಳು, ನಿಮ್ಮ ಡೇಟಾ ಮತ್ತು ಚಟುವಟಿಕೆ ಲಾಗ್.",
        memories: "ನೆನಪುಗಳು",
        addMemory: "ನೆನಪನ್ನು ಸೇರಿಸಿ",
        memoriesEmpty: "ಇನ್ನೂ ನೆನಪುಗಳಿಲ್ಲ",
        memoriesEmptySub: "ಮೊದಲ ಕುಟುಂಬದ ಫೋಟೋವನ್ನು ಅಪ್‌ಲೋಡ್ ಮಾಡಿ — ಮದುವೆಗಳು, ಹಬ್ಬಗಳು, ದಿನನಿತ್ಯದ ಸಂತೋಷ.",
        caption: "ಶೀರ್ಷಿಕೆ",
        captionPlaceholder: "ಉದಾ. ಅಜ್ಜಿಯ ಮನೆಯಲ್ಲಿ ದೀಪಾವಳಿ, 1998",
        memoryDate: "ಇದು ಯಾವಾಗ?",
        tagMember: "ಕುಟುಂಬದ ಸದಸ್ಯನನ್ನು ಟ್ಯಾಗ್ ಮಾಡಿ",
        choosePhoto: "ಫೋಟೋ ಆರಿಸಿ",
        changePhoto: "ಫೋಟೋ ಬದಲಾಯಿಸಿ",
        uploadMemory: "ನೆನಪು ಉಳಿಸಿ",
        uploading: "ಅಪ್‌ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
        deleteMemoryConfirm: "ಈ ನೆನಪನ್ನು ಅಳಿಸಬೇಕೇ? ಇದನ್ನು ಹಿಂಪಡೆಯಲಾಗುವುದಿಲ್ಲ.",
        confirmDelete: "ದೃಢೀಕರಿಸಲು ಮತ್ತೆ ಟ್ಯಾಪ್ ಮಾಡಿ",
        uploadedBy: "ಸೇರಿಸಿದವರು",
        heroEyebrow: "ನಿಮ್ಮ ಕುಟುಂಬ · ನಿಮ್ಮ ಪರಂಪರೆ",
        heroHeadline1: "ನಿಮ್ಮ ಕುಟುಂಬದ ಇತಿಹಾಸ",
        heroHeadlineAccent: "ಸಂಗ್ರಹಿತ",
        heroHeadline2: "ಯುಗಯುಗಾಂತರಕ್ಕೂ.",
        heroSubline: "ನಿಮ್ಮ ಕುಟುಂಬವೃಕ್ಷವನ್ನು ರಚಿಸಿ, ಹಿರಿಯರ ಧ್ವನಿಯನ್ನು ದಾಖಲೆ ಮಾಡಿ, ಫೋಟೋಗಳು ಮತ್ತು ದಾಖಲೆಗಳನ್ನು ಸಂಗ್ರಹಿಸಿ — ಇವೆಲ್ಲವನ್ನೂ ಭಾರತೀಯ ಕುಟುಂಬಗಳಿಗೆ, ನಿಮ್ಮದೇ ಭಾಷೆಯಲ್ಲಿ, ಮಾಡಿರುವ ಖಾಸಗಿ ಜಾಗದಲ್ಲಿ ಇಟ್ಟುಕೊಳ್ಳಿ.",
        heroCtaPrimary: "ಮೊಬೈಲ್ OTP ಮೂಲಕ ಉಚಿತವಾಗಿ ಆರಂಭಿಸಿ",
        heroCtaSecondary: "ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ ನೋಡಿ",
        heroTrustPrivate: "ತಾನಾಗಿಯೇ ಖಾಸಗಿ",
        heroTrustLangs: "10 ಭಾರತೀಯ ಭಾಷೆಗಳು",
        heroTrustFree: "ಆರಂಭಿಸಲು ಉಚಿತ",
        heroSocialProof: "12,500+ ಭಾರತೀಯ ಕುಟುಂಬಗಳು ನಂಬಿರುವ ಸೇವೆ",
        heroBadge10: "10+",
        heroBadge10Cap: "ಭಾರತೀಯ ಭಾಷೆಗಳು",
        heroStoryAudio: "ಆಡಿಯೋ ಕಥೆಗಳು",
        heroStoryPhotos: "ಫೋಟೋಗಳು",
        heroStoryDocs: "ದಾಖಲೆಗಳು",
        statHappyFamilies: "ಸಂತೋಷಿ ಕುಟುಂಬಗಳು",
        statTreesCreated: "ಸಂಗ್ರಹಿಸಿದ ಕುಟುಂಬ ಸದಸ್ಯರು",
        statStoriesRecorded: "ಕಥೆಗಳು ಮತ್ತು ನೆನಪುಗಳು",
        statDataPrivacy: "ಡೇಟಾ ಗೌಪ್ಯತೆ",
        demoBadge: "ಸೈನ್ ಅಪ್ ಇಲ್ಲದೆ ಪ್ರಯತ್ನಿಸಿ",
        demoHeadline: "ನಿಮ್ಮ ಕುಟುಂಬ ವೃಕ್ಷವು 30 ಸೆಕೆಂಡ್‌ಗಳಲ್ಲಿ ಜೀವಂತವಾಗುವುದನ್ನು ನೋಡಿ",
        demoSubline: "6 ಹೆಸರುಗಳನ್ನು ಸೇರಿಸಿ. ನಿಮ್ಮ 6 ತಲೆಮಾರದ ಕುಟುಂಬ ವೃಕ್ಷವು ಇಲ್ಲೇ ಚಲಿಸುವುದನ್ನು ನೋಡಿ.",
        demoTrustNoPhone: "ಫೋನ್ ಬೇಡ",
        demoTrustNoOtp: "OTP ಬೇಡ",
        demoTrustNoSignup: "ಸೈನ್ ಅಪ್ ಬೇಡ",
        demoStepPrefix: "ಹಂತ",
        demoStepOf: "ಒಟ್ಟು 6 ರಲ್ಲಿ —",
        demoLabelFather: "ತಂದೆಯ ಹೆಸರು",
        demoPhFather: "ಉದಾ: ರಾಜೇಶ್ ಶರ್ಮಾ",
        demoLabelGrandfather: "ಅಜ್ಜನ ಹೆಸರು",
        demoPhGrandfather: "ಉದಾ: ಮೋಹನ್ ಶರ್ಮಾ",
        demoLabelGreatGrand: "ಮಹಾ ಅಜ್ಜನ ಹೆಸರು",
        demoPhGreatGrand: "ಉದಾ: ಕಿಶನ್ ಶರ್ಮಾ",
        demoLabelChild: "ಮಗ / ಮಗಳ ಹೆಸರು",
        demoPhChild: "ಉದಾ: ಅನನ್ಯಾ ಶರ್ಮಾ",
        demoLabelGrandchild: "ಮೊಮ್ಮಗ / ಮೊಮ್ಮಗಳ ಹೆಸರು",
        demoPhGrandchild: "ಉದಾ: ವಿಹಾನ್ ಶರ್ಮಾ",
        roleFather: "ತಂದೆ",
        roleGrandfather: "ಅಜ್ಜ",
        roleGreatGrandfather: "ದೊಡ್ಡ ಅಜ್ಜ",
        roleChild: "ಮಗ/ಮಗಳು",
        roleGrandchild: "ಮೊಮ್ಮಗ/ಮೊಮ್ಮಗಳು",
        demoLabelYou: "ನಿಮ್ಮ ಹೆಸರು",
        demoLabelParent: "ನಿಮ್ಮ ತಂದೆ/ತಾಯಿಯ ಹೆಸರು",
        demoLabelGrand: "ನಿಮ್ಮ ಅಜ್ಜ/ಅಜ್ಜಿಯ ಹೆಸರು",
        demoPhYou: "ಉದಾ. ರಾಹುಲ್ ಶರ್ಮಾ",
        demoPhParent: "ಉದಾ. ಸೂರೇಶ್ ಶರ್ಮಾ",
        demoPhGrand: "ಉದಾ. ರಾಮಚಂದ್ರ ಶರ್ಮಾ",
        demoHint: "ನಿಮ್ಮ ವಂಶವೃಕ್ಷ ಜೀವಂತವಾಗಿ ಕಾಣಲು ಇನ್ನೂ {n} ಹೆಸರನ್ನು ಸೇರಿಸಿ.",
        demoNext: "ಮುಂದೆ",
        demoSeeTree: "ನಿಮ್ಮ ವಂಶವೃಕ್ಷ ನೋಡಿರಿ",
        demoRestart: "ಮತ್ತೊಮ್ಮೆ ಪ್ರಯತ್ನಿಸಿ",
        demoStartReal: "ನನ್ನ ನಿಜವಾದ ವಂಶವೃಕ್ಷವನ್ನು ಪ್ರಾರಂಭಿಸು (ಉಚಿತ)",
        demoTreeBadge: "ನಿಮ್ಮ ಚಿಕ್ಕ ವಂಶವೃಕ್ಷ",
        demoChooseDesign: "ಪೋಸ್ಟರ್ ವಿನ್ಯಾಸ ಆಯ್ಕೆಮಾಡಿ",
        demoClassic: "ಕ್ಲಾಸಿಕ್",
        demoFamilyTitle: "{name} ಕುಟುಂಬ",
        demoShareWhatsApp: "WhatsApp ನಲ್ಲಿ ಹಂಚಿಕೊಳ್ಳಿ",
        demoShareText: "ನಾನು 30 ಸೆಕೆಂಡುಗಳಲ್ಲಿ ನಮ್ಮ ವಂಶವೃಕ್ಷ ಮಾಡಿದೆ! 🌳 ನೀವೂ ಉಚಿತವಾಗಿ ಮಾಡಿ: {link}",
        demoScanToJoin: "ಸ್ಕ್ಯಾನ್ ಮಾಡಿ ಉಚಿತವಾಗಿ ಮಾಡಿ",
        demoTreeHeadline: "ಆರು ತಲೆಮಾರು, ಒಂದು ಜೀವಂತ ವೃಕ್ಷ.",
        demoTreeAddMore: "20ಕ್ಕಿಂತ ಹೆಚ್ಚು ಬಂಧುಗಳು, ಫೋಟೋಗಳು ಮತ್ತು ನೆನಪುಗಳನ್ನು ಸೇರಿಸಿ — ಉಚಿತ.",
        demoFooterPrivacy: "ನಿಮ್ಮ ಡೆಮೋ ಫಲಿತಾಂಶಗಳು ನಿಮ್ಮ ಬ್ರೌಸರ್‌ನಲ್ಲೇ ಇರುತ್ತವೆ. ಏನನ್ನೂ ಉಳಿಸಲಾಗುವುದಿಲ್ಲ, ಕಳುಹಿಸಲಾಗುವುದಿಲ್ಲ.",
        roleGrandparent: "ಅಜ್ಜ/ಅಜ್ಜಿ",
        roleParent: "ತಂದೆ/ತಾಯಿ",
        roleYou: "ನೀವು",
        testHeadline: "ಭಾರತದ ಅನೇಕ ಕುಟುಂಬಗಳು ನಮ್ಮ ಮೇಲೆ ಭರವಸೆ ಇಟ್ಟಿವೆ",
        testSubline: "ನಿಜವಾದ ಕುಟುಂಬಗಳು, ನಿಜವಾದ ಜೀವನಕಥೆಗಳು — ಸದಾಕಾಲ ಉಳಿಯುವಂತೆ.",
        testShareBtn: "ನಿಮ್ಮ ಕುಟುಂಬ ಕಥೆಯನ್ನು ಹಂಚಿಕೊಳ್ಳಿ",
        testShareHint: "Family Repository ನಿಮ್ಮ ಕುಟುಂಬಕ್ಕೆ ಸಹಾಯವಾದ್ದೇ? ನಿಮ್ಮ ಕಥೆಯನ್ನು ಹಂಚಿಕೊಳ್ಳಿ.",
        noOtpTitle: "Family Repository ನಿಮ್ಮ OTP ಅನ್ನು ಎಂದಿಗೂ ಕೇಳುವುದಿಲ್ಲ.",
        noOtpBody: "ಯಾರಾದರೂ ಕರೆ ಮಾಡಿ ಅಥವಾ ಸಂದೇಶ ಕಳುಹಿಸಿ OTP ಕೇಳಿದರೆ — ತಕ್ಷಣ ಸಂಪರ್ಕವನ್ನು ಕಡಿತಗೊಳಿಸಿ. ನಾವು ಅದನ್ನು ಎಂದಿಗೂ ಕೇಳುವುದಿಲ್ಲ.",
        navLogin: "ಲಾಗಿನ್",
        navStartFree: "ಉಚಿತವಾಗಿ ಆರಂಭಿಸಿ",
        navDashboard: "ಡ್ಯಾಶ್ಬೋರ್ಡ್",
        tnHome: "ಮನೆ",
        tnFamilyTree: "ಕುಟುಂಬ ವಂಶವೃಕ್ಷ",
        tnFamilyHub: "ಫ್ಯಾಮಿಲಿ ಹಬ್",
        tnEvents: "ಕಾರ್ಯಕ್ರಮಗಳು",
        tnPackages: "ಪ್ಯಾಕೇಜುಗಳು",
        tnVault: "ವಾಲ್ಟ್",
        tnReports: "ವರದಿಗಳು",
        tnMore: "ಇನ್ನಷ್ಟು",
        tnRecords: "ದಾಖಲೆಗಳು",
        tnSupport: "ಸಹಾಯ",
        sbDashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
        sbMemberProfile: "ಸದಸ್ಯರ ಪ್ರೊಫೈಲ್",
        sbgConnections: "ಸಂಪರ್ಕಗಳು",
        sbConnectFriends: "ವಿಶಾಲ ಕುಟುಂಬಗಳೊಂದಿಗೆ ಸಂಪರ್ಕಿಸಿ",
        sbConnectRelatives: "ಸನಿಹದ ಕುಟುಂಬಗಳೊಂದಿಗೆ ಸಂಪರ್ಕಿಸಿ",
        sbWorldMap: "ವಿಶ್ವ ನಕ್ಷೆ",
        sbgMemories: "ಸ್ಮೃತಿಗಳು",
        sbPhotoMemories: "ಫೋಟೋ ಸ್ಮೃತಿಗಳು",
        sbAudioMemories: "ಆಡಿಯೋ ಸ್ಮೃತಿಗಳು",
        sbVideoMemories: "ವೀಡಿಯೋ ಸ್ಮೃತಿಗಳು",
        sbgEvents: "ಕಾರ್ಯಕ್ರಮಗಳು ಮತ್ತು ಜ್ಞಾಪನೆಗಳು",
        sbFamilyCalendar: "ಕುಟುಂಬ ಕ್ಯಾಲೆಂಡರ್",
        sbTodaysCelebration: "ಇಂದು ಆಚರಣೆ",
        sbFamilyTimeline: "ವೈಯಕ್ತಿಕ ಜೀವನ ಪ್ರಯಾಣ",
        sbSmartReminder: "ಸ್ಮಾರ್ಟ್ ಜ್ಞಾಪನೆ",
        sbgHealth: "ಆರೋಗ್ಯ ಮತ್ತು ವೈದ್ಯಕೀಯ ಸಂಗ್ರಹ",
        sbMedicalHistory: "ವೈದ್ಯಕೀಯ ಇತಿಹಾಸ",
        sbChronicDiseases: "ದೀರ್ಘಕಾಲೀನ / ವಂಶಪಾರಂಪರ್ಯ ರೋಗಗಳು",
        sbCancerRegistry: "ಕ್ಯಾನ್ಸರ್ ನೋಂದಣಿ",
        sbGeneticCongenital: "ಜನ್ಯ ಮತ್ತು ಜನನಸಹಜ",
        sbAllergies: "ಅಲರ್ಜಿ ಮತ್ತು ಅಸಹಿಷ್ಣುತೆಗಳು",
        sbVaccination: "ಲಸಿಕೆ ದಾಖಲೆಗಳು",
        sbLifestyle: "ಜೀವನಶೈಲಿ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
        sbPregnancyBirth: "ಗರ್ಭಧಾರಣೆ ಮತ್ತು ಜನನ ಇತಿಹಾಸ",
        sbOrganDonation: "ಅಂಗ ದಾನ ಆಯ್ಕೆ",
        sbBloodGroup: "ರಕ್ತ ಗುಂಪು ನೋಂದಣಿ",
        sbgVault: "ವೈಯಕ್ತಿಕ ಭಂಡಾರ",
        sbDocumentVault: "ದಾಖಲೆ ಭಂಡಾರ",
        sbLegalVault: "ಕಾನೂನು ಭಂಡಾರ",
        sbgDisplay: "ಪ್ರದರ್ಶನ ಮತ್ತು ಡೌನ್‌ಲೋಡ್",
        sbDownloadExport: "ಡೌನ್‌ಲೋಡ್ ಮತ್ತು ಎಕ್ಸ್‌ಪೋರ್ಟ್",
        sbDigitalFamilyTree: "ಡಿಜಿಟಲ್ ಕುಟಂಬ ವೃಕ್ಷ",
        sbPackagesPlans: "ಪ್ಯಾಕೇಜುಗಳು ಮತ್ತು ಯೋಜನೆಗಳು",
        sbPrivacySettings: "ಗೌಪ್ಯತೆ ಮತ್ತು ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
        hdrDashboard: "ಡ್ಯಾಶ್‌ಬೋರ್ಡ್",
        hdrMenu: "ಮೆನು",
        hdrMyProfile: "ನನ್ನ ಪ್ರೊಫೈಲ್",
        hdrSettings: "ಸೆಟ್ಟಿಂಗ್‌ಗಳು",
        hdrSignOut: "ಸೈನ್ ಔಟ್",
        signOutTitle: "Family Repository ನಿಂದ ಸೈನ್ ಔಟ್ ಆಗಬೇಕೆ?",
        signOutBody: "ನೀವು ಯಾವಾಗ ಬೇಕಾದರೂ ನಿಮ್ಮ ಮೊಬೈಲ್ OTP ಬಳಸಿ ಮರು ಲಾಗಿನ್ ಆಗಬಹುದು.",
        roleAdministrator: "ನಿರ್ವಾಹಕರು",
        roleMemberPlain: "ಸದಸ್ಯ",
        searchPlaceholder: "ಏನನ್ನಾದರೂ ಹುಡುಕಿ…",
        searchNoResults: "ಇದಕ್ಕೆ ಯಾವುದೇ ಫಲಿತಾಂಶಗಳಿಲ್ಲ",
        grpMembers: "ಸದಸ್ಯರು",
        grpMemories: "ಸ್ಮೃತಿಗಳು",
        grpDocuments: "ದಾಖಲೆಗಳು",
        grpStories: "ಕಥೆಗಳು",
        grpEvents: "ಕಾರ್ಯಕ್ರಮಗಳು",
        msgTitle: "ಸಂದೇಶಗಳು ಮತ್ತು ನೆನಪಿಸುವಿಕೆಗಳು",
        msgMarkAll: "ಎಲ್ಲವನ್ನೂ ಓದಿರುವಂತೆ ಗುರುತಿಸಿ",
        msgEmpty: "ಇನ್ನೂ ಯಾವುದೇ ಸಂದೇಶಗಳಿಲ್ಲ — ನೆನಪಿಸುವಿಕೆಗಳು ಮತ್ತು ಕುಟುಂಬದ ವಿವರಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ.",
        mbHome: "ಮುಖಪುಟ",
        mbTree: "ವಂಶವೃಕ್ಷ",
        mbAdd: "ಸೇರಿಸಿ",
        mbHub: "ಮಧ್ಯಕೇಂದ್ರ",
        mbMe: "ನಾನು",
        dbMemberSince: "ಸದಸ್ಯತ್ವ ಆರಂಭ",
        dbGrowingTogether: "ಒಟ್ಟಿಗೆ ಬೆಳೆಯೋಣ",
        dbGrowingSub: "ಪ್ರತಿ ಕುಟುಂಬಕ್ಕೊಂದು ಕಥೆಯಿದೆ.",
        dbProfileCompleteness: "ಪ್ರೊಫೈಲ್ ಪೂರ್ಣತೆ",
        dbMemoryOfDay: "ಇಂದಿನ ನೆನಪು",
        dbMemoryQuote: "ಇಂದಿನ ಮನೆತನ — ನಾಳೆಯ ಪ್ರೇರಣೆ.",
        dbViewMemory: "ನೆನಪು ನೋಡಿ",
        dbGoPremium: "ಪ್ರೀಮಿಯಂಗೆ ವರ್ಗಾಯಿಸಿ",
        dbPremiumDesc: "ಹೆಚ್ಚುವರಿ ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ತೆರೆಯಿರಿ ಮತ್ತು ನಿಮ್ಮ ಕುಟುಂಬದ ಮಾಹಿತಿಯನ್ನು ಸುರಕ್ಷಿತವಾಗಿರಿಸಿ.",
        dbUpgradeNow: "ಈಗಲೇ ಅಪ್‌ಗ್ರೇಡ್ ಮಾಡಿ",
        plMembers: "ಸದಸ್ಯರು",
        plGenerations: "ಪೀಳಿಗೆಗಳು",
        plYearsHistory: "ವರ್ಷಗಳ ಇತಿಹಾಸ",
        plFamilyAccounts: "ಕುಟುಂಬ ಖಾತೆಗಳು",
        plTotalRecords: "ಒಟ್ಟು ದಾಖಲೆಗಳು",
        plPhotosVideos: "ಫೋಟೋಗಳು ಮತ್ತು ವೀಡಿಯೊಗಳು",
        plDocuments: "ಡಾಕ್ಯುಮೆಂಟ್‌ಗಳು",
        plUpcomingEvents: "ಮುಂಭರುವ ಕಾರ್ಯಕ್ರಮಗಳು",
        plAnalytics: "ವಿಶ್ಲೇಷಣೆ",
        qaTitle: "ತ್ವರಿತ ಕ್ರಿಯೆಗಳು",
        qaSubtitle: "ಹೆಚ್ಚು ಬಳಸುವ ವೈಶಿಷ್ಟ್ಯಗಳಿಗೆ ಶೀಘ್ರ ಮಾರ್ಗಗಳು",
        qaAddFamilyMember: "ಕುಟುಂಬ ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿ",
        qaViewFamilyTree: "ಕುಟುಂಬ ವಂಶವೃಕ್ಷವನ್ನು ನೋಡಿ",
        qaAddMemory: "ಸ್ಮರಣೆ ಸೇರಿಸಿ",
        qaScheduleEvent: "ಕಾರ್ಯಕ್ರಮವನ್ನು ನಿಗದಿಪಡಿ",
        qaInviteFamily: "ಕುಟುಂಬವನ್ನು ಆಹ್ವಾನಿಸಿ",
        chFamilyGrowth: "ಕುಟುಂಬದ ಬೆಳವಣಿಗೆ",
        chRecords: "ದಾಖಲೆಗಳು",
        chActivity: "ಚಟುವಟಿಕೆ",
        chAllTime: "ಎಲ್ಲಾ ಸಮಯ",
        chWeek: "ವಾರ",
        chTotal: "ಒಟ್ಟು",
        chActivityEmpty: "ನಿಮ್ಮ ಕುಟುಂಬವು ಸ್ಮರಣೆಗಳನ್ನು ಸೇರಿಸುತ್ತಾ ಹೋದಂತೆ ಇಲ್ಲಿ ಚಟುವಟಿಕೆ ಕಾಣಿಸುತ್ತದೆ",
        recDocuments: "ಡಾಕ್ಯುಮೆಂಟ್‌ಗಳು",
        recPhotosVideos: "ಫೋಟೋಗಳು ಮತ್ತು ವೀಡಿಯೊಗಳು",
        recHealthRecords: "ಆರೋಗ್ಯ ದಾಖಲೆಗಳು",
        recLegalRecords: "ಕಾನೂನು ದಾಖಲೆಗಳು",
        recStories: "ಕಥೆಗಳು",
        dbTodaysCelebrations: "ಇಂದಿನ ಸಂಭ್ರಮಗಳು",
        dbViewAll: "ಎಲ್ಲವನ್ನೂ ನೋಡಿ",
        dbNoCelebToday: "ಇಂದು ಜನ್ಮದಿನಗಳು ಅಥವಾ ವಾರ್ಷಿಕೋತ್ಸವಗಳು ಇಲ್ಲ.",
        dbTurnsToday: "ಇಂದು {n} ವಯಸ್ಸು ತುಂಬುತ್ತಿದೆ!",
        dbRemembranceDay: "ಸ್ಮರಣಾ ದಿನ",
        dbViewCalendar: "ಕ್ಯಾಲೆಂಡರ್ ನೋಡಿ",
        dbNoUpcoming60: "ಮುಂದಿನ 60 ದಿನಗಳಲ್ಲಿ ಯಾವುದೇ ಕಾರ್ಯಕ್ರಮಗಳಿಲ್ಲ.",
        evBirthdayOf: "{name} ಅವರ ಜನ್ಮದಿನ",
        evRemembranceOf: "{name} — ಸ್ಮರಣೆ",
        evToday: "ಇಂದು",
        evInDays: "{n} ದಿನಗಳಲ್ಲಿ",
        evTurning: "{n} ವರ್ಷ ತುಂಬುತ್ತಿದೆ",
        dbRecentActivity: "ಇತ್ತೀಚಿನ ಚಟುವಟಿಕೆ",
        dbNoActivity: "ಇನ್ನೂ ಯಾವುದೇ ಚಟುವಟಿಕೆ ಇಲ್ಲ — ಪ್ರಾರಂಭಿಸಲು ಸದಸ್ಯರನ್ನು ಅಥವಾ ನೆನಪುಗಳನ್ನು ಸೇರಿಸಿ.",
        actSomeone: "ಯಾರೋ ಒಬ್ಬರು",
        actPersonAdded: "ಹೊಸ ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿದ್ದಾರೆ",
        actPersonUpdated: "ಒಬ್ಬ ಸದಸ್ಯರ ವಿವರವನ್ನು ನವೀಕರಿಸಿದ್ದಾರೆ",
        actPersonDeleted: "ಒಬ್ಬ ಸದಸ್ಯರನ್ನು ತೆಗೆದುಹಾಕಿದ್ದಾರೆ",
        actMemoryUploaded: "ಒಂದು ಫೋಟೋವನ್ನು ಅಪ್‍ಲೋಡ್ ಮಾಡಿದ್ದಾರೆ",
        actMemoryDeleted: "ಒಂದು ಫೋಟೋವನ್ನು ಅಳಿಸಿದ್ದಾರೆ",
        actDocUploaded: "ಒಂದು ಡಾಕ್ಯುಮೆಂಟ್ ಅನ್ನು ಅಪ್‍ಲೋಡ್ ಮಾಡಿದ್ದಾರೆ",
        actDocViewed: "ಒಂದು ಡಾಕ್ಯುಮೆಂಟ್ ಅನ್ನು ವೀಕ್ಷಿಸಿದ್ದಾರೆ",
        actStoryRecorded: "ಒಂದು ಕಥೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿದ್ದಾರೆ",
        actStoryPlayed: "ಒಂದು ಕಥೆಯನ್ನು ಪ್ಲೇ ಮಾಡಿದ್ದಾರೆ",
        actCalendarCreate: "ಒಂದು ಕಾರ್ಯಕ್ರಮವನ್ನು ಸೃಷ್ಟಿಸಿದ್ದಾರೆ",
        actCalendarUpdate: "ಒಂದು ಕಾರ್ಯಕ್ರಮವನ್ನು ನವೀಕರಿಸಿದ್ದಾರೆ",
        actCapsuleCreated: "ಒಂದು ನೆನಪು ಕ್ಯಾಪ್ಸ್ಯೂಲ್ ಅನ್ನು ಸೃಷ್ಟಿಸಿದ್ದಾರೆ",
        actAchievementAdded: "ಒಂದು ಸಾಧನೆಯನ್ನು ಸೇರಿಸಿದ್ದಾರೆ",
        actDefault: "ಒಂದು ನವೀಕರಣ ಮಾಡಿದ್ದಾರೆ",
        taJustNow: "ಈಗ ತಾನೆ",
        taMinAgo: "{n}ನಿಮಿಷ ಹಿಂದೆಯೇ",
        taHourAgo: "{n}ಗಂಟೆ ಹಿಂದೆಯೇ",
        taDayAgo: "{n}ದಿನ ಹಿಂದೆಯೇ",
        gsTitle: "ಪ್ರಾರಂಭಿಸೋಣ",
        gsProgress: "{done}/{total} ಪೂರ್ಣಗೊಂಡಿವೆ",
        gsAddMember: "ಒಬ್ಬ ಕುಟುಂಬ ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿ",
        gsUploadMemory: "ಒಂದು ನೆನಪನ್ನು ಅಪ್ಲೋಡ್ ಮಾಡಿ",
        gsRecordStory: "ಒಂದು ಕಥೆಯನ್ನು ರೆಕಾರ್ಡ್ ಮಾಡಿ",
        gsInviteFamily: "ಕುಟುಂಬವನ್ನು ಆಹ್ವಾನಿಸಿ",
        gsSecureDoc: "ಒಂದು ದಾಖಲೆನ್ನು ಸುರಕ್ಷಿತಗೊಳಿಸಿ",
        gsCelebrateTitle: "ಪ್ರಾರಂಭಿಸುವುದು ಸಂಪೂರ್ಣವಾಗಿದೆ!",
        gsCelebrateBody: "ಎಲ್ಲಾ 5 ಹೆಜ್ಜೆಗಳು ಮುಗಿದಿವೆ — ನಿಮ್ಮ ಕುಟುಂಬದ ಡಿಜಿಟಲ್ ಪರಂಪರೆ ಈಗ ಅಧಿಕೃತವಾಗಿ ಆರಂಭವಾಗಿದೆ. ನಿಮ್ಮ ವಂಶವೃಕ್ಷವನ್ನು ಮುಂದುವರಿಸಿ ಬೆಳೆಸಿಕೊಳ್ಳಿ!",
        actProfileUpdated: "ತಮ್ಮ ಪ್ರೊಫೈಲ್ ಅನ್ನು ನವೀಕರಿಸಿದ್ದಾರೆ",
        daySun: "ರವಿ",
        dayMon: "ಸೋಮ",
        dayTue: "ಮಂಗಳ",
        dayWed: "ಬುಧ",
        dayThu: "ಗುರು",
        dayFri: "ಶುಕ್ರ",
        daySat: "ಶನಿ",
        hbTitle: "ಕುಟುಂಬ — ಎಲ್ಲವೂ ಒಂದೇ ಜಾಗದಲ್ಲಿ",
        hbCategories: "ವರ್ಗಗಳು",
        hbSearchAll: "ಎಲ್ಲಾ ವೈಶಿಷ್ಟ್ಯಗಳನ್ನು ಹುಡುಕಿ…",
        hbNoMatch: "ಯಾವ ವೈಶಿಷ್ಟ್ಯವೂ ಹೊಂದಿಕೆಯಾಗಲಿಲ್ಲ",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "ಬರುತ್ತಿದೆ",
        hbNotifyMe: "ನನ್ನಿಗೆ ತಿಳಿಸಿ",
        hbNotified: "ತಯಾರಾದಾಗ ತಿಳಿಸಲಾಗಿದೆ",
        hbNotifyTitle: "\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\" LIVE ಆಗುವಾಗ ನಾವು ನಿಮಗೆ ತಿಳಿಸುತ್ತೇವೆ",
        hbNotifyBody: "ನಿಮ್ಮ ಆಸಕ್ತಿ ನೋಂದಾಯಿಸಲಾಗಿದೆ. ಹೆಚ್ಚು ಕುಟುಂಬಗಳು ಕೇಳಿರುವ ವೈಶಿಷ್ಟ್ಯಗಳಿಗೆ ತಂಡ ಮೊದಲು ಆದ್ಯತೆ ನೀಡುತ್ತದೆ.",
        hbGreat: "ಸರಿ",
        rcSearch: "ಹುಡುಕಿ…",
        rcExportCsv: "CSV ರೀತಿ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ",
        rcAddNew: "ಹೊಸದು ಸೇರಿಸಿ",
        rcCreate: "ರಚಿಸಿ",
        rcEdit: "ತಿದ್ದು",
        rcSave: "ಉಳಿಸಿ",
        rcCancel: "ರದ್ದು ಮಾಡಿ",
        rcLoading: "ಲೋಡ್ ಆಗುತ್ತಿದೆ…",
        rcNoYet: "ಇನ್ನೂ {label} ಇಲ್ಲ",
        rcClick: "ಒತ್ತಿ",
        rcFirstEntry: "ನಿಮ್ಮ ಮೊದಲ ದಾಖಲೆಯನ್ನು ಸೇರಿಸಲು.",
        rcNew: "ಹೊಸದು",
        rcFormHint: "ಕೆಳಗಿನ ವಿವರಗಳನ್ನು ತುಂಬಿ — ಅಗತ್ಯ ಕ್ಷೇತ್ರಗಳನ್ನು ಕೆಂಪು ನಕ್ಷತ್ರದ ಮೂಲಕ ಗುರುತಿಸಲಾಗಿದೆ.",
        rcHealthBold: "ಕುಟುಂಬದ ದಾಖಲೆಗಳಿಗಾಗಿ ಮಾತ್ರ",
        rcHealthRest: "— ರೋಗನಿರ್ಣಯ, ಚಿಕಿತ್ಸೆ ಅಥವಾ ವೈದ್ಯಕೀಯ ಸಲಹೆಗಾಗಿ ಅಲ್ಲ. ಕುಟುಂಬದ ಆರೋಗ್ಯ ದಾಖಲೆಗಳನ್ನು ಕುಟುಂಬದ ಒಪ್ಪಿಗೆಯೊಂದಿಗೆ ಕುಟುಂಬ ಮುಖ್ಯಸ್ಥರು ಸೇರಿಸಬಹುದು ಅಥವಾ ತಿದ್ದಬಹುದು.",
        rcConfirmTitle: "ದಯವಿಟ್ಟು ಖಚಿತಪಡಿಸಿ",
        rcConfirmDelete: "ಈ ದಾಖಲೆಯನ್ನು ಅಳಿಸಬೇಕೇ?",
        rcUpdated: "ನವೀಕರಿಸಲಾಗಿದೆ.",
        rcSaved: "ಉಳಿಸಲಾಗಿದೆ.",
        rcCreated: "ಸೇರಿಸಲಾಗಿದೆ.",
        rcDeleted: "ಅಳಿಸಲಾಗಿದೆ.",
        rcChoose: "ಆಯ್ಕೆಮಾಡಿ…",
        rcUnknownSection: "ಗೊತ್ತಿರದ ವಿಭಾಗ",
        rcSectionMissing: "ಈ ಕುಟುಂಬದ ದಾಖಲೆಗಳ ವಿಭಾಗ ಅಸ್ತಿತ್ವದಲ್ಲಿಲ್ಲ.",
        rcBackToHub: "Family Hub ಗೆ ಹಿಂದಿರಗಿ",
        rcWatchDemo: "ಡೆಮೋ ನೋಡಿ",
        rcDemoSoon: "ಡೆಮೋ ಬೇಗ ಬರುತ್ತಿದೆ",
        rcFamilyService: "ಕುಟುಂಬ ಸೇವೆ",
        rcUntitled: "(ಶೀರ್ಷಿಕೆಯಿಲ್ಲ)",
        setTitle: "ಸಂಯೋಜನೆಗಳು ಮತ್ತು ಗೌಪ್ಯತೆ",
        setBack: "ಹಿಂದೆ",
        setTabInvites: "ಆಹ್ವಾನಗಳು",
        setTabSecurity: "ಭದ್ರತೆ",
        setTabData: "ನನ್ನ ಡೇಟಾ",
        setTabActivity: "ಚಟುವಟಿಕೆ",
        setTabHelp: "ಸಹಾಯ",
        setMyAccount: "ನನ್ನ ಖಾತೆ",
        setMyAccountSub: "ರಶೀದಿ, ಸ್ವಾಗತ ಪತ್ರ ಮತ್ತು ಸಹಾಯ ಉತ್ತರಗಳನ್ನು ಪಡೆಯಲು ನಿಮ್ಮ ಇಮೇಲ್ ಅನ್ನು ಸೇರಿಸಿ. ನಿಮ್ಮ ಫೋನ್ ಸಂಖ್ಯೆ ನಿಮ್ಮ ಲಾಗಿನ್ ಆಗಿದೆ ಮತ್ತು ಇದನ್ನು ಇಲ್ಲಿ ಬದಲಾಯಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ.",
        setName: "ಹೆಸರು",
        setEmail: "ಇಮೇಲ್",
        setEmailInvalid: "ದಯವಿಟ್ಟು ಮಾನ್ಯವಾದ ಇಮೇಲ್ ವಿಳಾಸವನ್ನು ನಮೂದಿಸಿ.",
        setSaveChanges: "ಬದಲಾವಣೆಗಳನ್ನು ಉಳಿಸಿ",
        setSaving: "ಉಳಿಸಲಾಗುತ್ತಿದೆ…",
        setSaved: "ಖಾತೆಯ ವಿವರಗಳನ್ನು ಉಳಿಸಲಾಗಿದೆ.",
        setFamilyMembers: "ಕುಟುಂಬದ ಸದಸ್ಯರು",
        setRolesHint: "Admin: ಸಂಪೂರ್ಣ ನಿಯಂತ್ರಣ · Editor: ಸದಸ್ಯರನ್ನು ಸೇರಿಸಿ/ತಿದ್ದುಪಡಿ ಮಾಡಿ · Viewer: ಓದಲು ಮಾತ್ರ",
        setYou: "(ನೀವು)",
        setRoleUpdated: "ಪಾತ್ರವನ್ನು ನವೀಕರಿಸಲಾಗಿದೆ.",
        setActiveInvites: "ಸಕ್ರಿಯ ಆಹ್ವಾನಗಳು",
        setInvitesSub: "ಕುಟುಂಬ ವೃಕ್ಷದಲ್ಲಿನ ಸದಸ್ಯರ ಪ್ರೊಫೈಲ್ ಡ್ರಾಯರ್‌ನಿಂದ ಆಹ್ವಾನಗಳನ್ನು ಕಳುಹಿಸಿ. ಯಾವಾಗ ಬೇಕಾದರೂ ಹಿಂಪಡೆಯಬಹುದು.",
        setNoActiveInvites: "ಸಕ್ರಿಯ ಆಹ್ವಾನಗಳಿಲ್ಲ.",
        setRevoke: "ಹಿಂಪಡಿ",
        setExpires: "ಅವಧಿ ಮುಗಿಯುವ ದಿನ",
        setOnlyAdmins: "ಕುಟುಂಬ ನಿರ್ವಾಹಕರು ಮಾತ್ರ ಆಹ್ವಾನಗಳನ್ನು ನಿರ್ವಹಿಸಬಹುದು.",
        setInviteRevoked: "ಆಹ್ವಾನವನ್ನು ಹಿಂಪಡೆಯಲಾಗಿದೆ.",
        setSeniorSub: "ಗೌಪ್ಯತಾ ಆಯ್ಕೆಗಳನ್ನು 3 ಪ್ರಮುಖ ಆಯ್ಕೆಗಳಿಗೆ ಸರಳಗೊಳಿಸುತ್ತದೆ: ಖಾಸಗಿ / ಕುಟುಂಬ / ಸಾರ್ವಜನಿಕ.",
        setWelcomeTour: "ಸ್ವಾಗತ ಪ್ರವಾಸ",
        setWelcomeTourSub: "3 ಹಂತಗಳ ಪರಿಚಯವನ್ನು ಮರುಪ್ರದರ್ಶಿಸಿ: ಕುಟುಂಬ ವೃಕ್ಷ → ನೆನಪುಗಳು → ವಾಲ್ಟ್.",
        setReplayTour: "ಪ್ರವಾಸವನ್ನು ಮರುಪ್ರಾರಂಭಿಸಿ",
        setLivingIn: "ತಮಗೆ ವಾಸವಾಗಿರುವುದು",
        setLivingInSub: "ಐಚ್ಛಿಕ — NRI ಕುಟುಂಬಗಳಿಗೆ ಉತ್ತಮ ಸೇವೆ ನೀಡಲು ನಮಗೆ ಸಹಾಯ ಮಾಡುತ್ತದೆ.",
        setIndia: "ಭಾರತ",
        setAbroad: "ವಿದೇಶ (NRI)",
        setSelect: "ಆಯ್ಕೆಮಾಡಿ",
        setWhatWeStore: "ನಿಮ್ಮ ಬಗ್ಗೆ ನಾವು ಸಂಗ್ರಹಿಸುವ ಮಾಹಿತಿ",
        setDpdpNote: "DPDP Act: ಸಂಪೂರ್ಣ ಪಾರದರ್ಶಕತೆ, ಸದಾ.",
        setProfileSections: "ಪ್ರೊಫೈಲ್ ವಿಭಾಗಗಳು",
        setVaultDocs: "ವಾಲ್ಟ್ ದಾಖಲೆಗಳು",
        setInvitesSent: "ಆಮಂತ್ರಣಗಳನ್ನು ಕಳುಹಿಸಲಾಗಿದೆ",
        setActivityEntries: "ಚಟುವಟಿಕೆಯ ದಾಖಲಾತಿಗಳು",
        setDownloadData: "ನನ್ನ ಎಲ್ಲಾ ಮಾಹಿತಿಯನ್ನು ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ (JSON)",
        setDeleteTitle: "ನನ್ನ ಖಾತೆ ಮತ್ತು ಮಾಹಿತಿಯನ್ನು ಅಳಿಸಿ",
        setDeleteSub: "ನಿಮ್ಮ ಖಾತೆ, ನಿಮ್ಮ ಕಥೆಗಳು, ವಾಲ್ಟ್ ದಾಖಲೆಗಳು ಮತ್ತು ಚಟುವಟಿಕೆ ದಾಖಲೆಯನ್ನು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸುತ್ತದೆ. ನೀವು ಇತರರ ಬಗ್ಗೆ ರಚಿಸಿದ ಕುಟುಂಬ ವೃಕ್ಷದ ವಿವರಗಳು ಕುಟುಂಬದಲ್ಲಿಯೇ ಉಳಿಯುತ್ತವೆ.",
        setDeleteEverything: "ಎಲ್ಲವನ್ನೂ ಅಳಿಸಿ",
        setDeleteConfirmTitle: "ನಿಮ್ಮ ಖಾತೆಯನ್ನು ಶಾಶ್ವತವಾಗಿ ಅಳಿಸಬೇಕೇ?",
        setDeleteConfirmBody: "ಇದನ್ನು ಹಿಂದಕ್ಕೆ ಕರೆತರಲು ಸಾಧ್ಯವಿಲ್ಲ. ನಿಮ್ಮ ಕಥೆಗಳು, ದಾಖಲೆಗಳು ಮತ್ತು ಖಾತೆಯನ್ನು DPDP ಕಾಯ್ದೆಯ ಪ್ರಕಾರ ಅಳಿಸಲಾಗುತ್ತದೆ.",
        setDeleting: "ಅಳಿಸಲಾಗುತ್ತಿದೆ…",
        setYesDelete: "ಹೌದು, ಎಲ್ಲವನ್ನೂ ಅಳಿಸಿ",
        setActivityLog: "ಚಟುವಟಿಕೆ ದಾಖಲೆ",
        setAuditAdminSub: "ನಿಮ್ಮ ಕುಟುಂಬದ ಮಟ್ಟದಲ್ಲಿ ಯಾರು ಏನು ನೋಡಿದರು ಮತ್ತು ಏನು ಬದಲಿಸಿದರು.",
        setAuditOwnSub: "ನಿಮ್ಮ ಸ್ವಂತ ಚಟುವಟಿಕೆ.",
        setNoActivityYet: "ಇನ್ನೂ ಯಾವುದೇ ಚಟುವಟಿಕೆ ಇಲ್ಲ.",
        setSearchFamily: "ಕುಟುಂಬವನ್ನು ಹುಡುಕಿ…",
        ptsEyebrow: "ನಮ್ಮ ವಿನ್ಯಾಸ ಸ್ಟುಡಿಯೋದಿಂದ",
        ptsTitle: "ಸುಂದರ ಕುಟುಂಬ ಪೋಸ್ಟರ್ ಟೆಂಪ್ಲೇಟ್‌ಗಳು",
        ptsSub: "ನಮ್ಮ ಸ್ಟುಡಿಯೋದಲ್ಲಿ ಜಾಗ್ರತಿಯಿಂದ ತಯಾರಿಸಿದ ಪೋಸ್ಟರ್ ವಿನ್ಯಾಸಗಳು — ನಿಮ್ಮ ಕುಟುಂಬದ ಫೋಟೋಗಳು ಅದಕ್ಕೇ ತಕ್ಕಂತೆ ಇರುತ್ತವೆ. ಸೈನ್ ಅಪ್ ಆದ ನಂತರ ನಿಮಗೆ ಇಷ್ಟವಾದುದನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
        ptsCta: "ನಿಮ್ಮ ಕುಟುಂಬದ ಪೋಸ್ಟರ್ ರಚಿಸಿ",
        ptsPremium: "ಪ್ರೀಮಿಯಂ",
        hcTitle: "ಸಹಾಯ ಕೇಂದ್ರ",
        hcSub: "ಯಾವುದೇ ವಿಚಾರವನ್ನು ಕೇಳಿ — ಸಾಮಾನ್ಯವಾಗಿ ನಾವು ಕೆಲವು ಗಂಟೆಗಳೊಳಗೆ ಉತ್ತರಿಸುತ್ತೇವೆ.",
        hcAskTitle: "ಪ್ರಶ್ನೆ ಕೇಳಿ",
        hcAskSub: "ನಿಮ್ಮ ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಿ, ನಮ್ಮ ಸಹಾಯ ತಂಡ ನಿಮಗೆ ಉತ್ತರ ಕೊಡುತ್ತದೆ.",
        hcSubject: "ವಿಷಯ",
        hcSubjectPh: "ಉದಾ: ನಾನು ಫೋಟೋ ಅಪ್‌ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗುತ್ತಿಲ್ಲ",
        hcMessage: "ನಿಮ್ಮ ಪ್ರಶ್ನೆ",
        hcMessagePh: "ಏನು ಏನಾಯಿತು ಎಂಬುದನ್ನು ಹಂತ ಹಂತವಾಗಿ ತಿಳಿಸಿ…",
        hcSubmit: "ಪ್ರಶ್ನೆಯನ್ನು ಕಳುಹಿಸಿ",
        hcSubmitting: "ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…",
        hcSuccess: "ನಿಮ್ಮ ಪ್ರಶ್ನೆಯನ್ನು ಕಳುಹಿಸಲಾಗಿದೆ — ನಾವು ಬೇಗನೆ ಉತ್ತರಿಸುತ್ತೇವೆ.",
        hcFailed: "ನಿಮ್ಮ ಪ್ರಶ್ನೆಯನ್ನು ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೊಮ್ಮೆ ಪ್ರಯತ್ನಿಸಿ.",
        hcFillBoth: "ದಯವಿಟ್ಟು ವಿಷಯ ಮತ್ತು ನಿಮ್ಮ ಪ್ರಶ್ನೆಯನ್ನು ತುಂಬಿ.",
        hcMyTickets: "ನನ್ನ ಪ್ರಶ್ನೆಗಳು",
        hcNoTickets: "ಇನ್ನೂ ಯಾವುದೇ ಪ್ರಶ್ನೆಗಳಿಲ್ಲ — ಮೇಲಿನ ಬಾಕ್ಸಿನಲ್ಲಿ ನಿಮ್ಮ ಮೊದಲ ಪ್ರಶ್ನೆಯನ್ನು ಕೇಳಿ.",
        hcStatusOpen: "ತೆರೆಯಲಾಗಿದೆ",
        hcStatusResolved: "ಪರಿಹರಿಸಲಾಗಿದೆ",
        hcSupportReply: "ಸಹಾಯ",
        hcQuickHelp: "ತ್ವರಿತ ಸಹಾಯ",
        hcWhatsapp: "WhatsApp ಸಹಾಯ — ತಕ್ಷಣದ ಚಾಟ್",
        hcCall: "ಕರೆ: +91-99999-99999 (ಸೋಮ–ಶನಿ 10–7)",
        hcFaq: "FAQ — ಸಾಮಾನ್ಯವಾಗಿ ಕೇಳುವ ಪ್ರಶ್ನೆಗಳು",
        hcTipTitle: "ಸಲಹೆ",
        hcTipBody: "ಬಹುತೇಕ ಉತ್ತರಗಳು ಈಗಾಗಲೇ FAQಯಲ್ಲಿ ಇವೆ. ನಿಮ್ಮ ಮನೆಯ/ಕುಟುಂಬದ ಮಾಹಿತಿಗೆ ಸಂಬಂಧಿಸಿದ ಯಾವುದೇ ವೈಯಕ್ತಿಕ ವಿಷಯಗಳಿಗೆ ಇಲ್ಲಿ ಕೇಳಿ — ಇದನ್ನು ನಮ್ಮ ಸಹಾಯ ತಂಡ ಮಾತ್ರ ನೋಡುತ್ತದೆ.",
        demoAddPhoto: "ಫೋಟೋ ಸೇರಿಸಿ (ಐಚ್ಛಿಕ)",
        demoChangePhoto: "ಫೋಟೋ ಬದಲಾಯಿಸಿ",
        demoDownloadPoster: "ಪೋಸ್ಟರ್ ಡೌನ್‌ಲೋಡ್ ಮಾಡಿ",
        demoDownloading: "ತಯಾರಾಗುತ್ತಿದೆ…",
    },
    ml: {
        tourListen: "കേൾക്കുക",
        tourStop: "നിർത്തുക",
        tourEyebrow: "വംശ്‌ട്രീയിലേക്ക് സ്വാഗതം",
        tourStepOf: "ഘട്ടം {n} / {total}",
        tourNext: "അടുത്തത്",
        tourStart: "തുടങ്ങുക",
        tourS1Title: "നിങ്ങളുടെ വംശവൃക്ഷം നിർമ്മിക്കുക",
        tourS1Body: "മാതാപിതാക്കളെയും മുത്തശ്ശീമുത്തശ്ശന്മാരെയും കുട്ടികളെയും ചേർത്ത് തുടങ്ങുക. ചേർക്കുന്ന ഓരോരുത്തരും നിങ്ങളുടെ കുടുംബകഥയുടെ ജീവനുള്ള ശാഖയാകുന്നു.",
        tourS1Cta: "വംശവൃക്ഷം തുറക്കുക",
        tourS2Title: "ഓർമ്മകൾ സൂക്ഷിക്കുക",
        tourS2Body: "ഫോട്ടോകൾ അപ്‌ലോഡ് ചെയ്യുക, ശബ്ദങ്ങൾ റെക്കോർഡ് ചെയ്യുക, കഥകൾ എഴുതുക. ഭാവി തലമുറകൾക്ക് വീണ്ടും അനുഭവിക്കാൻ ആൽബങ്ങളായി ക്രമീകരിക്കുക.",
        tourS2Cta: "ഓർമ്മകൾ തുറക്കുക",
        tourS3Title: "വോൾട്ട് സുരക്ഷിതമാക്കുക",
        tourS3Body: "വിൽപത്രങ്ങളും സ്വത്ത് രേഖകളും നിയമരേഖകളും ബാങ്ക്-നിലവാര സുരക്ഷയിൽ സൂക്ഷിക്കുക — ശരിയായ സമയത്ത് അടുത്ത തലമുറയ്ക്ക് കൈമാറുക.",
        tourS3Cta: "വോൾട്ട് തുറക്കുക",
        back: "പിന്നോട്ട്",
        cancel: "റദ്ദാക്കുക",
        delete: "ഇല്ലാതാക്കുക",
        logout: "ലോഗൗട്ട്",
        optional: "(ഐച്ഛികം)",
        tagline: "നിങ്ങളുടെ കുടുംബം. എപ്പോഴും.",
        familyLegacy: "കുടുംബ പൈതൃകം",
        signInTitle: "Family Repository-ൽ സൈൻ ഇൻ ചെയ്യുക",
        otpTitle: "നിങ്ങളുടെ OTP നൽകുക",
        phoneSub: "നിങ്ങളുടെ മൊബൈലിലേക്ക് OTP അയക്കും.",
        otpSentTo: "OTP അയച്ചു",
        expiresIn: "കാലഹരണപ്പെടും",
        mobileNumber: "മൊബൈൽ നമ്പർ",
        phoneHint: "ഇന്ത്യൻ നമ്പറുകൾ മാത്രം (6–9 ൽ തുടങ്ങണം).",
        yourName: "നിങ്ങളുടെ പേര്",
        firstTimeOnly: "(ആദ്യ തവണ മാത്രം)",
        haveInviteCode: "കുടുംബ ക്ഷണ കോഡുണ്ടോ?",
        apply: "പ്രയോഗിക്കുക",
        sendOtp: "OTP അയയ്ക്കുക",
        sendingOtp: "OTP അയയ്ക്കുന്നു…",
        otpLabel: "6 അക്കങ്ങളുള്ള OTP",
        verifyContinue: "പരിശോധിച്ച് തുടരുക",
        verifying: "പരിശോധിക്കുന്നു…",
        changeNumber: "നമ്പർ മാറ്റുക",
        resendOtp: "OTP വീണ്ടും അയയ്ക്കുക",
        resendIn: "വീണ്ടും അയയ്ക്കുക",
        terms: "തുടരുന്നതിലൂടെ നിങ്ങൾ Family Repository-യുടെ സേവന വ്യവസ്ഥകൾക്കും സ്വകാര്യതാ നയത്തിനും സമ്മതിക്കുന്നു.",
        trustStrip: "എൻക്രിപ്റ്റഡ് • കുടുംബ-നിയന്ത്രിതം • ഇന്ത്യയിൽ നിർമ്മിച്ചത്",
        welcome: "സ്വാഗതം",
        dashSub: "നിങ്ങളുടെ Family Repository തയ്യാറാണ്. ആദ്യ അംഗത്തെ ചേർക്കുക അല്ലെങ്കിൽ മുതിർന്നവരുടെ കഥ റെക്കോർഡ് ചെയ്യുക.",
        quickActions: "ദ്രുത പ്രവർത്തനങ്ങൾ",
        footerNote: "നിങ്ങളുടെ ഡാറ്റ സ്വകാര്യവും എൻക്രിപ്റ്റഡും കുടുംബ-നിയന്ത്രിതവുമാണ്. ഇന്ത്യക്കായി — എപ്പോഴും.",
        roleFamilyHead: "കുടുംബനാഥൻ",
        roleAdmin: "അഡ്മിൻ",
        roleEditor: "എഡിറ്റർ",
        roleViewer: "കാഴ്ചക്കാരൻ",
        roleMember: "അംഗം",
        cardTreeTitle: "കുടുംബവൃക്ഷം കാണുക",
        cardTreeDesc: "തലമുറകളിലൂടെ പരന്നുകിടക്കുന്ന നിങ്ങളുടെ കുടുംബം മുഴുവൻ കാണുക.",
        cardAddTitle: "അംഗത്തെ ചേർക്കുക",
        cardAddDesc: "മാതാപിതാക്കൾ, സഹോദരങ്ങൾ, കുട്ടികൾ അല്ലെങ്കിൽ ബന്ധുക്കളെ ചേർക്കുക.",
        cardMemoriesTitle: "ഓർമ്മകൾ",
        cardMemoriesDesc: "കുടുംബ നിമിഷങ്ങളുടെ ഫോട്ടോകൾ അപ്‌ലോഡ് ചെയ്യുക.",
        cardStoryTitle: "കഥ റെക്കോർഡ് ചെയ്യുക",
        cardStoryDesc: "മുതിർന്നവരുടെ ശബ്ദം എന്നേക്കും സൂക്ഷിക്കുക.",
        cardVaultTitle: "രേഖാ വാൾട്ട്",
        cardVaultDesc: "വിൽപത്രങ്ങൾ, രേഖകൾ — സുരക്ഷിതം, നിങ്ങളുടെ വ്യവസ്ഥകളിൽ.",
        cardSettingsTitle: "സ്വകാര്യത & ക്രമീകരണങ്ങൾ",
        cardSettingsDesc: "റോളുകൾ, ക്ഷണങ്ങൾ, നിങ്ങളുടെ ഡാറ്റയും പ്രവർത്തന ലോഗും.",
        memories: "ഓർമ്മകൾ",
        addMemory: "ഓർമ്മ ചേർക്കുക",
        memoriesEmpty: "ഇതുവരെ ഓർമ്മകളൊന്നുമില്ല",
        memoriesEmptySub: "ആദ്യ കുടുംബ ഫോട്ടോ അപ്‌ലോഡ് ചെയ്യുക — വിവാഹങ്ങൾ, ഉത്സവങ്ങൾ, ദൈനംദിന സന്തോഷം.",
        caption: "അടിക്കുറിപ്പ്",
        captionPlaceholder: "ഉദാ. അമ്മൂമ്മയുടെ വീട്ടിൽ ദീപാവലി, 1998",
        memoryDate: "ഇത് എപ്പോഴത്തേതാണ്?",
        tagMember: "കുടുംബാംഗത്തെ ടാഗ് ചെയ്യുക",
        choosePhoto: "ഫോട്ടോ തിരഞ്ഞെടുക്കുക",
        changePhoto: "ഫോട്ടോ മാറ്റുക",
        uploadMemory: "ഓർമ്മ സംരക്ഷിക്കുക",
        uploading: "അപ്‌ലോഡ് ചെയ്യുന്നു…",
        deleteMemoryConfirm: "ഈ ഓർമ്മ ഇല്ലാതാക്കണോ? ഇത് വീണ്ടെടുക്കാനാകില്ല.",
        confirmDelete: "സ്ഥിരീകരിക്കാൻ വീണ്ടും ടാപ്പ് ചെയ്യുക",
        uploadedBy: "ചേർത്തത്",
        heroEyebrow: "നിങ്ങളുടെ കുടുംബം · നിങ്ങളുടെ പൈതൃകം",
        heroHeadline1: "നിങ്ങളുടെ കുടുംബത്തിന്റെ ചരിത്രം",
        heroHeadlineAccent: "സംരക്ഷിച്ച്",
        heroHeadline2: "എന്നേക്കുമായി.",
        heroSubline: "നിങ്ങളുടെ കുടുംബവൃക്ഷം രൂപപ്പെടുത്തൂ, മുതിർന്നവരുടെ ശബ്ദങ്ങൾ റെക്കോർഡ് ചെയ്യൂ, ഫോട്ടോകളും രേഖകളും സംരക്ഷിക്കൂ — ഇന്ത്യൻ കുടുംബങ്ങൾക്കായി, നിങ്ങളുടെ ഭാഷയിൽ തയ്യാറാക്കിയ ഒരു സ്വകാര്യ ഇടത്തിൽ എല്ലാം ഒരുമിച്ച്.",
        heroCtaPrimary: "മൊബൈൽ OTP ഉപയോഗിച്ച് സൗജന്യമായി തുടങ്ങൂ",
        heroCtaSecondary: "ഇത് എങ്ങനെ പ്രവർത്തിക്കുന്നു എന്ന് കാണുക",
        heroTrustPrivate: "സ്വതവേ സ്വകാര്യമായി",
        heroTrustLangs: "10 ഇന്ത്യൻ ഭാഷകൾ",
        heroTrustFree: "തുടങ്ങുന്നത് സൗജന്യം",
        heroSocialProof: "12,500+ ഇന്ത്യൻ കുടുംബങ്ങളുടെ വിശ്വാസം",
        heroBadge10: "10+",
        heroBadge10Cap: "ഇന്ത്യൻ ഭാഷകൾ",
        heroStoryAudio: "ശ്രവണകഥകൾ",
        heroStoryPhotos: "ഫോട്ടോകൾ",
        heroStoryDocs: "രേഖകൾ",
        statHappyFamilies: "സന്തുഷ്ട കുടുംബങ്ങൾ",
        statTreesCreated: "സംരക്ഷിച്ച കുടുംബാംഗങ്ങൾ",
        statStoriesRecorded: "കഥകളും ഓർമ്മകളും",
        statDataPrivacy: "ഡാറ്റാ സ്വകാര്യത",
        demoBadge: "സൈൻ അപ്പ് ഇല്ലാതെ പരീക്ഷിക്കുക",
        demoHeadline: "30 സെക്കൻഡിനുള്ളിൽ നിങ്ങളുടെ കുടുംബവൃക്ഷം ജീവന്തമാകുന്നത് കാണൂ",
        demoSubline: "6 പേരുടെ പേരുകൾ ചേർക്കൂ. നിങ്ങളുടെ 6 തലമുറകളുടെ കുടുംബവൃക്ഷം ഇവിടെയെ തന്നെ സജീവമാകുന്നത് കാണൂ.",
        demoTrustNoPhone: "ഫോൺ വേണ്ട",
        demoTrustNoOtp: "OTP വേണ്ട",
        demoTrustNoSignup: "സൈൻ അപ്പ് വേണ്ട",
        demoStepPrefix: "ചുവടുവയ്‌പ്പ്",
        demoStepOf: "6ൽ —",
        demoLabelFather: "അച്ഛന്റെ പേര്",
        demoPhFather: "ഉദാ. രജേഷ് ശർമ്മ",
        demoLabelGrandfather: "അപ്പൂപ്പന്റെ പേര്",
        demoPhGrandfather: "ഉദാ. മോഹൻ ശർമ്മ",
        demoLabelGreatGrand: "മൂട്ടശ്ശന്റെ പേര്",
        demoPhGreatGrand: "ഉദാ. കിഷൻ ശർമ്മ",
        demoLabelChild: "മകന്റെ / മകളുടെ പേര്",
        demoPhChild: "ഉദാ. അനന്യ ശർമ്മ",
        demoLabelGrandchild: "കൊച്ചുമകന്റെ / കൊച്ചുമകളുടെ പേര്",
        demoPhGrandchild: "ഉദാ. വിഹാൻ ശർമ്മ",
        roleFather: "അച്ഛൻ",
        roleGrandfather: "അപ്പൂപ്പൻ",
        roleGreatGrandfather: "മൂപ്പൻ (പരദാദൻ)",
        roleChild: "മകൻ / മകൾ",
        roleGrandchild: "കൊച്ചുമകൻ / കൊച്ചുമകൾ",
        demoLabelYou: "താങ്കളുടെ പേര്",
        demoLabelParent: "താങ്കളുടെ മാതാപിതാവിന്റെ പേര്",
        demoLabelGrand: "താങ്കളുടെ മുത്തശ്ശന്റെ / മുത്തശ്ശിയുടെ പേര്",
        demoPhYou: "ഉദാ. രാഹുൽ ശർമ്മ",
        demoPhParent: "ഉദാ. സുരേഷ് ശർമ്മ",
        demoPhGrand: "ഉദാ. രാമചന്ദ്ര ശർമ്മ",
        demoHint: "താങ്കളുടെ വംശവൃക്ഷം ജീവന്റെ നിറം കൈവരിക്കാൻ ഇനി {n} പേരുകൾ കൂടി ചേർക്കുക.",
        demoNext: "അടുത്തത്",
        demoSeeTree: "താങ്കളുടെ വൃക്ഷം കാണുക",
        demoRestart: "വീണ്ടും ശ്രമിക്കുക",
        demoStartReal: "എന്റെ യഥാർത്ഥ വംശവൃക്ഷം തുടങ്ങുക (സൗജന്യം)",
        demoTreeBadge: "താങ്കളുടെ മിനി വംശവൃക്ഷം",
        demoChooseDesign: "പോസ്റ്റർ ഡിസൈൻ തിരഞ്ഞെടുക്കുക",
        demoClassic: "ക്ലാസിക്",
        demoFamilyTitle: "{name} കുടുംബം",
        demoShareWhatsApp: "WhatsApp-ൽ പങ്കിടുക",
        demoShareText: "ഞാൻ 30 സെക്കൻഡിൽ ഞങ്ങളുടെ കുടുംബവൃക്ഷം ഉണ്ടാക്കി! 🌳 നിങ്ങളും സൗജന്യമായി ഉണ്ടാക്കൂ: {link}",
        demoScanToJoin: "സ്കാൻ ചെയ്ത് സൗജന്യമായി ഉണ്ടാക്കൂ",
        demoTreeHeadline: "ആറ് തലമുറകൾ — ഒറ്റ ജീവിക്കുന്ന വൃക്ഷത്തിൽ.",
        demoTreeAddMore: "ഇനി 20-ൽ അധികം ബന്ധുക്കളെയും, ചിത്രങ്ങളും ഓർമ്മകളും — സൗജന്യമായി — ചേർക്കൂ.",
        demoFooterPrivacy: "ഈ ഡെമോ ഫലം താങ്കളുടെ ബ്രൗസറിനുള്ളിൽ മാത്രം നിലനിൽക്കും. ഒന്നും സംരക്ഷിക്കുകയോ അയക്കുകയോ ചെയ്യില്ല.",
        roleGrandparent: "മുത്തശ്ശൻ / മുത്തശ്ശി",
        roleParent: "മാതാപിതാവ്",
        roleYou: "താങ്കൾ",
        testHeadline: "ഇന്ത്യയിലാകെ കുടുംബങ്ങൾ ഞങ്ങളെ വിശ്വസിക്കുന്നു",
        testSubline: "യഥാർത്ഥ കുടുംബങ്ങൾ, യഥാർത്ഥ ജീവിതകഥകൾ — എന്നുമുള്ളതിനായി സംരക്ഷിച്ചിരിക്കുന്നു.",
        testShareBtn: "താങ്കളുടെ കുടുംബകഥ പങ്കിടുക",
        testShareHint: "Family Repository നിങ്ങളുടെ കുടുംബത്തിന് സഹായമായോ? നിങ്ങളുടെ കഥ പങ്കിടൂ.",
        noOtpTitle: "Family Repository ഒരിക്കലും നിങ്ങളുടെ OTP ചോദിക്കില്ല.",
        noOtpBody: "OTP ചോദിച്ച് ആരെങ്കിലും താങ്കളെ വിളിക്കുകയോ സന്ദേശമയയ്ക്കുകയോ ചെയ്താൽ — ഉടൻ തന്നെ ബന്ധം വിച്ഛേദിക്കുക. ഞങ്ങൾ ഒരിക്കലും OTP ചോദിക്കില്ല.",
        navLogin: "ലോഗിൻ",
        navStartFree: "സൗജന്യമായി ആരംഭിക്കുക",
        navDashboard: "ഡാഷ്‌ബോർഡ്",
        tnHome: "ഹോം",
        tnFamilyTree: "കുടുംബവൃക്ഷം",
        tnFamilyHub: "ഫാമിലി ഹബ്",
        tnEvents: "ഇവന്റ്സ്",
        tnPackages: "പാക്കേജുകൾ",
        tnVault: "വോൾട്ട്",
        tnReports: "റിപ്പോർട്ടുകൾ",
        tnMore: "കൂടുതൽ",
        tnRecords: "റെക്കോർഡുകൾ",
        tnSupport: "സപ്പോർട്ട്",
        sbDashboard: "ഡാഷ്ബോർഡ്",
        sbMemberProfile: "അംഗത്തിന്റെ പ്രൊഫൈൽ",
        sbgConnections: "ബന്ധങ്ങൾ",
        sbConnectFriends: "വിപുലമായ കുടുംബാംഗങ്ങളുമായി ബന്ധപ്പെടുക",
        sbConnectRelatives: "ആസന്ന കുടുംബാംഗങ്ങളുമായി ബന്ധപ്പെടുക",
        sbWorldMap: "ലോകമാപ്പ്",
        sbgMemories: "ഓർമ്മകൾ",
        sbPhotoMemories: "ഫോട്ടോ ഓർമ്മകൾ",
        sbAudioMemories: "ഓഡിയോ ഓർമ്മകൾ",
        sbVideoMemories: "വീഡിയോ ഓർമ്മകൾ",
        sbgEvents: "ഇവന്റുകളും റിമൈൻഡറുകളും",
        sbFamilyCalendar: "കുടുംബ കലണ്ടർ",
        sbTodaysCelebration: "ഇന്നത്തെ ആഘോഷം",
        sbFamilyTimeline: "സ്വന്തം ജീവിതയാത്ര",
        sbSmartReminder: "സ്മാർട്ട് റിമൈൻഡർ",
        sbgHealth: "ആരോഗ്യവും മെഡിക്കൽ ശേഖരവും",
        sbMedicalHistory: "മെഡിക്കൽ ചരിത്രം",
        sbChronicDiseases: "ദീർഘകാല / വംശപരമായ രോഗങ്ങൾ",
        sbCancerRegistry: "കാൻസർ രജിസ്റ്റർ",
        sbGeneticCongenital: "ജനിതകവും ജന്മനാടും ഉള്ളവ",
        sbAllergies: "അലർജികളും അസഹിഷ്ണുതകളും",
        sbVaccination: "ടീകരണ രേഖകൾ",
        sbLifestyle: "ജീവിതശൈലി ഡാഷ്ബോർഡ്",
        sbPregnancyBirth: "ഗർഭധാരണവും ജനനചരിത്രവും",
        sbOrganDonation: "അംഗദാന അഭിലാഷം",
        sbBloodGroup: "രക്തഗ്രൂപ്പ് രജിസ്ട്രി",
        sbgVault: "സ്വകാര്യ വോൾട്ട്",
        sbDocumentVault: "ഡോക്യൂമെന്റ് വോൾട്ട്",
        sbLegalVault: "നിയമപരമായ വോൾട്ട്",
        sbgDisplay: "പ്രദർശനവും ഡൗൺലോഡും",
        sbDownloadExport: "ഡൗൺലോഡ് & എക്സ്പോർട്ട്",
        sbDigitalFamilyTree: "ഡിജിറ്റൽ കുടുംബവൃക്ഷം",
        sbPackagesPlans: "പാക്കേജുകളും പ്ലാനുകളും",
        sbPrivacySettings: "സ്വകാര്യതയും ക്രമീകരണങ്ങളും",
        hdrDashboard: "ഡാഷ്ബോർഡ്",
        hdrMenu: "മെനു",
        hdrMyProfile: "എന്റെ പ്രൊഫൈൽ",
        hdrSettings: "ക്രമീകരണങ്ങൾ",
        hdrSignOut: "സൈൻ ഔട്ട്",
        signOutTitle: "Family Repository-ൽ നിന്ന് സൈൻ ഔട്ട് ചെയ്യണോ?",
        signOutBody: "നിങ്ങൾക്ക് നിങ്ങളുടെ മൊബൈൽ OTP ഉപയോഗിച്ച് ഏത് സമയത്തും വീണ്ടും ലോഗിൻ ചെയ്യാം.",
        roleAdministrator: "അഡ്മിനിസ്‌ട്രേറ്റർ",
        roleMemberPlain: "അംഗം",
        searchPlaceholder: "എന്തും തിരയൂ…",
        searchNoResults: "ഫലങ്ങളൊന്നുമില്ല",
        grpMembers: "അംഗങ്ങൾ",
        grpMemories: "ഓർമ്മകൾ",
        grpDocuments: "പ്രമാണങ്ങൾ",
        grpStories: "കഥകൾ",
        grpEvents: "ഇവന്റുകൾ",
        msgTitle: "സന്ദേശങ്ങളും റിമൈൻഡറുകളും",
        msgMarkAll: "എല്ലാം വായിച്ചതായി അടയാളപ്പെടുത്തുക",
        msgEmpty: "ഇനിയും ഒരു സന്ദേശവും ഇല്ല — റിമൈൻഡറുകളും കുടുംബത്തെക്കുറിച്ചുള്ള പുതുക്കലുകളും ഇവിടെ പ്രത്യക്ഷപ്പെടും.",
        mbHome: "ഹോം",
        mbTree: "വംശവൃക്ഷം",
        mbAdd: "ചേർക്കുക",
        mbHub: "ഹബ്",
        mbMe: "ഞാൻ",
        dbMemberSince: "അംഗമായത് മുതൽ",
        dbGrowingTogether: "ഒരുമിച്ച് വളരുന്നു",
        dbGrowingSub: "ഓരോ കുടുംബത്തിനും ഒരു കഥയുണ്ട്.",
        dbProfileCompleteness: "പ്രൊഫൈൽ പൂർണ്ണത",
        dbMemoryOfDay: "ഇന്നത്തെ ഓർമ്മ",
        dbMemoryQuote: "ഇന്നത്തെ കുടുംബം — നാളെയുടെ പ്രചോദനം.",
        dbViewMemory: "ഓർമ്മ കാണുക",
        dbGoPremium: "പ്രീമിയത്തിലേക്ക് മാറുക",
        dbPremiumDesc: "ഉയർന്ന നിലവാരത്തിലുള്ള സവിശേഷതകൾ തുറന്ന് നിങ്ങളുടെ കുടുംബ ഡാറ്റ സുരക്ഷിതമാക്കൂ.",
        dbUpgradeNow: "ഇപ്പോൾ തന്നെ അപ്ഗ്രേഡ് ചെയ്യുക",
        plMembers: "അംഗങ്ങൾ",
        plGenerations: "തലമുറകൾ",
        plYearsHistory: "വർഷങ്ങളായുള്ള ചരിത്രം",
        plFamilyAccounts: "കുടുംബ അക്കൗണ്ടുകൾ",
        plTotalRecords: "മൊത്തം രേഖകൾ",
        plPhotosVideos: "ഫോട്ടോകളും വീഡിയോകളും",
        plDocuments: "രേഖകൾ",
        plUpcomingEvents: "വരാനിരിക്കുന്ന പരിപാടികൾ",
        plAnalytics: "വിശകലനങ്ങൾ",
        qaTitle: "വേഗത്തിലുള്ള പ്രവർത്തനങ്ങൾ",
        qaSubtitle: "പതിവായി ഉപയോഗിക്കുന്ന ഫീച്ചറുകളിലേക്കുള്ള കുറുക്കുവഴികൾ",
        qaAddFamilyMember: "കുടുംബാംഗത്തെ ചേർക്കുക",
        qaViewFamilyTree: "കുടുംബ വംശവൃക്ഷം കാണുക",
        qaAddMemory: "ഓർമ്മ ചേർക്കുക",
        qaScheduleEvent: "ഇവന്റ് നിശ്ചയിക്കുക",
        qaInviteFamily: "കുടുംബത്തെ ക്ഷണിക്കുക",
        chFamilyGrowth: "കുടുംബ വളർച്ച",
        chRecords: "റെക്കോർഡുകൾ",
        chActivity: "പ്രവർത്തനം",
        chAllTime: "എപ്പോഴുമുള്ളത്",
        chWeek: "ആഴ്ച",
        chTotal: "മൊത്തം",
        chActivityEmpty: "നിങ്ങളുടെ കുടുംബം ഓർമ്മകൾ ചേർക്കുമ്പോൾ ഇവിടെ പ്രവർത്തനങ്ങൾ കാണാം",
        recDocuments: "ഡോക്യുമെന്റുകൾ",
        recPhotosVideos: "ഫോട്ടോകളും വീഡിയോകളും",
        recHealthRecords: "ആരോഗ്യ റെക്കോർഡുകൾ",
        recLegalRecords: "നിയമപരമായ റെക്കോർഡുകൾ",
        recStories: "കഥകൾ",
        dbTodaysCelebrations: "ഇന്നത്തെ ആഘോഷങ്ങൾ",
        dbViewAll: "എല്ലാം കാണുക",
        dbNoCelebToday: "ഇന്ന് പിറന്നാളോ വിവാഹ വാർഷികങ്ങളോ ഒന്നുമില്ല.",
        dbTurnsToday: "ഇന്ന് {n} വയസ്സാകുന്നു!",
        dbRemembranceDay: "സ്മരണ ദിനം",
        dbViewCalendar: "കാലണ്ടർ കാണുക",
        dbNoUpcoming60: "അടുത്ത 60 ദിവസത്തിനുള്ളിൽ ഒരു പരിപാടികളും നിശ്ചയിച്ചിട്ടില്ല.",
        evBirthdayOf: "{name}യുടെ ജന്മദിനം",
        evRemembranceOf: "{name} — സ്മരണ",
        evToday: "ഇന്ന്",
        evInDays: "ഇനി {n} ദിവസം",
        evTurning: "{n} വയസ് തികയുന്നു",
        dbRecentActivity: "സമീപകാല പ്രവർത്തനങ്ങൾ",
        dbNoActivity: "ഇപ്പോഴും ഒന്നും നടന്നിട്ടില്ല — തുടങ്ങാൻ അംഗങ്ങളെയും ഓർമ്മകളെയും ചേർക്കുക.",
        actSomeone: "ആരെങ്കിലും",
        actPersonAdded: "ഒരു പുതിയ അംഗത്തെ ചേർത്തു",
        actPersonUpdated: "ഒരു അംഗത്തിന്റെ പ്രോഫൈൽ പുതുക്കി",
        actPersonDeleted: "ഒരു അംഗത്തെ നീക്കി",
        actMemoryUploaded: "ഒരു ചിത്രം അപ്‌ലോഡ് ചെയ്തു",
        actMemoryDeleted: "ഒരു ചിത്രം നീക്കി",
        actDocUploaded: "ഒരു ഡോക്യുമെന്റ് അപ്‌ലോഡ് ചെയ്തു",
        actDocViewed: "ഒരു ഡോക്യുമെന്റ് കണ്ടു",
        actStoryRecorded: "ഒരു കഥ റെക്കോർഡ് ചെയ്തു",
        actStoryPlayed: "ഒരു കഥ പ്ലേ ചെയ്തു",
        actCalendarCreate: "ഒരു ഇവന്റ് സൃഷ്ടിച്ചു",
        actCalendarUpdate: "ഒരു ഇവന്റ് പുതുക്കി",
        actCapsuleCreated: "ഒരു മെമ്മറി ക്യാപ്‌സൂൾ സൃഷ്ടിച്ചു",
        actAchievementAdded: "ഒരു നേട്ടം ചേർത്തു",
        actDefault: "ഒരു അപ്‌ഡേറ്റ് നടത്തി",
        taJustNow: "ഇപ്പോൾ",
        taMinAgo: "{n} മിനിറ്റ് മുൻപ്",
        taHourAgo: "{n} മണിക്കൂർ മുൻപ്",
        taDayAgo: "{n} ദിവസം മുൻപ്",
        gsTitle: "ആരംഭിക്കാം",
        gsProgress: "{done}/{total} പൂർത്തിയായി",
        gsAddMember: "ഒരു കുടുംബാംഗത്തെ ചേർക്കുക",
        gsUploadMemory: "ഒരു ഓർമ്മ അപ്‌ലോഡ് ചെയ്യുക",
        gsRecordStory: "ഒരു കഥ റെക്കോർഡ് ചെയ്യുക",
        gsInviteFamily: "കുടുംബത്തെ ക്ഷണിക്കുക",
        gsSecureDoc: "ഒരു പ്രമാണം സുരക്ഷിതമാക്കുക",
        gsCelebrateTitle: "ആരംഭിക്കൽ പൂർത്തിയായി!",
        gsCelebrateBody: "എല്ലാ 5 ഘട്ടങ്ങളും പൂർത്തിയായി — നിങ്ങളുടെ കുടുംബത്തിന്റെ ഡിജിറ്റൽ പാരമ്പര്യം ഔദ്യോഗികമായി ആരംഭിച്ചു. നിങ്ങളുടെ കുടുംബവൃക്ഷം തുടർച്ചയായി വളർത്തൂ!",
        actProfileUpdated: "സ്വന്തം പ്രൊഫൈൽ പുതുക്കി",
        daySun: "ഞായർ",
        dayMon: "തിങ്കൾ",
        dayTue: "ചൊവ്വ",
        dayWed: "ബുധൻ",
        dayThu: "വ്യാഴം",
        dayFri: "വെള്ളി",
        daySat: "ശനി",
        hbTitle: "കുടുംബം — എല്ലാം ഒരൊറ്റ സ്ഥലത്ത്",
        hbCategories: "വിഭാഗങ്ങൾ",
        hbSearchAll: "എല്ലാ ഫീച്ചറുകളിലും തിരയുക…",
        hbNoMatch: "ഒന്നും പൊരുത്തപ്പെടുന്നില്ല",
        hbLive: "LIVE",
        hbBeta: "BETA",
        hbSoon: "SOON",
        hbNotifyMe: "എന്നെ അറിയിക്കൂ",
        hbNotified: "തയ്യാറായപ്പോൾ അറിയിക്കും",
        hbNotifyTitle: "\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\" ലൈവാകുമ്പോൾ നിങ്ങളെ അറിയിക്കും",
        hbNotifyBody: "താങ്കളുടെ താൽപര്യം ഞങ്ങൾ രേഖപ്പെടുത്തി. കൂടുതൽ കുടുംബങ്ങൾ ആവശ്യപ്പെടുന്ന ഫീച്ചറുകൾക്കാണ് ടീം ആദ്യം മുൻഗണന നൽകുന്നത്.",
        hbGreat: "നന്നായി",
        rcSearch: "തിരയുക…",
        rcExportCsv: "CSV ആയി എക്സ്പോർട്ട് ചെയ്യുക",
        rcAddNew: "പുതിയത് ചേർക്കുക",
        rcCreate: "സൃഷ്ടിക്കുക",
        rcEdit: "തിരുത്തുക",
        rcSave: "സംരക്ഷിക്കുക",
        rcCancel: "റദ്ദാക്കുക",
        rcLoading: "ലോഡ് ചെയ്യുന്നു…",
        rcNoYet: "ഇന്നും {label} ഇല്ല",
        rcClick: "ക്ലിക്ക് ചെയ്യുക",
        rcFirstEntry: "നിങ്ങളുടെ ആദ്യ രേഖ ചേർക്കാൻ.",
        rcNew: "പുതിയത്",
        rcFormHint: "താഴെയുള്ള ഫീൽഡുകൾ പൂരിപ്പിക്കുക — നിർബന്ധമായ ഫീൽഡുകൾ ചുവപ്പ് നക്ഷത്രം ഉപയോഗിച്ച് അടയാളപ്പെടുത്തിയിരിക്കും.",
        rcHealthBold: "കുടുംബ രേഖാസൂക്ഷണത്തിനായുമാത്രം",
        rcHealthRest: "— പരിശോധന, ചികിത്സ, അല്ലെങ്കിൽ വൈദ്യോപദേശത്തിനല്ല. കുടുംബത്തിന്റെ സമ്മതത്തോടെ Family Head-ന് ആരോഗ്യ രേഖകൾ ചേർക്കാനും തിരുത്താനും കഴിയും.",
        rcConfirmTitle: "ദയവായി സ്ഥിരീകരിക്കുക",
        rcConfirmDelete: "ഈ റെക്കോർഡ് ഇല്ലാതാക്കണോ?",
        rcUpdated: "അപ്ഡേറ്റ് ചെയ്തു.",
        rcSaved: "സൂക്ഷിച്ചു.",
        rcCreated: "സൃഷ്ടിച്ചു.",
        rcDeleted: "ഇല്ലാതാക്കി.",
        rcChoose: "തിരഞ്ഞെടുക്കുക…",
        rcUnknownSection: "അജ്ഞാത വിഭാഗം",
        rcSectionMissing: "ഈ Family Records വിഭാഗം നിലവിലില്ല.",
        rcBackToHub: "Family Hub-ിലേക്ക് മടങ്ങുക",
        rcWatchDemo: "ഡെമോ കാണുക",
        rcDemoSoon: "ഡെമോ ഉടൻ വരുന്നു",
        rcFamilyService: "കുടുംബ സേവനം",
        rcUntitled: "(ശീർഷകമില്ലാത്തത്)",
        setTitle: "ക്രമീകരണങ്ങളും സ്വകാര്യതയും",
        setBack: "തിരികെ",
        setTabInvites: "ക്ഷണക്കൾ",
        setTabSecurity: "സുരക്ഷ",
        setTabData: "എന്റെ ഡാറ്റ",
        setTabActivity: "പ്രവർത്തനം",
        setTabHelp: "സഹായം",
        setMyAccount: "എന്റെ അക്കൗണ്ട്",
        setMyAccountSub: "രസീതുകളും, സ്വാഗതമെയിലും, സഹായത്തിനുള്ള മറുപടികളും ലഭിക്കാൻ നിങ്ങളുടെ ഇമെയിൽ ചേർക്കുക. നിങ്ങളുടെ ഫോണാണ് നിങ്ങളുടെ ലോഗിൻ, അത് ഇവിടെ മാറ്റാൻ സാധിക്കില്ല.",
        setName: "പേര്",
        setEmail: "ഇമെയിൽ",
        setEmailInvalid: "ശരിയായ ഇമെയിൽ വിലാസം നൽകുക.",
        setSaveChanges: "മാറ്റങ്ങൾ സേവ് ചെയ്യുക",
        setSaving: "സേവ് ചെയ്യുന്നു…",
        setSaved: "അക്കൗണ്ട് വിവരങ്ങൾ സേവ് ചെയ്തിരിക്കുന്നു.",
        setFamilyMembers: "കുടുംബാംഗങ്ങൾ",
        setRolesHint: "Admin: പൂർണ അധികാരം · Editor: അംഗങ്ങളെ ചേർക്കുക/തിരുത്തുക · Viewer: വായിക്കാനുള്ളത് മാത്രം",
        setYou: "(നിങ്ങൾ)",
        setRoleUpdated: "റോൾ അപ്ഡേറ്റ് ചെയ്തിരിക്കുന്നു.",
        setActiveInvites: "സജീവ ക്ഷണങ്ങൾ",
        setInvitesSub: "ട്രീയിലുള്ള അംഗത്തിന്റെ പ്രൊഫൈൽ ഡ്രോയറിൽ നിന്ന് ക്ഷണങ്ങൾ അയക്കാം. ഏതുനേരവും റദ്ദാക്കാം.",
        setNoActiveInvites: "സജീവ ക്ഷണങ്ങളൊന്നുമില്ല.",
        setRevoke: "റദ്ദാക്കുക",
        setExpires: "കാലാവധി അവസാനിക്കുന്നത്",
        setOnlyAdmins: "ക്ഷണങ്ങൾ കൈകാര്യം ചെയ്യാൻ കഴിയുന്നത് കുടുംബ അഡ്മിൻമാർക്ക് മാത്രം.",
        setInviteRevoked: "ക്ഷണം റദ്ദാക്കി.",
        setSeniorSub: "സ്വകാര്യത തിരഞ്ഞെടുക്കൽ 3 വലിയ ഓപ്ഷനുകളാക്കി ലളിതമാക്കുന്നു: സ്വകാര്യം / കുടുംബം / പൊതു.",
        setWelcomeTour: "സ്വാഗത ടൂർ",
        setWelcomeTourSub: "3-സ്റ്റെപ്പ് പരിചയപ്പെടുത്തൽ വീണ്ടും കാണുക: Family Tree → Memories → Vault.",
        setReplayTour: "ടൂർ വീണ്ടും കാണുക",
        setLivingIn: "താമസിക്കുന്നത്",
        setLivingInSub: "ഐച്ഛികം — NRI കുടുംബങ്ങൾക്ക് നന്നായി സേവനം നൽകാൻ ഇത് സഹായിക്കുന്നു.",
        setIndia: "ഇന്ത്യ",
        setAbroad: "വിദേശം (NRI)",
        setSelect: "തിരഞ്ഞെടുക്കുക",
        setWhatWeStore: "നിങ്ങളെക്കുറിച്ച് ഞങ്ങൾ സൂക്ഷിക്കുന്നത്",
        setDpdpNote: "DPDP Act: പൂർണ്ണ സുതാര്യത, എല്ലായ്പ്പോഴും.",
        setProfileSections: "പ്രൊഫൈൽ വിഭാഗങ്ങൾ",
        setVaultDocs: "Vault രേഖകൾ",
        setInvitesSent: "ക്ഷണങ്ങൾ അയച്ചു",
        setActivityEntries: "പ്രവർത്തന എൻട്രികൾ",
        setDownloadData: "എന്റെ എല്ലാ ഡാറ്റയും ഡൗൺലോഡ് ചെയ്യുക (JSON)",
        setDeleteTitle: "എന്റെ അക്കൗണ്ടും ഡാറ്റയും ഇല്ലാതാക്കുക",
        setDeleteSub: "നിങ്ങളുടെ അക്കൗണ്ട്, നിങ്ങളുടെ കഥകൾ, വോൾട്ട് ഡോക്കുമെന്റുകൾ, പ്രവർത്തന ലോഗ് എന്നിവ എല്ലാം സ്ഥിരമായി നീക്കം ചെയ്യും. നിങ്ങൾ മറ്റുള്ളവരെക്കുറിച്ച് സൃഷ്ടിച്ച കുടുംബവൃക്ഷ എൻട്രികൾ, കുടുംബത്തിനോടൊപ്പം തുടരും.",
        setDeleteEverything: "എല്ലാം ഇല്ലാതാക്കുക",
        setDeleteConfirmTitle: "നിങ്ങളുടെ അക്കൗണ്ട് സ്ഥിരമായി ഇല്ലാതാക്കണോ?",
        setDeleteConfirmBody: "ഇത് പിന്നെയും തിരുത്താൻ സാധിക്കില്ല. നിങ്ങളുടെ കഥകളും ഡോക്കുമെന്റുകളും അക്കൗണ്ടും DPDP നിയമം അനുസരിച്ച് പൂര്‍ണമായി ഇല്ലാതാക്കപ്പെടും.",
        setDeleting: "ഇല്ലാതാക്കുന്നു…",
        setYesDelete: "അതെ, എല്ലാം ഇല്ലാതാക്കുക",
        setActivityLog: "പ്രവർത്തന ലോഗ്",
        setAuditAdminSub: "നിങ്ങളുടെ കുടുംബത്തിൽ ആരെല്ലാം എന്താണ് കണ്ടതും മാറ്റിയതും.",
        setAuditOwnSub: "നിങ്ങളുടെ സ്വന്തം പ്രവർത്തനം.",
        setNoActivityYet: "ഇതുവരെ ഒരു പ്രവർത്തനവും ഇല്ല.",
        setSearchFamily: "കുടുംബാംഗങ്ങളെ കണ്ടെത്തുക…",
        ptsEyebrow: "ഞങ്ങളുടെ ഡിസൈൻ സ്റ്റുഡിയോയിൽ നിന്ന്",
        ptsTitle: "അലങ്കാരമായ കുടുംബ പോസ്റ്റർ മാതൃകകൾ",
        ptsSub: "ഞങ്ങളുടെ സ്റ്റുഡിയോയിൽ കൈവെച്ചുള്ള പോസ്റ്റർ ഡിസൈനുകൾ — നിങ്ങളുടെ കുടുംബ ഫോട്ടോകൾ അതിൽ സ്വാഭാവികമായി ചേരും. നിങ്ങൾ സൈൻ അപ്പ് ചെയ്ത ശേഷം ഇഷ്ടപ്പെട്ടത് തേരഞ്ഞെടുക്കാം.",
        ptsCta: "നിങ്ങളുടെ കുടുംബ പോസ്റ്റർ ഉണ്ടാക്കുക",
        ptsPremium: "പ്രീമിയം",
        hcTitle: "ഹെൽപ്പ് സെന്റർ",
        hcSub: "ഏതും ചോദിക്കാം — സാധാരണയായി ഞങ്ങൾ ഏതാനും മണിക്കൂറിനുള്ളിൽ മറുപടി നൽകും.",
        hcAskTitle: "ഒരു ചോദ്യം ചോദിക്കുക",
        hcAskSub: "നിങ്ങളുടെ പ്രശ്നം വിശദമായി എഴുതൂ, ഞങ്ങളുടെ സപ്പോർട്ട് സംഘം നിങ്ങളെ തിരികെ സമീപിക്കും.",
        hcSubject: "വിഷയം",
        hcSubjectPh: "ഉദാ: എനിക്ക് ഫോട്ടോ അപ്‌ലോഡ് ചെയ്യാൻ പറ്റുന്നില്ല",
        hcMessage: "നിങ്ങളുടെ ചോദ്യം",
        hcMessagePh: "എന്ത് എന്ത് നടന്നുവെന്ന് ഘട്ടം ഘട്ടമായി പറഞ്ഞുതരൂ…",
        hcSubmit: "ചോദ്യം അയക്കുക",
        hcSubmitting: "അയക്കുന്നു…",
        hcSuccess: "നിങ്ങളുടെ ചോദ്യം അയച്ചിട്ടുണ്ട് — ഞങ്ങൾ ഉടൻ മറുപടി നൽകും.",
        hcFailed: "നിങ്ങളുടെ ചോദ്യം അയയ്ക്കാൻ കഴിഞ്ഞില്ല. ദയവായി വീണ്ടും ശ്രമിക്കുക.",
        hcFillBoth: "ദയവായി വിഷയം കൂടി നിങ്ങളുടെ ചോദ്യം കൂടി പൂരിപ്പിക്കുക.",
        hcMyTickets: "എന്റെ ചോദ്യങ്ങൾ",
        hcNoTickets: "ഇതുവരെ ചോദ്യങ്ങളൊന്നുമില്ല — മുകളിലുള്ള ബോക്സിൽ നിങ്ങളുടെ ആദ്യ ചോദ്യം ചോദിക്കൂ.",
        hcStatusOpen: "തുറന്നിരിക്കുന്നു",
        hcStatusResolved: "പരിഹരിച്ചു",
        hcSupportReply: "സഹായം",
        hcQuickHelp: "ഉടൻ സഹായം",
        hcWhatsapp: "WhatsApp support — തത്സമയ ചാറ്റ്",
        hcCall: "വിളിക്കുക: +91-99999-99999 (തിങ്കൾ–ശനി 10–7)",
        hcFaq: "FAQ — പലപ്പോഴും ചോദിക്കുന്ന ചോദ്യങ്ങൾ",
        hcTipTitle: "സൂചന",
        hcTipBody: "മിക്ക ചോദ്യങ്ങളുടെയും ഉത്തരങ്ങൾ ഇതിനകം തന്നെ FAQ-യിൽ ഉണ്ട്. നിങ്ങളുടെ കുടുംബത്തിന്റെ വിവരങ്ങളുമായി ബന്ധമുള്ള വ്യക്തിപരമായ കാര്യങ്ങൾക്കായി ഇവിടെ ചോദിക്കാം — അത് നമ്മുടെ support ടീം മാത്രമേ കാണുകയുള്ളു.",
        demoAddPhoto: "ഫോട്ടോ ചേർക്കുക (ഐച്ഛികം)",
        demoChangePhoto: "ഫോട്ടോ മാറ്റുക",
        demoDownloadPoster: "പോസ്റ്റർ ഡൗൺലോഡ് ചെയ്യുക",
        demoDownloading: "തയ്യാറാക്കുന്നു…",
    },
    pa: {
        tourListen: "ਸੁਣੋ",
        tourStop: "ਰੋਕੋ",
        tourEyebrow: "ਵੰਸ਼ਟ੍ਰੀ ਵਿੱਚ ਜੀ ਆਇਆਂ ਨੂੰ",
        tourStepOf: "ਕਦਮ {n} / {total}",
        tourNext: "ਅੱਗੇ",
        tourStart: "ਸ਼ੁਰੂ ਕਰੋ",
        tourS1Title: "ਆਪਣਾ ਵੰਸ਼ ਰੁੱਖ ਬਣਾਓ",
        tourS1Body: "ਮਾਤਾ-ਪਿਤਾ, ਦਾਦਾ-ਦਾਦੀ ਅਤੇ ਬੱਚਿਆਂ ਨੂੰ ਜੋੜ ਕੇ ਸ਼ੁਰੂਆਤ ਕਰੋ। ਹਰ ਮੈਂਬਰ ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦੀ ਕਹਾਣੀ ਦੀ ਇੱਕ ਜਿਊਂਦੀ ਟਾਹਣੀ ਬਣ ਜਾਂਦਾ ਹੈ।",
        tourS1Cta: "ਵੰਸ਼ ਰੁੱਖ ਖੋਲ੍ਹੋ",
        tourS2Title: "ਯਾਦਾਂ ਸੰਭਾਲੋ",
        tourS2Body: "ਫੋਟੋਆਂ ਅੱਪਲੋਡ ਕਰੋ, ਆਵਾਜ਼ਾਂ ਰਿਕਾਰਡ ਕਰੋ ਅਤੇ ਕਹਾਣੀਆਂ ਲਿਖੋ। ਐਲਬਮਾਂ ਵਿੱਚ ਸਜਾਓ ਤਾਂ ਜੋ ਆਉਣ ਵਾਲੀਆਂ ਪੀੜ੍ਹੀਆਂ ਇਨ੍ਹਾਂ ਨੂੰ ਮੁੜ ਜੀਅ ਸਕਣ।",
        tourS2Cta: "ਯਾਦਾਂ ਖੋਲ੍ਹੋ",
        tourS3Title: "ਵਾਲਟ ਸੁਰੱਖਿਅਤ ਕਰੋ",
        tourS3Body: "ਵਸੀਅਤਾਂ, ਜਾਇਦਾਦ ਦੇ ਕਾਗਜ਼ ਅਤੇ ਕਾਨੂੰਨੀ ਦਸਤਾਵੇਜ਼ ਬੈਂਕ-ਪੱਧਰੀ ਸੁਰੱਖਿਆ ਵਿੱਚ ਰੱਖੋ — ਅਤੇ ਸਹੀ ਸਮੇਂ ਅਗਲੀ ਪੀੜ੍ਹੀ ਨੂੰ ਸੌਂਪੋ।",
        tourS3Cta: "ਵਾਲਟ ਖੋਲ੍ਹੋ",
        back: "ਪਿੱਛੇ",
        cancel: "ਰੱਦ ਕਰੋ",
        delete: "ਹਟਾਓ",
        logout: "ਲੌਗਆਊਟ",
        optional: "(ਵਿਕਲਪਿਕ)",
        tagline: "ਤੁਹਾਡਾ ਪਰਿਵਾਰ. ਹਮੇਸ਼ਾ ਲਈ.",
        familyLegacy: "ਪਰਿਵਾਰਕ ਵਿਰਾਸਤ",
        signInTitle: "Family Repository ਵਿੱਚ ਸਾਈਨ ਇਨ ਕਰੋ",
        otpTitle: "ਆਪਣਾ OTP ਦਰਜ ਕਰੋ",
        phoneSub: "ਅਸੀਂ ਤੁਹਾਡੇ ਮੋਬਾਈਲ ਉੱਤੇ OTP ਭੇਜਾਂਗੇ.",
        otpSentTo: "OTP ਭੇਜਿਆ",
        expiresIn: "ਇਸ ਦੀ ਮਿਆਦ ਖ਼ਤਮ ਹੋ ਜਾਵੇਗੀ",
        mobileNumber: "ਮੋਬਾਈਲ ਨੰਬਰ",
        phoneHint: "ਸਿਰਫ਼ ਭਾਰਤੀ ਨੰਬਰ (6–9 ਨਾਲ ਸ਼ੁਰੂ).",
        yourName: "ਤੁਹਾਡਾ ਨਾਮ",
        firstTimeOnly: "(ਸਿਰਫ਼ ਪਹਿਲੀ ਵਾਰ)",
        haveInviteCode: "ਪਰਿਵਾਰ ਦਾ ਇਨਵਾਈਟ ਕੋਡ ਹੈ?",
        apply: "ਲਾਗੂ ਕਰੋ",
        sendOtp: "OTP ਭੇਜੋ",
        sendingOtp: "OTP ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…",
        otpLabel: "6-ਅੰਕੀ OTP",
        verifyContinue: "ਪੁਸ਼ਟੀ ਕਰੋ ਅਤੇ ਅੱਗੇ ਵਧੋ",
        verifying: "ਪੁਸ਼ਟੀ ਹੋ ਰਹੀ ਹੈ…",
        changeNumber: "ਨੰਬਰ ਬਦਲੋ",
        resendOtp: "OTP ਮੁੜ ਭੇਜੋ",
        resendIn: "ਮੁੜ ਭੇਜੋ",
        terms: "ਜਾਰੀ ਰੱਖ ਕੇ ਤੁਸੀਂ Family Repository ਦੀਆਂ ਸੇਵਾ ਸ਼ਰਤਾਂ ਅਤੇ ਪ੍ਰਾਈਵੇਸੀ ਨੀਤੀ ਨਾਲ ਸਹਿਮਤ ਹੁੰਦੇ ਹੋ.",
        trustStrip: "ਏਨਕ੍ਰਿਪਟਡ • ਪਰਿਵਾਰ-ਨਿਯੰਤਰਿਤ • ਭਾਰਤ ਵਿੱਚ ਬਣਾਇਆ ਗਿਆ",
        welcome: "ਜੀ ਆਇਆਂ ਨੂੰ",
        dashSub: "ਤੁਹਾਡਾ Family Repository ਤਿਆਰ ਹੈ. ਪਹਿਲਾ ਮੈਂਬਰ ਜੋੜੋ ਜਾਂ ਬਜ਼ੁਰਗਾਂ ਦੀ ਕਹਾਣੀ ਰਿਕਾਰਡ ਕਰੋ.",
        quickActions: "ਤੇਜ਼ ਕਾਰਵਾਈਆਂ",
        footerNote: "ਤੁਹਾਡਾ ਡੇਟਾ ਨਿਜੀ, ਏਨਕ੍ਰਿਪਟਡ ਅਤੇ ਪਰਿਵਾਰ-ਨਿਯੰਤਰਿਤ ਹੈ. ਭਾਰਤ ਲਈ — ਹਮੇਸ਼ਾ.",
        roleFamilyHead: "ਪਰਿਵਾਰਕ ਮੁਖੀ",
        roleAdmin: "ਐਡਮਿਨ",
        roleEditor: "ਸੰਪਾਦਕ",
        roleViewer: "ਦਰਸ਼ਕ",
        roleMember: "ਮੈਂਬਰ",
        cardTreeTitle: "ਪਰਿਵਾਰਕ ਰੁੱਖ ਵੇਖੋ",
        cardTreeDesc: "ਪੀੜ੍ਹੀਆਂ ਵਿੱਚ ਫੈਲਿਆ ਆਪਣਾ ਸਾਰਾ ਪਰਿਵਾਰ ਵੇਖੋ.",
        cardAddTitle: "ਮੈਂਬਰ ਜੋੜੋ",
        cardAddDesc: "ਮਾਪੇ, ਭੈਣ-ਭਰਾ, ਬੱਚੇ ਜਾਂ ਰਿਸ਼ਤੇਦਾਰ ਜੋੜੋ.",
        cardMemoriesTitle: "ਯਾਦਾਂ",
        cardMemoriesDesc: "ਪਰਿਵਾਰਕ ਪਲਾਂ ਦੀਆਂ ਤਸਵੀਰਾਂ ਅਪਲੋਡ ਕਰੋ.",
        cardStoryTitle: "ਕਹਾਣੀ ਰਿਕਾਰਡ ਕਰੋ",
        cardStoryDesc: "ਬਜ਼ੁਰਗਾਂ ਦੀ ਆਵਾਜ਼ ਹਮੇਸ਼ਾ ਲਈ ਸੰਭਾਲੋ.",
        cardVaultTitle: "ਦਸਤਾਵੇਜ਼ ਵਾਲਟ",
        cardVaultDesc: "ਵਸੀਅਤਾਂ, ਦਸਤਾਵੇਜ਼ — ਸੁਰੱਖਿਅਤ, ਤੁਹਾਡੀਆਂ ਸ਼ਰਤਾਂ ਉੱਤੇ.",
        cardSettingsTitle: "ਪ੍ਰਾਈਵੇਸੀ ਅਤੇ ਸੈਟਿੰਗਜ਼",
        cardSettingsDesc: "ਭੂਮਿਕਾਵਾਂ, ਸੱਦੇ, ਤੁਹਾਡਾ ਡੇਟਾ ਅਤੇ ਗਤੀਵਿਧੀ ਲੌਗ.",
        memories: "ਯਾਦਾਂ",
        addMemory: "ਯਾਦ ਜੋੜੋ",
        memoriesEmpty: "ਅਜੇ ਕੋਈ ਯਾਦਾਂ ਨਹੀਂ",
        memoriesEmptySub: "ਪਹਿਲੀ ਪਰਿਵਾਰਕ ਫੋਟੋ ਅਪਲੋਡ ਕਰੋ — ਵਿਆਹ, ਤਿਉਹਾਰ, ਰੋਜ਼ਾਨਾ ਖ਼ੁਸ਼ੀਆਂ.",
        caption: "ਕੈਪਸ਼ਨ",
        captionPlaceholder: "ਉਦਾ. ਨਾਨੀ ਦੇ ਘਰ ਦਿਵਾਲੀ, 1998",
        memoryDate: "ਇਹ ਕਦੋਂ ਦੀ ਹੈ?",
        tagMember: "ਪਰਿਵਾਰ ਦੇ ਮੈਂਬਰ ਨੂੰ ਟੈਗ ਕਰੋ",
        choosePhoto: "ਫੋਟੋ ਚੁਣੋ",
        changePhoto: "ਫੋਟੋ ਬਦਲੋ",
        uploadMemory: "ਯਾਦ ਸੰਭਾਲੋ",
        uploading: "ਅਪਲੋਡ ਹੋ ਰਹੀ ਹੈ…",
        deleteMemoryConfirm: "ਇਹ ਯਾਦ ਹਟਾਉਣੀ ਹੈ? ਇਹ ਵਾਪਸ ਨਹੀਂ ਆਵੇਗੀ.",
        confirmDelete: "ਪੁਸ਼ਟੀ ਲਈ ਮੁੜ ਟੈਪ ਕਰੋ",
        uploadedBy: "ਜੋੜਿਆ",
        heroEyebrow: "ਤੁਹਾਡਾ ਪਰਿਵਾਰ · ਤੁਹਾਡੀ ਵਿਰਾਸਤ",
        heroHeadline1: "ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦਾ ਇਤਿਹਾਸ",
        heroHeadlineAccent: "ਸੰਭਾਲਿਆ ਹੋਇਆ",
        heroHeadline2: "ਹਮੇਸ਼ਾਂ ਲਈ।",
        heroSubline: "ਆਪਣਾ ਪਰਿਵਾਰਿਕ ਰੁੱਖ ਬਣਾਓ, ਵੱਡਿਆਂ ਦੀਆਂ ਆਵਾਜ਼ਾਂ ਰਿਕਾਰਡ ਕਰੋ, ਫੋਟੋਆਂ ਅਤੇ ਦਸਤਾਵੇਜ਼ ਸੰਭਾਲੋ — ਇਹ ਸਭ ਇਕ ਨਿੱਜੀ ਜਗ੍ਹਾ ਵਿੱਚ, ਜੋ ਭਾਰਤੀ ਪਰਿਵਾਰਾਂ ਲਈ ਹੈ, ਤੁਹਾਡੀ ਆਪਣੀ ਭਾਸ਼ਾ ਵਿੱਚ।",
        heroCtaPrimary: "ਮੋਬਾਈਲ OTP ਨਾਲ ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ",
        heroCtaSecondary: "ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ ਵੇਖੋ",
        heroTrustPrivate: "ਮੂਲ ਰੂਪ ਵਿੱਚ ਨਿੱਜੀ",
        heroTrustLangs: "10 ਭਾਰਤੀ ਭਾਸ਼ਾਵਾਂ",
        heroTrustFree: "ਸ਼ੁਰੂ ਕਰਨ ਲਈ ਮੁਫ਼ਤ",
        heroSocialProof: "12,500+ ਭਾਰਤੀ ਪਰਿਵਾਰਾਂ ਦਾ ਭਰੋਸਾ",
        heroBadge10: "10+",
        heroBadge10Cap: "ਭਾਰਤੀ ਭਾਸ਼ਾਵਾਂ",
        heroStoryAudio: "ਆਡੀਓ ਕਹਾਣੀਆਂ",
        heroStoryPhotos: "ਤਸਵੀਰਾਂ",
        heroStoryDocs: "ਦਸਤਾਵੇਜ਼",
        statHappyFamilies: "ਖੁਸ਼ ਪਰਿਵਾਰ",
        statTreesCreated: "ਸੰਭਾਲੇ ਪਰਿਵਾਰਕ ਮੈਂਬਰ",
        statStoriesRecorded: "ਕਹਾਣੀਆਂ ਅਤੇ ਯਾਦਾਂ",
        statDataPrivacy: "ਡਾਟਾ ਪਰਦੇਦਾਰੀ",
        demoBadge: "ਬਿਨਾਂ ਸਾਇਨਅਪ ਅਜ਼ਮਾਓ",
        demoHeadline: "ਆਪਣੀ ਪਰਿਵਾਰਕ ਰੁੱਖ ਨੂੰ 30 ਸੈਕਿੰਡ ਵਿੱਚ ਜਿਊਂਦਾ ਦੇਖੋ",
        demoSubline: "6 ਨਾਮ ਜੋੜੋ। ਆਪਣਾ 6 ਪੀੜ੍ਹੀਆਂ ਦਾ ਪਰਿਵਾਰਕ ਰੁੱਖ ਇੱਥੇ ਹੀ ਹਿਲਦਿਆਂ‑ਦੁਲਦਿਆਂ ਦੇਖੋ।",
        demoTrustNoPhone: "ਬਿਨਾਂ ਫ਼ੋਨ",
        demoTrustNoOtp: "ਬਿਨਾਂ OTP",
        demoTrustNoSignup: "ਬਿਨਾਂ ਸਾਇਨਅਪ",
        demoStepPrefix: "ਕਦਮ",
        demoStepOf: "ਵਿੱਚੋਂ 6 —",
        demoLabelFather: "ਪਿਤਾਜੀ ਦਾ ਨਾਮ",
        demoPhFather: "ਜਿਵੇਂ: Rajesh Sharma",
        demoLabelGrandfather: "ਦਾਦਾ / ਨਾਨਾ ਜੀ ਦਾ ਨਾਮ",
        demoPhGrandfather: "ਜਿਵੇਂ: Mohan Sharma",
        demoLabelGreatGrand: "ਪਰਦਾਦਾ / ਪਰਨਾਨਾ ਜੀ ਦਾ ਨਾਮ",
        demoPhGreatGrand: "ਜਿਵੇਂ: Kishan Sharma",
        demoLabelChild: "ਪੁੱਤਰ / ਧੀ ਦਾ ਨਾਮ",
        demoPhChild: "ਜਿਵੇਂ: Ananya Sharma",
        demoLabelGrandchild: "ਪੋਤਾ / ਪੋਤੀ / ਨਾਤੀ / ਨਾਤਨ ਦਾ ਨਾਮ",
        demoPhGrandchild: "ਜਿਵੇਂ: Vihaan Sharma",
        roleFather: "ਪਿਤਾਜੀ",
        roleGrandfather: "ਦਾਦਾ / ਨਾਨਾ ਜੀ",
        roleGreatGrandfather: "ਪਰਦਾਦਾ / ਪਰਦਾਦੀ",
        roleChild: "ਪੁੱਤਰ / ਧੀ",
        roleGrandchild: "ਪੋਤਾ / ਪੋਤੀ",
        demoLabelYou: "ਤੁਹਾਡਾ ਨਾਮ",
        demoLabelParent: "ਤੁਹਾਡੇ ਮਾਤਾ‑ਪਿਤਾ ਦਾ ਨਾਮ",
        demoLabelGrand: "ਤੁਹਾਡੇ ਦਾਦਾ‑ਦਾਦੀ / ਨਾਨਾ‑ਨਾਨੀ ਦਾ ਨਾਮ",
        demoPhYou: "ਜਿਵੇਂ ਰਾਹੁਲ ਸ਼ਰਮਾ",
        demoPhParent: "ਜਿਵੇਂ ਸੁਰੇਸ਼ ਸ਼ਰਮਾ",
        demoPhGrand: "ਜਿਵੇਂ ਰਾਮਚੰਦਰ ਸ਼ਰਮਾ",
        demoHint: "ਆਪਣਾ ਦਰੱਖਤ ਜੀਵੰਤ ਦੇਖਣ ਲਈ ਹੋਰ {n} ਨਾਮ ਜੋੜੋ।",
        demoNext: "ਅੱਗੇ",
        demoSeeTree: "ਆਪਣਾ ਦਰੱਖਤ ਵੇਖੋ",
        demoRestart: "ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ",
        demoStartReal: "ਮੇਰਾ ਅਸਲੀ ਦਰੱਖਤ ਸ਼ੁਰੂ ਕਰੋ (ਮੁਫ਼ਤ)",
        demoTreeBadge: "ਤੁਹਾਡਾ ਛੋਟਾ ਦਰੱਖਤ",
        demoChooseDesign: "ਪੋਸਟਰ ਡਿਜ਼ਾਈਨ ਚੁਣੋ",
        demoClassic: "ਕਲਾਸਿਕ",
        demoFamilyTitle: "{name} ਪਰਿਵਾਰ",
        demoShareWhatsApp: "WhatsApp 'ਤੇ ਸਾਂਝਾ ਕਰੋ",
        demoShareText: "ਮੈਂ 30 ਸਕਿੰਟਾਂ ਵਿੱਚ ਸਾਡਾ ਪਰਿਵਾਰਕ ਰੁੱਖ ਬਣਾਇਆ! 🌳 ਤੁਸੀਂ ਵੀ ਮੁਫ਼ਤ ਬਣਾਓ: {link}",
        demoScanToJoin: "ਸਕੈਨ ਕਰੋ, ਮੁਫ਼ਤ ਬਣਾਓ",
        demoTreeHeadline: "ਛੇ ਪੀੜ੍ਹੀਆਂ, ਇੱਕ ਜੀਉਂਦਾ ਦਰੱਖਤ।",
        demoTreeAddMore: "20 ਤੋਂ ਵੱਧ ਰਿਸ਼ਤੇਦਾਰ, ਫੋਟੋਆਂ ਅਤੇ ਯਾਦਾਂ ਜੋੜੋ — ਮੁਫ਼ਤ।",
        demoFooterPrivacy: "ਤੁਹਾਡੇ ਡੈਮੋ ਨਤੀਜੇ ਸਿਰਫ਼ ਤੁਹਾਡੇ ਬ੍ਰਾਊਜ਼ਰ ਵਿੱਚ ਹੀ ਰਹਿੰਦੇ ਹਨ। ਕੁਝ ਵੀ ਸੰਭਾਲਿਆ ਜਾਂ ਭੇਜਿਆ ਨਹੀਂ ਜਾਂਦਾ।",
        roleGrandparent: "ਦਾਦਾ‑ਦਾਦੀ / ਨਾਨਾ‑ਨਾਨੀ",
        roleParent: "ਮਾਤਾ‑ਪਿਤਾ",
        roleYou: "ਤੁਸੀਂ",
        testHeadline: "ਭਾਰਤ ਭਰ ਦੀਆਂ ਪਰਿਵਾਰਾਂ ਦਾ ਸਾਡੀ ਉੱਤੇ ਭਰੋਸਾ",
        testSubline: "ਅਸਲੀ ਪਰਿਵਾਰ, ਅਸਲੀ ਜੀਵਨ ਕਹਾਣੀਆਂ — ਸਦਾ ਲਈ ਸੰਭਾਲੀਆਂ ਹੋਈਆਂ।",
        testShareBtn: "ਆਪਣੇ ਪਰਿਵਾਰ ਦੀ ਕਹਾਣੀ ਸਾਂਝੀ ਕਰੋ",
        testShareHint: "ਕੀ Family Repository ਨੇ ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦੀ ਮਦਦ ਕੀਤੀ ਹੈ? ਆਪਣੀ ਕਹਾਣੀ ਸਾਂਝੀ ਕਰੋ।",
        noOtpTitle: "Family Repository ਕਦੇ ਵੀ ਤੁਹਾਡੇ OTP ਦੀ ਮੰਗ ਨਹੀਂ ਕਰਦਾ।",
        noOtpBody: "ਜੇ ਕੋਈ ਤੁਹਾਨੂੰ ਫੋਨ ਕਰੇ ਜਾਂ ਸੁਨੇਹਾ ਭੇਜੇ ਅਤੇ OTP ਮੰਗੇ — ਤੁਰੰਤ ਸੰਪਰਕ ਤੋੜ ਦਿਓ। ਅਸੀਂ ਕਦੇ ਵੀ ਇਸਦੀ ਬੇਨਤੀ ਨਹੀਂ ਕਰਾਂਗੇ।",
        navLogin: "ਲੌਗਇਨ",
        navStartFree: "ਮੁਫ਼ਤ ਸ਼ੁਰੂ ਕਰੋ",
        navDashboard: "ਡੈਸ਼ਬੋਰਡ",
        tnHome: "ਘਰ",
        tnFamilyTree: "ਪਰਿਵਾਰਿਕ ਵਰਖ਼",
        tnFamilyHub: "ਪਰਿਵਾਰ ਹੱਬ",
        tnEvents: "ਇਵੈਂਟ",
        tnPackages: "ਪੈਕੇਜ",
        tnVault: "ਤਿੱਖਾਣਾ",
        tnReports: "ਰਿਪੋਰਟਾਂ",
        tnMore: "ਹੋਰ",
        tnRecords: "ਰਿਕਾਰਡ",
        tnSupport: "ਸਹਾਇਤਾ",
        sbDashboard: "ਡੈਸ਼ਬੋਰਡ",
        sbMemberProfile: "ਮੈਂਬਰ ਪ੍ਰੋਫ਼ਾਈਲ",
        sbgConnections: "ਜੁੜਾਵ",
        sbConnectFriends: "ਵੱਡੇ ਪਰਿਵਾਰਾਂ ਨਾਲ ਜੁੜੋ",
        sbConnectRelatives: "ਨੇੜਲੇ ਪਰਿਵਾਰਾਂ ਨਾਲ ਜੁੜੋ",
        sbWorldMap: "ਵਿਸ਼ਵ ਨਕਸ਼ਾ",
        sbgMemories: "ਯਾਦਾਂ",
        sbPhotoMemories: "ਫੋਟੋ ਯਾਦਾਂ",
        sbAudioMemories: "ਆਡੀਓ ਯਾਦਾਂ",
        sbVideoMemories: "ਵੀਡੀਓ ਯਾਦਾਂ",
        sbgEvents: "ਇਵੈਂਟ ਅਤੇ ਰਿਮਾਈਂਡਰ",
        sbFamilyCalendar: "ਪਰਿਵਾਰ ਕੈਲੰਡਰ",
        sbTodaysCelebration: "ਅੱਜ ਦਾ ਜਸ਼ਨ",
        sbFamilyTimeline: "ਵਿਅਕਤੀਗਤ ਜੀਵਨ ਯਾਤਰਾ",
        sbSmartReminder: "ਸਮਾਰਟ ਰਿਮਾਈਂਡਰ",
        sbgHealth: "ਸਿਹਤ ਅਤੇ ਮੈਡੀਕਲ ਰਿਪੋਜ਼ਿਟਰੀ",
        sbMedicalHistory: "ਮੈਡੀਕਲ ਹਿਸਟਰੀ",
        sbChronicDiseases: "ਦੀਰਘਕਾਲੀ / ਵਿਰਾਸਤੀ ਬਿਮਾਰੀਆਂ",
        sbCancerRegistry: "ਕੈਂਸਰ ਰਜਿਸਟਰੀ",
        sbGeneticCongenital: "ਜਨਮਜਾਤ ਅਤੇ ਜੈਨੇਟਿਕ",
        sbAllergies: "ਐਲਰਜੀਆਂ ਅਤੇ ਅਸਹਿਨਸ਼ੀਲਤਾਵਾਂ",
        sbVaccination: "ਟੀਕਾਕਰਣ ਰਿਕਾਰਡ",
        sbLifestyle: "ਜੀਵਨਸ਼ੈਲੀ ਡੈਸ਼ਬੋਰਡ",
        sbPregnancyBirth: "ਗਰਭਾਵਸਥਾ ਅਤੇ ਜਨਮ ਇਤਿਹਾਸ",
        sbOrganDonation: "ਅੰਗ ਦਾਨ ਪਸੰਦ",
        sbBloodGroup: "ਰਕਤ ਸਮੂਹ ਰਜਿਸਟਰੀ",
        sbgVault: "ਵਿਅਕਤੀਗਤ ਵਾਲਟ",
        sbDocumentVault: "ਦਸਤਾਵੇਜ਼ ਵਾਲਟ",
        sbLegalVault: "ਕਾਨੂੰਨੀ ਵਾਲਟ",
        sbgDisplay: "ਡਿਸਪਲੇ ਅਤੇ ਡਾਊਨਲੋਡ",
        sbDownloadExport: "ਡਾਊਨਲੋਡ ਅਤੇ ਐਕਸਪੋਰਟ",
        sbDigitalFamilyTree: "ਡਿਜਿਟਲ ਪਰਿਵਾਰਕ ਵ੍ਰਿਕਸ਼",
        sbPackagesPlans: "ਪੈਕੇਜ ਅਤੇ ਯੋਜਨਾਵਾਂ",
        sbPrivacySettings: "ਪਰਦੇਦਾਰੀ ਅਤੇ ਸੈਟਿੰਗਾਂ",
        hdrDashboard: "ਡੈਸ਼ਬੋਰਡ",
        hdrMenu: "ਮੀਨੂ",
        hdrMyProfile: "ਮੇਰੀ ਪ੍ਰੋਫ਼ਾਈਲ",
        hdrSettings: "ਸੈਟਿੰਗਾਂ",
        hdrSignOut: "ਸਾਈਨ ਆਉਟ",
        signOutTitle: "Family Repository ਤੋਂ ਸਾਈਨ ਆਉਟ ਕਰਨਾ ਹੈ?",
        signOutBody: "ਤੁਸੀਂ ਕਿਸੇ ਵੀ ਵੇਲੇ ਆਪਣੇ ਮੋਬਾਈਲ OTP ਨਾਲ ਮੁੜ ਲਾਗਿਨ ਕਰ ਸਕਦੇ ਹੋ।",
        roleAdministrator: "ਪ੍ਰਬੰਧਕ",
        roleMemberPlain: "ਮੈਂਬਰ",
        searchPlaceholder: "ਕੁਝ ਵੀ ਖੋਜੋ…",
        searchNoResults: "ਇਸ ਲਈ ਕੋਈ ਨਤੀਜੇ ਨਹੀਂ:",
        grpMembers: "ਮੈਂਬਰ",
        grpMemories: "ਯਾਦਾਂ",
        grpDocuments: "ਦਸਤਾਵੇਜ਼",
        grpStories: "ਕਹਾਣੀਆਂ",
        grpEvents: "ਇਵੈਂਟ",
        msgTitle: "ਸੰਦਸ਼ ਤੇ ਯਾਦ ਦਿਹਾਣੀਆਂ",
        msgMarkAll: "ਸਭ ਨੂੰ ਪੜ੍ਹਿਆ ਮੰਨੋ",
        msgEmpty: "ਅਜੇ ਕੋਈ ਸੰਦਸ਼ ਨਹੀਂ — ਯਾਦ ਦਿਹਾਣੀਆਂ ਅਤੇ ਪਰਿਵਾਰਕ ਅਪਡੇਟ ਇੱਥੇ ਦਿਖਾਈ ਦੇਣਗੇ।",
        mbHome: "ਘਰ",
        mbTree: "ਵੰਸ਼ਾਵਲੀ ਰੁੱਖ",
        mbAdd: "ਜੋੜੋ",
        mbHub: "ਹੱਬ",
        mbMe: "ਮੈਂ",
        dbMemberSince: "ਮੈਂਬਰ ਸਿੰਸ",
        dbGrowingTogether: "ਇੱਕਠੇ ਵੱਧ ਰਹੇ ਹਾਂ",
        dbGrowingSub: "ਹਰ ਪਰਿਵਾਰ ਦੀ ਆਪਣੀ ਇਕ ਕਹਾਣੀ ਹੁੰਦੀ ਹੈ।",
        dbProfileCompleteness: "ਪ੍ਰੋਫਾਇਲ ਪੂਰਨਤਾ",
        dbMemoryOfDay: "ਅੱਜ ਦੀ ਯਾਦ",
        dbMemoryQuote: "ਅੱਜ ਦਾ ਪਰਿਵਾਰ — ਕੱਲ੍ਹ ਦੀ ਪ੍ਰੇਰਣਾ।",
        dbViewMemory: "ਯਾਦ ਵੇਖੋ",
        dbGoPremium: "ਪ੍ਰੀਮੀਅਮ ਲਓ",
        dbPremiumDesc: "ਤਕਨੀਕੀ ਫੀਚਰ ਖੋਲ੍ਹੋ ਅਤੇ ਆਪਣੇ ਪਰਿਵਾਰਕ ਡਾਟਾ ਨੂੰ ਸੁਰੱਖਿਅਤ ਰੱਖੋ।",
        dbUpgradeNow: "ਹੁਣੇ ਅੱਪਗ੍ਰੇਡ ਕਰੋ",
        plMembers: "ਮੈਂਬਰ",
        plGenerations: "ਪੀੜ੍ਹੀਆਂ",
        plYearsHistory: "ਸਾਲਾਂ ਦੀ ਇਤਿਹਾਸ",
        plFamilyAccounts: "ਪਰਿਵਾਰਕ ਅਕਾਉਂਟ",
        plTotalRecords: "ਕੁੱਲ ਰਿਕਾਰਡ",
        plPhotosVideos: "ਫੋਟੋਆਂ ਅਤੇ ਵੀਡੀਓ",
        plDocuments: "ਦਸਤਾਵੇਜ਼",
        plUpcomingEvents: "ਆਉਣ ਵਾਲੇ ਇਵੈਂਟ",
        plAnalytics: "ਐਨਾਲੇਟਿਕਸ",
        qaTitle: "ਛੇਤੀ ਕਾਰਵਾਈਆਂ",
        qaSubtitle: "ਵਾਰ-ਵਾਰ ਵਰਤੇ ਜਾਂਦੇ ਫੀਚਰਾਂ ਲਈ ਸ਼ਾਰਟਕਟ",
        qaAddFamilyMember: "ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਜੋੜੋ",
        qaViewFamilyTree: "ਖਾਨਦਾਨੀ ਰੁੱਖ ਵੇਖੋ",
        qaAddMemory: "ਯਾਦ ਜੋੜੋ",
        qaScheduleEvent: "ਕਾਰਜ ਤਹਿ ਕਰੋ",
        qaInviteFamily: "ਪਰਿਵਾਰ ਨੂੰ ਬੁਲਾਓ",
        chFamilyGrowth: "ਪਰਿਵਾਰ ਦੀ ਵਾਧਾ",
        chRecords: "ਰਿਕਾਰਡ",
        chActivity: "ਗਤੀਵਿਧੀ",
        chAllTime: "ਹਮੇਸ਼ਾਂ ਦਾ",
        chWeek: "ਹਫ਼ਤਾ",
        chTotal: "ਕੁੱਲ",
        chActivityEmpty: "ਜਿਵੇਂ–ਜਿਵੇਂ ਤੁਹਾਡਾ ਪਰਿਵਾਰ ਯਾਦਾਂ ਜੋੜੇਗਾ, ਇੱਥੇ ਗਤੀਵਿਧੀ ਦਿਖੇਗੀ",
        recDocuments: "ਦਸਤਾਵੇਜ਼",
        recPhotosVideos: "ਫੋਟੋਆਂ ਅਤੇ ਵੀਡੀਓ",
        recHealthRecords: "ਸਿਹਤ ਰਿਕਾਰਡ",
        recLegalRecords: "ਕਾਨੂੰਨੀ ਰਿਕਾਰਡ",
        recStories: "ਕਹਾਣੀਆਂ",
        dbTodaysCelebrations: "ਅੱਜ ਦੇ ਜਸ਼ਨ",
        dbViewAll: "ਸਭ ਵੇਖੋ",
        dbNoCelebToday: "ਅੱਜ ਕੋਈ ਜਨਮਦਿਨ ਜਾਂ ਵਰ੍ਹੇਗੰਢ ਨਹੀਂ ਹੈ।",
        dbTurnsToday: "ਅੱਜ {n} ਸਾਲ ਦਾ/ਦੀ ਹੋ ਰਿਹਾ/ਰਹੀ ਹੈ!",
        dbRemembranceDay: "ਯਾਦਗਾਰੀ ਦਿਵਸ",
        dbViewCalendar: "ਕੈਲੰਡਰ ਵੇਖੋ",
        dbNoUpcoming60: "ਅਗਲੇ 60 ਦਿਨਾਂ ਵਿੱਚ ਕੋਈ ਆਉਣ ਵਾਲੇ ਸਮਾਗਮ ਨਹੀਂ ਹਨ।",
        evBirthdayOf: "{name} ਦਾ ਜਨਮ ਦਿਨ",
        evRemembranceOf: "{name} — ਯਾਦਗਾਰੀ",
        evToday: "ਅੱਜ",
        evInDays: "{n} ਦਿਨਾਂ ਵਿੱਚ",
        evTurning: "{n} ਦੇ ਹੋ ਰਹੇ ਹਨ",
        dbRecentActivity: "ਤਾਜ਼ਾ ਸਰਗਰਮੀਆਂ",
        dbNoActivity: "ਅਜੇ ਤੱਕ ਕੋਈ ਸਰਗਰਮੀ ਨਹੀਂ — ਸ਼ੁਰੂ ਕਰਨ ਲਈ ਮੈਂਬਰ ਜਾਂ ਯਾਦਾਂ ਸ਼ਾਮਲ ਕਰੋ।",
        actSomeone: "ਕਿਸੇ ਨੇ",
        actPersonAdded: "ਨਵਾਂ ਮੈਂਬਰ ਜੋੜਿਆ",
        actPersonUpdated: "ਮੈਂਬਰ ਦੀ ਪ੍ਰੋਫ਼ਾਈਲ ਅੱਪਡੇਟ ਕੀਤੀ",
        actPersonDeleted: "ਇੱਕ ਮੈਂਬਰ ਨੂੰ ਹਟਾਇਆ",
        actMemoryUploaded: "ਤਸਵੀਰ ਅੱਪਲੋਡ ਕੀਤੀ",
        actMemoryDeleted: "ਤਸਵੀਰ ਮਿਟਾਈ",
        actDocUploaded: "ਦਸਤਾਵੇਜ਼ ਅੱਪਲੋਡ ਕੀਤਾ",
        actDocViewed: "ਦਸਤਾਵੇਜ਼ ਵੇਖਿਆ",
        actStoryRecorded: "ਕਹਾਣੀ ਰਿਕਾਰਡ ਕੀਤੀ",
        actStoryPlayed: "ਕਹਾਣੀ ਚਲਾਈ",
        actCalendarCreate: "ਇਕ ਇਵੈਂਟ ਬਣਾਇਆ",
        actCalendarUpdate: "ਇਕ ਇਵੈਂਟ ਅੱਪਡੇਟ ਕੀਤਾ",
        actCapsuleCreated: "ਇਕ ਯਾਦਾਂ ਦੀ ਕੈਪਸੂਲ ਬਣਾਈ",
        actAchievementAdded: "ਇਕ ਉਪਲਬਧੀ ਜੋੜੀ",
        actDefault: "ਇਕ ਅੱਪਡੇਟ ਕੀਤਾ",
        taJustNow: "ਹੁਣੇ ਹੀ",
        taMinAgo: "{n} ਮਿੰਟ ਪਹਿਲਾਂ",
        taHourAgo: "{n} ਘੰਟੇ ਪਹਿਲਾਂ",
        taDayAgo: "{n} ਦਿਨ ਪਹਿਲਾਂ",
        gsTitle: "ਸ਼ੁਰੂ ਕਰ ਰਹੇ ਹਾਂ",
        gsProgress: "{done}/{total} ਪੂਰੇ ਹੋਏ",
        gsAddMember: "ਇੱਕ ਪਰਿਵਾਰਕ ਮੈਂਬਰ ਜੋੜੋ",
        gsUploadMemory: "ਇੱਕ ਯਾਦ ਅੱਪਲੋਡ ਕਰੋ",
        gsRecordStory: "ਇੱਕ ਕਹਾਣੀ ਰਿਕਾਰਡ ਕਰੋ",
        gsInviteFamily: "ਪਰਿਵਾਰ ਨੂੰ ਸੱਦਾ ਦਿਓ",
        gsSecureDoc: "ਇੱਕ ਦਸਤਾਵੇਜ਼ ਸੁਰੱਖਿਅਤ ਕਰੋ",
        gsCelebrateTitle: "ਸ਼ੁਰੂਆਤੀ ਕਦਮ ਪੂਰੇ ਹੋਏ!",
        gsCelebrateBody: "ਸਾਰੇ 5 ਕਦਮ ਮੁਕੰਮਲ — ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦੀ ਡਿਜ਼ਿਟਲ ਵਿਰਾਸਤ ਅਧਿਕਾਰਕ ਤੌਰ ‘ਤੇ ਸ਼ੁਰੂ ਹੋ ਚੁੱਕੀ ਹੈ। ਆਪਣਾ ਪਰਿਵਾਰਕ ਦਰੱਖਤ ਵਧਾਉਂਦੇ ਰਹੋ!",
        actProfileUpdated: "ਆਪਣਾ ਪ੍ਰੋਫ਼ਾਈਲ ਅੱਪਡੇਟ ਕੀਤਾ",
        daySun: "ਐਤ",
        dayMon: "ਸੋਮ",
        dayTue: "ਮੰਗਲ",
        dayWed: "ਬੁੱਧ",
        dayThu: "ਵੀਰ",
        dayFri: "ਸ਼ੁੱਕਰ",
        daySat: "ਸ਼ਨੀ",
        hbTitle: "ਪਰਿਵਾਰ — ਸਭ ਕੁਝ ਇਕ ਥਾਂ",
        hbCategories: "ਸ਼੍ਰੇਣੀਆਂ",
        hbSearchAll: "ਸਾਰੀਆਂ ਸੁਵਿਧਾਵਾਂ ਵਿੱਚ ਖੋਜੋ…",
        hbNoMatch: "ਕੋਈ ਵੀ ਸੁਵਿਧਾ ਨਹੀਂ ਮਿਲੀ",
        hbLive: "ਲਾਈਵ",
        hbBeta: "ਬੀਟਾ",
        hbSoon: "ਜਲਦੀ",
        hbNotifyMe: "ਮੈਨੂੰ ਸੂਚਿਤ ਕਰੋ",
        hbNotified: "ਤਿਆਰ ਹੋਣ ਤੇ ਸੂਚਨਾ ਭੇਜੀ ਗਈ",
        hbNotifyTitle: "ਅਸੀਂ ਤੁਹਾਨੂੰ ਦੱਸਾਂਗੇ ਜਦੋਂ \\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\"{title}\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\" ਲਾਈਵ ਹੋਵੇਗੀ",
        hbNotifyBody: "ਤੁਹਾਡੀ ਰੁਚੀ ਦਰਜ ਕਰ ਲਈ ਗਈ ਹੈ। ਟੀਮ ਉਹਨਾਂ ਸੁਵਿਧਾਵਾਂ ਨੂੰ ਪਹਿਲ ਦਿੰਦੀ ਹੈ ਜਿਨ੍ਹਾਂ ਲਈ ਸਭ ਤੋਂ ਵੱਧ ਪਰਿਵਾਰ ਬੇਨਤੀ ਕਰਦੇ ਹਨ।",
        hbGreat: "ਠੀਕ ਹੈ",
        rcSearch: "ਖੋਜੋ…",
        rcExportCsv: "CSV ਨਿਕਾਸ ਕਰੋ",
        rcAddNew: "ਨਵਾਂ ਜੋੜੋ",
        rcCreate: "ਬਣਾਓ",
        rcEdit: "ਸੋਧੋ",
        rcSave: "ਸੰਭਾਲੋ",
        rcCancel: "ਰੱਦ ਕਰੋ",
        rcLoading: "ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…",
        rcNoYet: "ਹਾਲੇ ਤੱਕ ਕੋਈ {label} ਨਹੀਂ",
        rcClick: "ਕਲਿੱਕ ਕਰੋ",
        rcFirstEntry: "ਆਪਣੀ ਪਹਿਲੀ ਐਂਟਰੀ ਜੋੜਣ ਲਈ।",
        rcNew: "ਨਵਾਂ",
        rcFormHint: "ਹੇਠਾਂ ਦਿੱਤੇ ਖਾਨੇ ਭਰੋ — ਲਾਲ ਤਾਰੇ ਨਾਲ ਨਿਸ਼ਾਨਜ਼ਦਾ ਖੇਤਰ ਲਾਜ਼ਮੀ ਹਨ।",
        rcHealthBold: "ਸਿਰਫ਼ ਪਰਿਵਾਰਕ ਰਿਕਾਰਡ ਲਈ",
        rcHealthRest: "— ਨਿਦਾਨ, ਇਲਾਜ ਜਾਂ ਤਬੀ ਸਲਾਹ ਲਈ ਨਹੀਂ। ਪਰਿਵਾਰ ਦੀ ਰਜ਼ਾਮੰਦੀ ਨਾਲ ਸਿਹਤ ਸਬੰਧੀ ਰਿਕਾਰਡ ਸਿਰਫ਼ ਪਰਿਵਾਰ ਮੁਖੀ ਦੁਆਰਾ ਜੋੜੇ ਜਾਂ ਸੋਧੇ ਜਾ ਸਕਦੇ ਹਨ।",
        rcConfirmTitle: "ਕਿਰਪਾ ਕਰਕੇ ਪੁਸ਼ਟੀ ਕਰੋ",
        rcConfirmDelete: "ਇਹ ਰਿਕਾਰਡ ਮਿਟਾਉਣਾ ਹੈ?",
        rcUpdated: "ਅੱਪਡੇਟ ਹੋ ਗਿਆ।",
        rcSaved: "ਸੰਭਾਲ ਲਿਆ ਗਿਆ।",
        rcCreated: "ਬਣਾਇਆ ਗਿਆ।",
        rcDeleted: "ਮਿਟਾ ਦਿੱਤਾ ਗਿਆ।",
        rcChoose: "ਚੁਣੋ…",
        rcUnknownSection: "ਅਣਜਾਣ ਭਾਗ",
        rcSectionMissing: "ਇਹ ਪਰਿਵਾਰਕ ਰਿਕਾਰਡਸ ਵਾਲਾ ਭਾਗ ਮੌਜੂਦ ਨਹੀਂ ਹੈ।",
        rcBackToHub: "ਪਰਿਵਾਰ ਹੱਬ ਵੱਲ ਵਾਪਸ ਜਾਓ",
        rcWatchDemo: "ਡੈਮੋ ਵੇਖੋ",
        rcDemoSoon: "ਡੈਮੋ ਜਲਦ ਆ ਰਿਹਾ ਹੈ",
        rcFamilyService: "ਪਰਿਵਾਰਕ ਸੇਵਾ",
        rcUntitled: "(ਸ਼ੀਰਸ਼ਕ ਰਹਿਤ)",
        setTitle: "ਸੈਟਿੰਗਾਂ ਅਤੇ ਪਰਦੇਦਾਰੀ",
        setBack: "ਵਾਪਸ",
        setTabInvites: "ਨਿਯੌਤੇ",
        setTabSecurity: "ਸੁਰੱਖਿਆ",
        setTabData: "ਮੇਰਾ ਡਾਟਾ",
        setTabActivity: "ਗਤੀਵਿਧੀ",
        setTabHelp: "ਮਦਦ",
        setMyAccount: "ਮੇਰਾ ਖਾਤਾ",
        setMyAccountSub: "ਰਸੀਦਾਂ, ਸੁਆਗਤ-ਈਮੇਲ ਅਤੇ ਸਹਾਇਤਾ ਦੇ ਜਵਾਬਾਂ ਲਈ ਆਪਣੀ ਈਮੇਲ ਜੋੜੋ। ਤੁਹਾਡਾ ਫ਼ੋਨ ਨੰਬਰ ਤੁਹਾਡਾ ਲੌਗਇਨ ਹੈ ਅਤੇ ਇਸ ਨੂੰ ਇੱਥੇ ਬਦਲਾ ਨਹੀਂ ਜਾ ਸਕਦਾ।",
        setName: "ਨਾਮ",
        setEmail: "ਈਮੇਲ",
        setEmailInvalid: "ਕਿਰਪਾ ਕਰਕੇ ਸਹੀ ਈਮੇਲ ਪਤਾ ਦਾਖਲ ਕਰੋ।",
        setSaveChanges: "ਬਦਲਾਅ ਸੰਭਾਲੋ",
        setSaving: "ਸੰਭਾਲਿਆ ਜਾ ਰਿਹਾ ਹੈ…",
        setSaved: "ਖਾਤੇ ਦੀ ਜਾਣਕਾਰੀ ਸੰਭਾਲ ਲਈ ਗਈ ਹੈ।",
        setFamilyMembers: "ਪਰਿਵਾਰ ਦੇ ਮੈਂਬਰ",
        setRolesHint: "ਐਡਮਿਨ: ਪੂਰਾ ਕੰਟਰੋਲ · ਐਡਿਟਰ: ਮੈਂਬਰ ਜੋੜੋ/ਸੋਧੋ · ਦਰਸ਼ਕ: ਸਿਰਫ਼ ਵੇਖੋ",
        setYou: "(ਤੁਸੀਂ)",
        setRoleUpdated: "ਭੂਮਿਕਾ ਅੱਪਡੇਟ ਕੀਤੀ ਗਈ ਹੈ।",
        setActiveInvites: "ਸਰਗਰਮ ਨਿਯੌਤੇ",
        setInvitesSub: "ਵੰਸ਼ਾਵਲੀ ਵਿੱਚ ਮੈਂਬਰ ਦੇ ਪ੍ਰੋਫ਼ਾਈਲ ਡ੍ਰਾਅਰ ਤੋਂ ਨਿਯੌਤੇ ਭੇਜੋ। ਕਦੇ ਵੀ ਰੱਦ ਕਰ ਸਕਦੇ ਹੋ।",
        setNoActiveInvites: "ਕੋਈ ਸਰਗਰਮ ਨਿਯੌਤੇ ਨਹੀਂ ਹਨ।",
        setRevoke: "ਰੱਦ ਕਰੋ",
        setExpires: "ਮਿਆਦ ਖ਼ਤਮ",
        setOnlyAdmins: "ਸਿਰਫ ਪਰਿਵਾਰ ਦੇ ਐਡਮਿਨ ਨਿਯੌਤੇ ਸੰਭਾਲ ਸਕਦੇ ਹਨ।",
        setInviteRevoked: "ਨਿਯੌਤਾ ਰੱਦ ਹੋ ਗਿਆ ਹੈ।",
        setSeniorSub: "ਪਰਾਈਵੇਸੀ ਚੋਣਾਂ ਨੂੰ 3 ਵੱਡੀਆਂ ਵਿਕਲਪਾਂ ਵਿੱਚ ਸੌਖਾ ਕਰਦਾ ਹੈ: Private / Family / Public.",
        setWelcomeTour: "ਸਵਾਗਤੀ ਦੌਰਾ",
        setWelcomeTourSub: "3 ਕਦਮਾਂ ਵਾਲਾ ਜਾਣ-ਪਹਿਚਾਣ ਦੌਰਾ ਮੁੜ ਦੇਖੋ: Family Tree → Memories → Vault.",
        setReplayTour: "ਦੌਰਾ ਮੁੜ ਚਲਾਓ",
        setLivingIn: "ਇੱਥੇ ਰਹਿੰਦੇ ਹੋ",
        setLivingInSub: "ਚੋਣਵਾਂ — NRI ਪਰਿਵਾਰਾਂ ਨੂੰ ਬਿਹਤਰ ਸੇਵਾ ਦੇਣ ਵਿੱਚ ਸਾਡੀ ਮਦਦ ਕਰਦਾ ਹੈ।",
        setIndia: "ਭਾਰਤ",
        setAbroad: "ਵਿਦੇਸ਼ (NRI)",
        setSelect: "ਚੁਣੋ",
        setWhatWeStore: "ਅਸੀਂ ਤੁਹਾਡੇ ਬਾਰੇ ਕੀ ਸੰਭਾਲਦੇ ਹਾਂ",
        setDpdpNote: "DPDP Act: ਪੂਰੀ ਪਾਰਦਰਸ਼ਤਾ, ਹਮੇਸ਼ਾ।",
        setProfileSections: "ਪ੍ਰੋਫ਼ਾਈਲ ਭਾਗ",
        setVaultDocs: "Vault ਦਸਤਾਵੇਜ਼",
        setInvitesSent: "ਨਿਯੌਤੇ ਭੇਜੇ ਗਏ",
        setActivityEntries: "ਸਰਗਰਮੀ ਦੀਆਂ ਐਂਟਰੀਆਂ",
        setDownloadData: "ਮੇਰਾ ਸਾਰਾ ਡਾਟਾ ਡਾਊਨਲੋਡ ਕਰੋ (JSON)",
        setDeleteTitle: "ਮੇਰਾ ਖਾਤਾ ਅਤੇ ਡਾਟਾ ਮਿਟਾਓ",
        setDeleteSub: "ਤੁਹਾਡਾ ਖਾਤਾ, ਕਹਾਣੀਆਂ, ਤਿਜੋਰੀ ਦੇ ਦਸਤਾਵੇਜ਼ ਅਤੇ ਸਰਗਰਮੀ ਲਾਗ ਹਮੇਸ਼ਾਂ ਲਈ ਮਿਟ ਜਾਣਗੇ। ਜਿਨ੍ਹਾਂ ਪਰਿਵਾਰਕ ਰੁੱਖ ਐਂਟਰੀਆਂ ਵਿੱਚ ਤੁਸੀਂ ਦੂਜਿਆਂ ਬਾਰੇ ਜਾਣਕਾਰੀ ਭਰੀ ਹੈ, ਉਹ ਪਰਿਵਾਰ ਨਾਲ ਹੀ ਰਹੇਗੀ।",
        setDeleteEverything: "ਸਭ ਕੁਝ ਮਿਟਾਓ",
        setDeleteConfirmTitle: "ਆਪਣਾ ਖਾਤਾ ਹਮੇਸ਼ਾਂ ਲਈ ਮਿਟਾਉਣਾ ਹੈ?",
        setDeleteConfirmBody: "ਇਹ ਵਾਪਸ ਨਹੀਂ ਕੀਤਾ ਜਾ ਸਕਦਾ। ਤੁਹਾਡੀਆਂ ਕਹਾਣੀਆਂ, ਦਸਤਾਵੇਜ਼ ਅਤੇ ਖਾਤਾ DPDP Act ਦੇ ਅਨੁਸਾਰ ਮਿਟਾ ਦਿੱਤੇ ਜਾਣਗੇ।",
        setDeleting: "ਮਿਟਾਇਆ ਜਾ ਰਿਹਾ ਹੈ…",
        setYesDelete: "ਹਾਂ, ਸਭ ਕੁਝ ਮਿਟਾਓ",
        setActivityLog: "ਸਰਗਰਮੀ ਲਾਗ",
        setAuditAdminSub: "ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਵਿੱਚ ਕਿਸ ਨੇ ਕੀ ਵੇਖਿਆ ਅਤੇ ਕੀ ਬਦਲਿਆ।",
        setAuditOwnSub: "ਤੁਹਾਡੀ ਆਪਣੀ ਸਰਗਰਮੀ।",
        setNoActivityYet: "ਅਜੇ ਤੱਕ ਕੋਈ ਸਰਗਰਮੀ ਨਹੀਂ ਹੈ।",
        setSearchFamily: "ਪਰਿਵਾਰ ਖੋਜੋ…",
        ptsEyebrow: "ਸਾਡੇ ਡਿਜ਼ਾਇਨ ਸਟੂਡੀਓ ਤੋਂ",
        ptsTitle: "ਖੂਬਸੂਰਤ ਪਰਿਵਾਰਿਕ ਪੋਸਟਰ ਟੈਂਪਲੇਟ",
        ptsSub: "ਸਾਡੇ ਸਟੂਡੀਓ ਦੇ ਹੱਥੋਂ ਤਿਆਰ ਕੀਤੇ ਪੋਸਟਰ ਡਿਜ਼ਾਇਨ — ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦੀਆਂ ਫ਼ੋਟੋਆਂ ਬਿਲਕੁਲ ਫਿੱਟ ਬੈਠਦੀਆਂ ਹਨ। ਸਾਈਨ ਅੱਪ ਕਰਨ ਤੋਂ ਬਾਅਦ ਆਪਣਾ ਮਨਪਸੰਦ ਚੁਣੋ।",
        ptsCta: "ਆਪਣਾ ਪਰਿਵਾਰਿਕ ਪੋਸਟਰ ਬਣਾਓ",
        ptsPremium: "ਪ੍ਰੀਮੀਅਮ",
        hcTitle: "ਹੈਲਪ ਸੈਂਟਰ",
        hcSub: "ਸਾਨੂੰ ਕੁਝ ਵੀ ਪੁੱਛੋ — ਅਸੀਂ ਆਮ ਤੌਰ 'ਤੇ ਕੁਝ ਘੰਟਿਆਂ ਵਿੱਚ ਜਵਾਬ ਦੇ ਦਿੰਦੇ ਹਾਂ।",
        hcAskTitle: "ਸਵਾਲ ਪੁੱਛੋ",
        hcAskSub: "ਆਪਣੀ ਸਮੱਸਿਆ ਦਾ ਵੇਰਵਾ ਦਿਓ, ਸਾਡੀ ਸਹਾਇਤਾ ਟੀਮ ਤੁਹਾਨੂੰ ਵਾਪਸ ਸੰਪਰਕ ਕਰੇਗੀ।",
        hcSubject: "ਵਿਸ਼ਾ",
        hcSubjectPh: "ਜਿਵੇਂ: ਮੈਂ ਫੋਟੋ ਅੱਪਲੋਡ ਨਹੀਂ ਕਰ ਸਕਦਾ/ਸਕਦੀ",
        hcMessage: "ਤੁਹਾਡਾ ਸਵਾਲ",
        hcMessagePh: "ਸਾਨੂੰ ਕਦਮ ਦਰ ਕਦਮ ਦੱਸੋ ਕਿ ਕੀ ਹੋਇਆ…",
        hcSubmit: "ਸਵਾਲ ਭੇਜੋ",
        hcSubmitting: "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ…",
        hcSuccess: "ਤੁਹਾਡਾ ਸਵਾਲ ਭੇਜ ਦਿੱਤਾ ਗਿਆ ਹੈ — ਅਸੀਂ ਜਲਦੀ ਜਵਾਬ ਦੇਵਾਂਗੇ।",
        hcFailed: "ਤੁਹਾਡਾ ਸਵਾਲ ਭੇਜਿਆ ਨਹੀਂ ਜਾ ਸਕਿਆ। ਕਿਰਪਾ ਕਰਕੇ ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ।",
        hcFillBoth: "ਕਿਰਪਾ ਕਰਕੇ ਵਿਸ਼ਾ ਅਤੇ ਆਪਣਾ ਸਵਾਲ ਦੋਵੇਂ ਭਰੋ।",
        hcMyTickets: "ਮੇਰੇ ਸਵਾਲ",
        hcNoTickets: "ਹਜੇ ਤੱਕ ਕੋਈ ਸਵਾਲ ਨਹੀਂ — ਉੱਪਰ ਆਪਣਾ ਪਹਿਲਾ ਸਵਾਲ ਪੁੱਛੋ।",
        hcStatusOpen: "ਖੁੱਲ੍ਹਾ",
        hcStatusResolved: "ਹੱਲ ਹੋਇਆ",
        hcSupportReply: "ਸਪੋਰਟ",
        hcQuickHelp: "ਤੁਰੰਤ ਮਦਦ",
        hcWhatsapp: "WhatsApp ਸਹਾਇਤਾ — ਤੁਰੰਤ ਚੈਟ",
        hcCall: "ਕਾਲ: +91-99999-99999 (ਸੋਮ–ਸ਼ਨੀ 10–7)",
        hcFaq: "FAQ — ਅਕਸਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਪ੍ਰਸ਼ਨ",
        hcTipTitle: "ਸੁਝਾਅ",
        hcTipBody: "ਜ਼ਿਆਦਾਤਰ ਜਵਾਬ ਪਹਿਲਾਂ ਹੀ FAQ ਵਿੱਚ ਹਨ। ਤੁਹਾਡੇ ਪਰਿਵਾਰ ਦੇ ਡਾਟਾ ਨਾਲ ਜੁੜੇ ਕਿਸੇ ਵੀ ਨਿੱਜੀ ਮਾਮਲੇ ਲਈ, ਇੱਥੇ ਪੁੱਛੋ — ਇਹ ਸਿਰਫ਼ ਸਾਡੀ ਸਪੋਰਟ ਟੀਮ ਹੀ ਵੇਖਦੀ ਹੈ।",
        demoAddPhoto: "ਫੋਟੋ ਜੋੜੋ (ਇੱਛਿਕ)",
        demoChangePhoto: "ਫੋਟੋ ਬਦਲੋ",
        demoDownloadPoster: "ਪੋਸਟਰ ਡਾਊਨਲੋਡ ਕਰੋ",
        demoDownloading: "ਤਿਆਰ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ…",
    },
};
;
